import { Formatters } from '../shared/lib/formatters';
import { parSizesText } from '../shared/lib/parSizes';   // R113: "incl. 6 King · 3 Queen"
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { enqueuePhotos, pendingCountFor, pendingCountWhere } from '../lib/photoUploader';
import { QuickLogIssue } from '../shared/components/QuickLogIssue';
import { openExternal, openApiDoc, fieldViewSwitcherHtml, bindFieldViewSwitcher } from '../shared/lib/nav';
import { openLightbox, stripUrlsFor } from '../shared/components/Lightbox';
import { isVideoUrl } from '../shared/lib/mediaKind';
import { followWarning } from '../../../lib/inspFollow.mjs';   // R114 review: say so when the inspection couldn't follow
import { resolveRosterId } from '../../../lib/rosterIdentity.mjs';   // R120: which roster row this login works as (C8)
import { isBoardUnknown, offerBoardRetry, BOARD_UNKNOWN_MSG } from '../shared/lib/boardRetry';   // review R120 #14 (round 6)

// Housekeeping — the turnover board (API: /api/assetops).
//
// Housekeeping is a real-time dispatch problem against a hard deadline: every
// guest checkout creates a "turn" (a clean) that must finish before the next
// check-in. This page is the manager's air-traffic-control view: turns are
// GENERATED from the reservations calendar (POST /turns/generate, idempotent),
// then ranked deadline-forward on the BOARD (GET /turnover-board) — at-risk
// same-day crunches first, acuity-colored, deadline-forward.
//
// Phase 1 ships the Board tab. A turn and an inspection are deliberately the
// same shape, so later phases (assign cleaners, capture, verify, route issues)
// reuse the Inspections engine; the tabs scaffold leaves room for those.
//
// PRINCIPLES.md: every count states its basis inline; a turn with no next
// booking says "no next booking" rather than faking a deadline.

const API = '/api/assetops';

const TURN_STATUSES: Array<['unstarted' | 'assigned' | 'in_progress' | 'cleaned' | 'verified' | 'ready', string]> = [
  ['unstarted', 'Unstarted'],
  ['assigned', 'Assigned'],
  ['in_progress', 'In progress'],
  ['cleaned', 'Cleaned'],
  ['verified', 'Verified'],
  ['ready', 'Ready'],
];
const TURN_TYPES: Array<['turnover' | 'mid_stay' | 'deep' | 'owner' | 'first_clean', string]> = [
  ['turnover', 'Turnover'],
  ['mid_stay', 'Mid-stay'],
  ['deep', 'Deep clean'],
  ['owner', 'Owner stay'],
  ['first_clean', 'First clean'],
];
// R112: the board's filter set (the KPI cards add `only`, their own lens).
interface BoardFilters { status: string; cleaner: string; prop: string; coverage: 'in_house' | 'vendor' | 'all'; only: '' | 'at_risk' | 'same_day'; }
// A finding's state on the turn card (R120: the server's disposition, else
// its own action). board_unknown: the Issues board could not be read.
type TurnDisp = { state: string; by: string | null; by_id: string | null; at: string | null; reason: string | null; reason_code: string | null; via: string; board_unknown: boolean };

export class HousekeepingPage {
  private container: HTMLElement;
  private tab: 'board' | 'cleaners' | 'scheduler' | 'day' | 'performance' | 'history' = 'board';
  private historyRows: any[] = [];   // GET /turns/history (R18 #1)
  private historyCapped = false;     // R34: server hit its row cap — window truncated
  private historyBoardUnknown = false;   // R120 #14 (round 3): the history answer could not read the Issues board
  private histFrom = '';             // R39: optional date-range filter (YYYY-MM-DD)
  private histTo = '';
  private histMode: 'assigned' | 'completed' = 'assigned';   // R88 #4.2 (Michelle): scheduled-that-day vs actually-done-that-day
  private histReq = 0;               // supersede token — stale range fetches must not repaint

  // data
  private board: any = null;                 // { as_of, days, summary, turns:[] } from /turnover-board
  private cleaners: any[] = [];              // { ..., assigned, completed } from /cleaners

  // performance tab (Phase 4) — all READ-ONLY scorecards/cost intelligence
  private perfDays = 90;                     // lookback window (30/90/180)
  private perfStats: any = null;             // /housekeeping/cleaner-stats
  private perfCosts: any = null;             // /housekeeping/costs
  private perfCoverage: 'in_house' | 'vendor' | 'all' = 'in_house';  // R10 #4 — Jesse watches in-house
  private perfPayouts: any = null;           // /housekeeping/payouts
  // R78 payroll audit: daily hours tracker, pay periods, unit-cost report.
  private perfDaily: any = null;             // /housekeeping/daily-hours
  private perfPeriods: any = null;           // /housekeeping/pay-periods
  private perfUnit: any = null;              // /housekeeping/unit-cost
  private ppOpen: string | null = null;      // expanded pay-period id
  private payrollChain: Promise<any> = Promise.resolve();   // serialized daily-hours/register saves
  private perfLoading = false;
  private payoutTurnsOpen: string | null = null; // cleaner id whose payout turns are expanded
  private payoutTurns: Record<string, any[]> = {}; // cleaner id → that cleaner's completed turns

  // board state
  private days = 14;                         // horizon toggle (7/14/30/60)
  // R45: "Today" audit mode — a CLIENT filter over the fetched horizon, not a
  // days=1 fetch: shrinking `days` would also shrink the turn-generation window
  // behind "Refresh turns", leaving future checkouts without turns.
  private todayMode = true;
  // R46 (Kai): a specific date under Timeframe. Renders as the same grouped
  // audit list as Today, keyed to the picked day. Overrides todayMode/Nd chips.
  private selectedDate: string | null = null;
  private hkDateTimer: number | null = null;   // settle timer for the date input's per-segment 'change' events

  // The single day the board is focused on (picked date, or today in Today
  // mode), or null when showing a multi-day window. Every day-conditioned
  // branch keys off THIS, so Today and picked-date behave identically.
  private boardFocusDay(): string | null {
    if (this.selectedDate) return this.selectedDate;
    if (this.todayMode) return (this.board && this.board.as_of) || this.localToday();
    return null;
  }
  private expandedTurn: string | null = null; // turn id open in detail view, else null
  // R6 #4 — board view toggle + filters (drive BOTH the list and the calendar,
  // and the KPI band recomputes from the filtered set).
  private boardView: 'list' | 'calendar' = 'list';
  private calAssignTurn: string | null = null; // turn id in the calendar quick-assign popup (R48), else null
  // coverage defaults to in-house: Jesse runs vendor-cleaned properties on the
  // vendors' own systems, so the board's job is "are all IN-HOUSE cleans covered".
  private boardFilters: BoardFilters = { status: 'all', cleaner: 'all', prop: '', coverage: 'in_house', only: '' };

  // R6 #6 — smart scheduler state.
  private schedRows: any[] = [];             // /housekeeping/schedules
  private schedRules: { max_same_day_default: number | null } = { max_same_day_default: 2 };   // R107
  private sdDefaultDraft: string | null = null;   // R107 review: the unsaved Team-default box survives redraws
  private factRows: any[] = [];              // /housekeeping/facts
  private schedProposal: any = null;         // /housekeeping/schedule/propose
  private schedHorizon = 7;
  private schedLoading = false;
  private proposing = false;
  private applying = false;
  private factsOpen = false;
  private schedNote = '';
  private syncingFacts = false;
  private generating = false;                // 'Refresh turns' in flight
  private addCleanOpen = false;              // R33 #3: the '+ Add clean' form
  private addingClean = false;               // submit in flight
  private acForm: { listing_id: string; date: string; type: string; note: string; cleaner_id?: string } | null = null;   // survives busy/failure redraws

  // cleaners tab — the edit form (null = closed, '' = new, id = editing)
  private editCleaner: string | null = null;

  // cleaner-day (mobile job) view
  private dayCleaner = '';                   // selected cleaner id, '' = none picked
  private dayDate = new Date().toLocaleDateString('en-CA'); // office can browse any day (R8 #5); field cleaners stay on today
  private dayTurns: any[] = [];              // that cleaner's turns (from /cleaners/:id)
  private dayJobOpen: string | null = null;  // turn id whose job sheet is expanded
  private dayJobs: Record<string, any> = {}; // turn id → job sheet (GET /turns/:id/job)
  // Review R120 #14 (round 4): the NEWEST answer always wins. Every parsed
  // answer's turn(s) is stamped with its receipt order (stampAnswer); per
  // finding the card keeps the disposition the newest answer that carried one
  // gave (boardSeen: turn id → finding id → { at, d }). A raw answer (no
  // dispositions) takes the board's part from there, never from the cached
  // job sheet; an older answer applied after a newer one changes nothing; a
  // finding no answer has described yet reads board_unknown (fail closed).
  private answerClock = 0;
  private answerAt = new WeakMap<object, number>();
  private boardSeen: Record<string, Record<string, { at: number; d: any }>> = {};
  // R37: field cleaners get their last 3 days of turns below today's route —
  // "phone died / guest showed up, had to upload when I got home" must work.
  // The photo endpoint never gated on status; only this view's today-filter
  // made done turns unreachable after local midnight.
  private recentTurns: any[] = [];
  private recentOpen = false;
  // Jesse #3: the cleaner's upcoming assigned turns, read-only, below today's
  // route — plan ahead without drowning today in future work. R120 (team:
  // "the HK portal doesn't show future cleans"; D27): the next 14 days, not 5.
  private upcomingTurns: any[] = [];
  // R120: the login's roster link points at a removed cleaner profile and no
  // active profile carries its email — the field view says so in plain words
  // instead of a bare "couldn't match" (never set when the roster didn't load).
  private staleLink = false;
  // R120: Cleaners tab — a 409 DUP_EMAIL on save ("<name> already has this
  // email on the roster") with an Edit-that-row button.
  private cfDup: { msg: string; id: string; name: string } | null = null;
  private dayContext: Record<string, any> = {}; // turn id → past issues + supply reqs (R18 #8)
  private dayLoading = false;
  // R51: the FULL turn returned by a successful checkout — drives the
  // "🎉 Clean submitted!" confirmation card on that job until dismissed.
  private completedTurn: any = null;
  // R51: listing_id → fetched checklist override ({disable_standard,by,at} or
  // null when unset). has() = "fetched"; office checkbox stays disabled until then.
  private checklistOverrides = new Map<string, any>();
  private analyzing: string | null = null;   // turn id whose /analyze is in flight
  private aiCheckFailed = new Set<string>();  // AI-unblock: turn ids whose last /analyze didn't produce a verification

  // In-house vs vendor cleaning mode per property (listing_id → mode).
  private cleaningModes: Record<string, string> = {};
  private modesLoaded = false;   // /cleaning-modes fetch succeeded at least once
  // Interval that live-updates the running clean timer's elapsed display.
  private elapsedTimer: any = null;

  // Ad-hoc field storefront (supplies) + damage report — not tied to a turn.
  private storeOpen = false;
  private storeLock = false;        // opened from a turn — property fixed to that turn
  private storeTurnId = '';         // R37: the EXACT turn the storefront opened from — turn_id must not re-resolve by listing (a recent + a today turn can share one)
  private storeCustomName = '';     // "something else?" free-text item (state-backed)
  private storeCustomQty = 1;
  private catalog: any[] = [];                 // GET /supplies/standard
  private cart: Record<string, number> = {};   // supply id → qty
  private pickerListings: any[] = [];           // active listings for the property picker
  private storeListing = '';
  private storeNeededBy = '';       // R66 (Marina): "soonest date needed" — prefilled from the next scheduled clean
  private storeNeededByEdited = false;   // only an ACTUAL edit is sent — untouched prefill lets the server auto-match (honest 'auto' chip)
  private stockOpenTurn: string | null = null;   // R70: which turn's stocking checklist is expanded
  private invOpenTurn: string | null = null;     // R79 #3: which turn's inventory count is expanded
  private invSaveChain: Promise<any> = Promise.resolve();   // serialized count saves (whole-pds rewrites land in order)
  // R78: global standard-checklist editor (Board toolbar → ✎ Standard checklist).
  // R81 #1.1: property-coverage view state (Scheduler tab).
  private covSearch = '';
  private covUnassignedFirst = true;
  private stdEditOpen = false;
  private stdEditData: any = null;      // GET /standard-checklist payload; {error:true} = load failed (save stays disabled — wipe protection)
  private stdEditSaving = false;
  private stdDraft: string | null = null;   // unsaved textarea text — INSTANCE state so it survives render()/auto-refresh, not just draw()
  // R78: Past-cleans expandable detail — open turn ids + cached /turns/:id/job payloads.
  private histOpen = new Set<string>();
  private histDetail = new Map<string, any>();   // turn id → job payload | 'loading' | 'error'
  private streakFlags: any[] | null = null;      // R70: GET /staff/streaks — 6+-consecutive-day assignment runs
  private fieldBusy = false;                     // submit in flight
  private storeLoaded = false;
  // R112 (Jesse #7: "check-in just keeps loading … click it again and it
  // opens"): the check-in POST is a whole-blob write — seconds on production
  // — so the button needs a busy state and a double-tap guard.
  private checkingIn: string | null = null;      // turn id whose /checkin is in flight
  private jobLoading = new Set<string>();        // turn ids whose job sheet is being fetched (ensureJob)
  private jobFailed = new Set<string>();         // turn ids whose last sheet fetch failed → "try again" instead of a spinner loop
  private cleanerResolving = false;              // first paint: login→roster match in flight (spinner, not the "couldn't match" state)
  // R112 B4: photos the background uploader could NOT deliver (permanent
  // rejection) — surfaced on the card so the cleaner retakes, not just on the
  // floating chip. Fed by the window 'surge-photo-dropped' event.
  private droppedPhotos: Record<string, { n: number; note: string; at: number }> = {};


  // R38: the most-recently-rendered instance is the ONLY one allowed to react
  // to upload events — hub tabs mint a fresh instance per mount, and without
  // this every past instance would replaceDayTurn + draw on each photo.
  private static activeInstance: HousekeepingPage | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    // R38: the background uploader finishes a photo → merge the fresh turn
    // (thumbnails, satisfied gates) into our lists. Guarded to the ACTIVE
    // instance (isConnected as backstop), and a redraw never lands while the
    // cleaner is typing in one of our inputs.
    window.addEventListener('surge-photo-uploaded', (e: any) => {
      if (HousekeepingPage.activeInstance !== this || !this.container.isConnected) return;
      const turn = this.stampAnswer(e?.detail?.response || null)?.turn;
      if (turn && turn.id) this.mergeTurnIfFresh(turn);
      this.drawIfIdle();
    });
    // R112 B4: a photo the uploader gave up on (404/400/413/415/422) is
    // reported on the job card itself — "1 photo was rejected — retake".
    // detail: { url: '/api/assetops/turns/<id>/photo', label?, status?, note? }
    window.addEventListener('surge-photo-dropped', (e: any) => {
      if (HousekeepingPage.activeInstance !== this || !this.container.isConnected) return;
      const m = String(e?.detail?.url || '').match(/\/turns\/([^/?#]+)\/photo/);
      if (!m) return;
      const id = decodeURIComponent(m[1]);
      const cur = this.droppedPhotos[id] || { n: 0, note: '', at: 0 };
      this.droppedPhotos[id] = { n: cur.n + 1, note: String(e?.detail?.note || e?.detail?.label || 'photo').slice(0, 120), at: Date.now() };
      this.drawIfIdle();
    });
  }

  // R40: with 3 parallel uploads, response ARRIVAL order isn't commit order —
  // a delayed early-commit snapshot must not clobber a fresher one (thumbnails
  // would vanish, the analyze gate would re-lock). capture.photo_keys grows
  // by one per committed upload → monotone; log length breaks ties.
  private mergeTurnIfFresh(turn: any): void {
    if (!this.isStaleTurn(turn)) this.replaceDayTurn(turn);
  }
  // an answer whose turn snapshot predates the row's is older in full (its
  // dispositions included — review R120 #14, round 4)
  private isStaleTurn(turn: any): boolean {
    const cur = this.findDayTurn(turn.id);
    const keys = (t: any) => ((t?.capture?.photo_keys || []).length);
    const logs = (t: any) => ((t?.log || []).length);
    return !!cur && (keys(cur) > keys(turn) || (keys(cur) === keys(turn) && logs(cur) > logs(turn)));
  }

  // A redraw that never lands over a mid-interaction DOM: typing in one of
  // our inputs, or the storefront sheet open (a rebuild would eat the cart).
  private drawIfIdle(): void {
    const ae = document.activeElement;
    const typing = !!ae && this.container.contains(ae) && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA');
    if (!typing && !this.storeOpen) this.draw();
  }

  async render(): Promise<void> {
    HousekeepingPage.activeInstance = this;
    this.container.innerHTML = `
      <div class="page-content">
        <div id="hk-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    // R112: the Calendar's turn popover hands off a turn id ("Open in
    // Housekeeping") through sessionStorage — one-shot, consumed here.
    let focus: string | null = null;
    try { focus = sessionStorage.getItem('hk_focus_turn'); if (focus) sessionStorage.removeItem('hk_focus_turn'); } catch { /* private mode */ }
    if (HousekeepingPage.fieldRole() === 'cleaner') {
      // R112 first-paint fix: the cleaner never waited behind POST
      // /turns/generate (a whole-blob WRITE the server already runs on its
      // own timer) + the office board — only the roster (the login→cleaner
      // match, the crew bar) loads before the first paint, and that paint is
      // a spinner until the match settles, never the "couldn't match your
      // login" state. nextCleanFor falls back to the day/upcoming lists.
      await this.reloadCleaners();
      // R120 review fix (#11): the first match ran blind (roster or link not
      // loaded) → match again now that the roster is here.
      let rematched = false;
      if (this.cleanerResolved && !this.cleanerResolving && this.cleanerProvisional && this.cleanersLoadedOnce) {
        this.cleanerResolving = true;
        try { rematched = await this.resolveCleaner(); } catch { /* keep the provisional match */ } finally { this.cleanerResolving = false; }
      }
      if (this.cleanerResolved && this.dayCleaner && !rematched) {
        // Re-entry (navigate / 15-min refresh with no card open): fresh day,
        // and the open card's sheet revalidated so it never regresses to slim.
        await this.reloadDay();
        if (this.dayJobOpen) await this.ensureJob(this.dayJobOpen, { revalidate: true, draw: false });
        void this.loadMine().then(() => this.drawIfIdle());
      }
      this.draw();
      this.startElapsedTicker();
      return;
    }
    // On open: generate turns for the horizon, then load the board (so it's
    // populated). Generation is idempotent — re-opening never duplicates.
    await this.generate();
    await Promise.all([this.reloadBoard(), this.reloadCleaners(), this.reloadModes()]);
    if (focus) await this.focusTurn(focus);
    this.draw();
    if (focus && this.expandedTurn === focus) {
      this.container.querySelector(`[data-hk-toggle="${CSS.escape(focus)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      void this.loadChecklistOverride(focus);
    }
    this.startElapsedTicker();
  }

  // R112: land on one turn's expanded card (from the Calendar). The list
  // shows the day that turn is on — Today mode when it's today, the picked-
  // date audit list for a future day, the horizon list (Overdue bucket) for
  // a past one — and a coverage filter never hides it. A turn past the
  // loaded horizon widens the fetch to the 60-day ceiling (a READ, not a
  // regenerate). Not on the board at all (cancelled) → the plain board.
  private async focusTurn(id: string): Promise<void> {
    const find = () => ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
    let t = find();
    if (!t && this.days < 60) { this.days = 60; await this.reloadBoard(); t = find(); }
    if (!t) return;
    this.tab = 'board'; this.boardView = 'list'; this.expandedTurn = id; this.calAssignTurn = null;
    const asOf = (this.board && this.board.as_of) || this.localToday();
    if (t.check_out === asOf) { this.todayMode = true; this.selectedDate = null; }
    else if (t.check_out > asOf) { this.todayMode = false; this.selectedDate = t.check_out; }
    else { this.todayMode = false; this.selectedDate = null; }
    if (this.modesLoaded && this.boardFilters.coverage !== 'all' && this.turnMode(t) !== this.boardFilters.coverage) this.boardFilters.coverage = 'all';
    if (this.boardFilters.cleaner === 'none' && t.assigned_to) this.boardFilters.cleaner = 'all';
    if (this.boardFilters.status !== 'all') this.boardFilters.status = 'all';
    this.boardFilters.only = '';
    this.boardFilters.prop = '';
  }

  // Live-tick the running clean timer's elapsed display without a full redraw
  // (updates only [data-hk-elapsed] spans that carry a data-started stamp).
  private startElapsedTicker(): void {
    if (this.elapsedTimer) return;
    this.elapsedTimer = setInterval(() => {
      this.container.querySelectorAll<HTMLElement>('[data-hk-elapsed][data-started]').forEach(s => {
        const started = s.getAttribute('data-started');
        const base = Number(s.getAttribute('data-base') || '0');
        if (!started) return;
        s.textContent = this.fmtDur(base + Math.max(0, (Date.now() - Date.parse(started)) / 60000));
      });
      // R70: the expected-time strip flips to over-time LIVE (a running clean
      // rarely redraws on its own — the ticker is what keeps this honest).
      this.container.querySelectorAll<HTMLElement>('[data-hk-eststrip][data-started]').forEach(s => {
        const started = s.getAttribute('data-started');
        const est = Number(s.getAttribute('data-est') || '0');
        const base = Number(s.getAttribute('data-base') || '0');
        if (!started || !est) return;
        const elapsed = base + Math.max(0, (Date.now() - Date.parse(started)) / 60000);
        const over = elapsed > est;
        s.style.color = over ? 'var(--danger-text,#b91c1c)' : 'var(--text-muted)';
        const note = s.querySelector('[data-hk-over]') as HTMLElement | null;
        if (note) note.style.display = over ? '' : 'none';
      });
    }, 15000);
  }

  // ── data ─────────────────────────────────────────────────────
  private async generate(): Promise<void> {
    await this.post(`/turns/generate?days=${this.days}`, {});
  }
  private async reloadBoard(): Promise<void> {
    const j = await fetch(`${API}/turnover-board?days=${this.days}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.board = j;
  }
  private cleanersLoadFailed = false;   // R46: surface roster-load failures — a silently
                                        // empty dropdown reads as "cleaners are missing"
  private cleanersLoadedOnce = false;   // R120: resolveRosterId trusts the stored link until the roster has loaded
  private loginsUnavailable = false;    // R120: ?logins=1 couldn't read the links — the Login column says so
  private async reloadCleaners(): Promise<void> {
    // R120: the office roster asks for each row's login (?logins=1 — the
    // Cleaners tab's Login column + duplicate badge); a field login never does.
    const office = HousekeepingPage.fieldRole() !== 'cleaner';
    const j = await fetch(`${API}/cleaners${office ? '?logins=1' : ''}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.cleanersLoadFailed = !j;
    if (!j) return;   // keep the stale roster instead of blanking every dropdown
    this.cleaners = (j && j.cleaners) || [];
    this.cleanersLoadedOnce = true;
    this.loginsUnavailable = !!(j && j.logins_unavailable);
  }
  private async reloadModes(): Promise<void> {
    const j = await fetch(`${API}/cleaning-modes`).then(r => r.ok ? r.json() : null).catch(() => null);
    // Only trust the map when the fetch succeeded — the coverage filter is
    // load-bearing on it (R10 #4); a failed fetch must not silently classify
    // every property as in-house.
    if (j && j.modes) { this.cleaningModes = j.modes; this.modesLoaded = true; }
    else if (!this.modesLoaded) this.cleaningModes = {};
  }
  private async reloadScheduler(): Promise<void> {
    this.schedLoading = true;
    const [sch, facts, modes] = await Promise.all([
      fetch(`${API}/housekeeping/schedules`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/facts`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/cleaning-modes`).then(r => r.ok ? r.json() : null).catch(() => null),   // R81: coverage chips
    ]);
    this.schedRows = (sch && sch.schedules) || [];
    if (sch && sch.rules) this.schedRules = sch.rules;
    this.factRows = (facts && facts.properties) || [];
    if (modes && modes.modes) { this.cleaningModes = modes.modes; this.modesLoaded = true; }
    this.schedLoading = false;
  }
  private modeOf(lid: string | number): 'in_house' | 'vendor' {
    return this.cleaningModes[String(lid)] === 'vendor' ? 'vendor' : 'in_house';
  }
  // Effective mode for one turn: its override beats the property's global setting.
  private turnMode(t: any): 'in_house' | 'vendor' {
    return t && (t.mode_override === 'vendor' || t.mode_override === 'in_house') ? t.mode_override : this.modeOf(t?.listing_id);
  }
  // Cleaner-day view: the picked cleaner's turns whose checkout is today,
  // ordered same-day-first then by deadline (the dispatch order they work).
  private async reloadDay(): Promise<void> {
    if (!this.dayCleaner) { this.dayTurns = []; return; }
    this.dayLoading = true;
    // Field cleaners always see TODAY (local, not UTC — their evening is still
    // "today"); the office can browse any date (R8 #5 — see tomorrow's route
    // right after applying a schedule).
    const today = HousekeepingPage.fieldRole() === 'cleaner' ? this.localToday() : (this.dayDate || this.localToday());
    // R51 perf: only the window this view can show (recent −3d … upcoming +5d,
    // centered on the viewed day) and slim rows — the full-history payload was
    // hundreds of turns with photos/logs the day view never renders. The
    // client-side filters below stay as-is (defense in depth).
    // R120 (D27): a field cleaner's window reaches 14 days ahead (the upcoming list).
    const ahead = HousekeepingPage.fieldRole() === 'cleaner' ? HousekeepingPage.UPCOMING_DAYS : 5;
    const j = await this.get(`/cleaners/${this.dayCleaner}?from=${this.addDaysLocal(today, -3)}&to=${this.addDaysLocal(today, ahead)}&slim=1`);
    this.dayLoading = false;
    const all: any[] = (j && j.turns) || [];
    // R112 slim-seam fix (the class behind "some photos don't show up" AND the
    // stuck check-in): slim rows lack capture / verification / log /
    // time_entries / checklist_photos / issues, and the job body renders from
    // the day ROW. Every caller used to have to remember to re-merge the full
    // turn after this call — now the reload itself carries those fields over
    // from the rows it is replacing (the freshest merged state we hold), else
    // from the cached job sheet. Status / sla / reports always come from the
    // fresh slim row, so a reload can never regress a checkout.
    const prevById = new Map<string, any>([...this.dayTurns, ...this.recentTurns].map(t => [t.id, t]));
    const SLIM_FIELDS = ['capture', 'verification', 'log', 'time_entries', 'checklist_photos', 'issues'];
    const heal = (row: any) => {
      const prev = prevById.get(row.id);
      const src = prev || (this.dayJobs[row.id] && this.dayJobs[row.id].turn) || null;
      if (!src) return row;
      const patch: any = {};
      for (const k of SLIM_FIELDS) if (row[k] === undefined && src[k] !== undefined) patch[k] = src[k];
      // review R120 #14 (round 4): findings healed from the cached sheet show
      // the newest word on each (the row's own already do)
      if (!prev && Array.isArray(patch.issues)) patch.issues = HousekeepingPage.withBoardState(this.seenFor(row.id), patch.issues, this.stampOf(src));
      return Object.keys(patch).length ? { ...row, ...patch } : row;
    };
    // Ordering is same-day (back-to-back) first, then by next-check-in deadline.
    // Geo/route ordering is deferred: no listing/turn carries coordinates (only PDS
    // address strings), so distance-based routing isn't attempted here.
    this.dayTurns = all
      .filter(t => t.check_out === today)
      .sort((a, b) => (Number(!!b.same_day) - Number(!!a.same_day))
        || ((a.next_check_in || '9999-12-31') < (b.next_check_in || '9999-12-31') ? -1 : 1))
      .map(heal);
    // R37: the cleaner's last 3 days stay reachable (add late after-photos,
    // finish an interrupted clean). Local clock on both bounds — this window
    // is about the CLEANER's yesterday, not the business day.
    if (HousekeepingPage.fieldRole() === 'cleaner') {
      const cut = this.addDaysLocal(today, -3);
      this.recentTurns = all
        .filter(t => t.check_out < today && t.check_out >= cut)
        .sort((a, b) => (a.check_out < b.check_out ? 1 : -1))
        .map(heal);
      // Jesse #3: the days ahead too — read-only preview so a cleaner can
      // plan, without showing the entire future schedule (R120: 14 days, D27).
      const until = this.addDaysLocal(today, HousekeepingPage.UPCOMING_DAYS);
      this.upcomingTurns = all
        .filter(t => t.check_out > today && t.check_out <= until)
        .sort((a, b) => (a.check_out < b.check_out ? -1 : a.check_out > b.check_out ? 1
          : (Number(!!b.same_day) - Number(!!a.same_day))));
    }
    // R37: an open job that vanished from BOTH lists (office reassigned or
    // moved it, or it aged out of the recent window) must not leave a ghost
    // dayJobOpen — that would latch suspendAutoRefresh for the whole session.
    if (this.dayJobOpen && !this.findDayTurn(this.dayJobOpen)) this.dayJobOpen = null;
  }
  // Performance tab: all three read-only Phase 4 reports for the current window.
  // perfReq is a monotonic request token — a stale in-flight reload (rapid
  // window/coverage clicks) must not overwrite a newer one's results.
  private perfReq = 0;
  private costsUrl(): string {
    return `${API}/housekeeping/costs?days=${this.perfDays}${this.perfCoverage !== 'all' ? `&coverage=${this.perfCoverage}` : ''}`;
  }
  private async reloadPerformance(): Promise<void> {
    const req = ++this.perfReq;
    this.perfLoading = true;
    // R78 (payroll audit): daily hours, pay periods and the unit-cost report
    // join the three Phase 4 reports.
    const [stats, costs, payouts, daily, periods, unit] = await Promise.all([
      fetch(`${API}/housekeeping/cleaner-stats?days=${this.perfDays}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(this.costsUrl()).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/payouts`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/daily-hours?days=31`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/pay-periods`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/unit-cost?days=${this.perfDays}`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (req !== this.perfReq) return;   // superseded — discard
    this.perfStats = stats;
    this.perfCosts = costs;
    this.perfPayouts = payouts;
    this.perfDaily = daily;
    this.perfPeriods = periods;
    this.perfUnit = unit;
    this.perfLoading = false;
  }
  // R78: refetch just the unit-cost report (channel-fee % edit, period close).
  // Rides the perfReq token like reloadCostsOnly — a window change mid-flight
  // must not let this stale response overwrite the newer report.
  private async reloadUnitCostOnly(): Promise<void> {
    const req = this.perfReq;
    const j = await fetch(`${API}/housekeeping/unit-cost?days=${this.perfDays}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (req !== this.perfReq) return;   // superseded — discard
    if (j) { this.perfUnit = j; this.draw(); }
  }
  // Coverage only affects the cost report — refetch just that (scorecards and
  // payouts are coverage-independent; see their captions).
  private async reloadCostsOnly(): Promise<void> {
    const req = ++this.perfReq;
    this.perfLoading = true;
    const costs = await fetch(this.costsUrl()).then(r => r.ok ? r.json() : null).catch(() => null);
    if (req !== this.perfReq) return;   // superseded — discard
    this.perfCosts = costs;
    this.perfLoading = false;
  }
  private async get(path: string): Promise<any> {
    const r = await fetch(`${API}${path}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { alert(j.message || j.error || `request failed (${r.status})`); return null; }
    return this.stampAnswer(j);
  }
  // Review R120 #14 (round 4): an answer's receipt order, stamped on its
  // turn(s) the moment it is parsed — a handler may apply an older answer
  // after a newer one (the checkout handler merges its own answer after the
  // job sheet it fetched next), and the newer must still win.
  private stampAnswer<T>(j: T): T {
    const o: any = j;
    if (!o || typeof o !== 'object') return j;
    const n = ++this.answerClock;
    if (o.turn && typeof o.turn === 'object' && !this.answerAt.has(o.turn)) this.answerAt.set(o.turn, n);
    if (Array.isArray(o.turns)) for (const t of o.turns) if (t && typeof t === 'object' && !this.answerAt.has(t)) this.answerAt.set(t, n);
    return j;
  }
  // a turn object's stamp; one never stamped is arriving now
  private stampOf(t: any): number {
    const s = this.answerAt.get(t);
    if (s != null) return s;
    const n = ++this.answerClock;
    this.answerAt.set(t, n);
    return n;
  }
  // R70 review: every mutation bumps this so an in-flight revalidate GET can
  // tell it was issued BEFORE the write and discard its stale snapshot.
  private dayMutSeq = 0;
  // retried (review R120 #14, round 6): the server could not read the Issues
  // board (503 board_unknown) and saved nothing. With a retried callback the
  // page says so in plain words and offers Try again (the SAME request; its
  // answer goes to retried); without one, the plain words alone.
  private async post(path: string, body: any, retried?: (j: any) => void): Promise<any> {
    this.dayMutSeq++;
    // R50 review: a dead network makes fetch REJECT — without this catch the
    // click handler dies silently (unhandled rejection) and the cleaner gets
    // no feedback at all. Alert + null keeps every caller's failure path sane.
    let r: Response;
    try {
      r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    } catch {
      alert('No connection — that didn\'t save. Try again when you have signal.');
      return null;
    }
    const j = await r.json().catch(() => ({}));
    if (!r.ok && isBoardUnknown(r.status, j)) {
      if (retried) offerBoardRetry(async () => { const j2 = await this.post(path, body, retried); if (j2) retried(j2); });
      else alert(BOARD_UNKNOWN_MSG);
      return null;
    }
    if (!r.ok) alert(j.message || j.error || `request failed (${r.status})`);
    return r.ok ? this.stampAnswer(j) : null;
  }
  // R107: post() for the routes that warn instead of block ("too many cleans
  // on Fri: Maria would have 3 same-day turns (max 2)"). A 409 needs_confirm
  // asks; OK re-sends with override:true. Cancel / failure → null, and the
  // caller redraws so a select snaps back to what's actually saved.
  private async postConfirm(path: string, body: any): Promise<any> {
    this.dayMutSeq++;
    let r: Response;
    try {
      r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    } catch {
      alert('No connection — that didn\'t save. Try again when you have signal.');
      return null;
    }
    const j = await r.json().catch(() => ({}));
    if (r.status === 409 && j && j.needs_confirm) {
      if (!confirm(j.error || 'This goes over a cleaning limit. Continue anyway?')) return null;
      return this.post(path, { ...body, override: true });
    }
    if (!r.ok) { alert(j.message || j.error || `request failed (${r.status})`); return null; }
    return this.stampAnswer(j);
  }
  // R78: PUT twin of post() — same dead-network + error-alert behavior.
  private async put(path: string, body: any): Promise<any> {
    this.dayMutSeq++;
    let r: Response;
    try {
      r = await fetch(`${API}${path}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    } catch {
      alert('No connection — that didn\'t save. Try again when you have signal.');
      return null;
    }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) alert(j.message || j.error || `request failed (${r.status})`);
    return r.ok ? this.stampAnswer(j) : null;
  }
  // R66: the property's next scheduled clean after today — the natural
  // supplies deadline. Best-effort over whatever turn collections this view
  // has loaded; '' when unknown (the server auto-computes it then).
  private nextCleanFor(lid: string): string {
    const today = this.localToday();
    return [...(((this.board && this.board.turns) || []) as any[]), ...this.dayTurns, ...this.upcomingTurns]
      .filter((t: any) => t && String(t.listing_id) === String(lid) && String(t.check_out || '') > today
        && !t.orphaned && !['cleaned', 'verified', 'ready'].includes(String(t.status || '')))
      .map((t: any) => String(t.check_out)).sort()[0] || '';
  }

  // R120: the raw form of post()/del() for routes whose 409 is a conversation,
  // not an error (DUP_EMAIL / LINKED_LOGIN / UPCOMING_TURNS) — no alert here;
  // a dead network answers status 0 with the same plain words post() uses.
  private async send(method: 'POST' | 'DELETE', path: string, body?: any): Promise<{ ok: boolean; status: number; j: any }> {
    this.dayMutSeq++;
    try {
      const r = await fetch(`${API}${path}`, { method, headers: { 'Content-Type': 'application/json' }, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
      const j = this.stampAnswer(await r.json().catch(() => ({})));
      return { ok: r.ok, status: r.status, j };
    } catch {
      return { ok: false, status: 0, j: { error: 'No connection — that didn\'t save. Try again when you have signal.' } };
    }
  }
  private async del(path: string): Promise<boolean> {
    const r = await fetch(`${API}${path}`, { method: 'DELETE' });
    if (!r.ok) { const j = await r.json().catch(() => ({} as any)); alert(j.message || j.error || `request failed (${r.status})`); }
    return r.ok;
  }

  // ── formatting helpers ───────────────────────────────────────
  // "Today" in the USER'S timezone (en-CA = YYYY-MM-DD). toISOString() is UTC,
  // which from ~5pm Pacific labels TOMORROW as today — wrong day for cleaners.
  private localToday(): string {
    try { return new Date().toLocaleDateString('en-CA'); } catch { return new Date().toISOString().split('T')[0]; }
  }
  // Date-string arithmetic pinned to UTC so it can't double-shift across the
  // local-midnight boundary (same rule as the server's addDaysStr).
  private addDaysLocal(ymd: string, days: number): string {
    const d = new Date(ymd + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private fmtDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso + 'T00:00:00Z');
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // R51: 'Wednesday, Aug 13' for the field header — UTC-pinned date parse
  // (same rule as OpsBoard fmtDay) so a PT evening can't drift the label.
  private fmtDayBanner(ymd: string): string {
    const d = new Date(ymd + 'T00:00:00Z');
    return isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // R51: a clock time in PT ('10:04 AM') from a full ISO timestamp.
  private fmtTimePT(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
  }
  // R51: 'Mon Aug 12 · 10:04 AM' (PT) for the Started/Completed stamps.
  private fmtStampPT(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}`;
  }
  // Whole days from today to a YYYY-MM-DD date (negative = past).
  private daysFromToday(isoDate: string | null): number | null {
    if (!isoDate) return null;
    const today = new Date(this.localToday() + 'T00:00:00Z').getTime();
    const target = new Date(isoDate + 'T00:00:00Z').getTime();
    if (isNaN(target)) return null;
    return Math.round((target - today) / 86400000);
  }
  // "today" / "tomorrow" / "in Nd" / "Nd ago" for a YYYY-MM-DD checkout date.
  private relDay(isoDate: string | null): string {
    const n = this.daysFromToday(isoDate);
    if (n == null) return '—';
    if (n === 0) return 'today';
    if (n === 1) return 'tomorrow';
    if (n === -1) return 'yesterday';
    return n > 0 ? `in ${n}d` : `${-n}d ago`;
  }
  private statusPill(status: string): string {
    const map: Record<string, [string, string, string]> = {
      unstarted: ['var(--bg-subtle)', 'var(--text-muted)', 'Unstarted'],
      assigned: ['var(--info-soft, #dbeafe)', 'var(--info, #1d4ed8)', 'Assigned'],
      in_progress: ['var(--warning-soft)', 'var(--warning-text)', 'In progress'],
      cleaned: ['var(--success-soft)', 'var(--success-text, #166534)', 'Cleaned'],
      verified: ['var(--success-soft)', 'var(--success-text, #166534)', 'Verified'],
      ready: ['var(--success-soft)', 'var(--success-text, #166534)', 'Ready'],
    };
    const [bg, fg, label] = map[status] || map.unstarted;
    return `<span class="hk-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }
  private typeLabel(type: string): string {
    const m = TURN_TYPES.find(([k]) => k === type);
    return m ? m[1] : (type || 'Turnover');
  }
  // Resolve an assigned_to id to a cleaner name (falls back to the raw id when
  // the cleaner is archived / not in the loaded roster — never invents one).
  private cleanerName(id: string | null): string {
    if (!id) return '';
    const c = this.cleaners.find(x => x.id === id);
    return c ? c.name : id;
  }
  private fmtMoney(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(Number(n))) return '—';
    return `$${Number(n).toLocaleString('en-US')}`;
  }
  // A whole-number percentage, or an em-dash with the honest "no basis" reason
  // when the metric couldn't be computed (null denominator → never a faked 0%).
  private fmtPct(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(Number(n))) return '<span style="color:var(--text-faint, #9ca3af);">—</span>';
    return `${Math.round(Number(n))}%`;
  }
  // Verification grade → traffic color (matches Inspections gradeHex bands).
  private gradeHex(g: number | null | undefined): string {
    if (g == null || !Number.isFinite(Number(g))) return 'var(--text-faint, #9ca3af)';
    const n = Number(g);
    if (n >= 85) return 'var(--success-text, #16a34a)';
    if (n >= 70) return 'var(--warning-text, #b45309)';
    return 'var(--danger, #dc2626)';
  }

  // ── shell ────────────────────────────────────────────────────
  private cleanerResolved = false;           // R5 #3: one-time login→roster match done
  // Resolve the logged-in cleaner to their ao_cleaners id: prefer the admin's
  // explicit tech-link (GET /api/me), else match the login email to a cleaner.
  // R120 (team: Deadira — invited, then "can't see her assigned cleans"): the
  // link wins only when it points at an ACTIVE roster row. Her link pointed at
  // the invite-made row the office had removed as "the empty duplicate", so
  // the portal loaded a row with no turns and said "Nothing today" while her
  // cleans sat on the active twin. lib/rosterIdentity resolveRosterId: a stale
  // link falls back to the active row with the login's email; a roster that
  // didn't load keeps the link (no false alarm). View-as resolves the same way
  // (main.ts hands this call the viewed person's link and email).
  // R120 review fix (#11, and the #9 class here): a match made while the
  // roster had not loaded (the stored link trusted as is) or while the login's
  // link could not be read (/api/me failed or said links_unavailable) is
  // PROVISIONAL. render() matches again on the next pass (navigation or the
  // 15-min refresh on this cached page) once both are known, and a changed
  // answer replaces the day: a stale link is never trusted for the whole
  // session. → true when the matched cleaner changed (the day was reloaded or
  // cleared here).
  // R120 fix round 2 (#11 remaining): a failed read never DOWNGRADES the
  // identity. The link the last good /api/me returned is kept (linkRead), so
  // a re-match pass on which /api/me fails still matches by that link, never
  // by an email guess that could pick another row with her email.
  private cleanerProvisional = false;
  private linkUnknown = false;
  private linkRead: { link: any } | null = null;   // the last link a good /api/me returned (link null = not linked)
  private async resolveCleaner(): Promise<boolean> {
    try {
      const me = await fetch('/api/me').then(r => r.ok ? r.json() : null).catch(() => null);
      if (me && !me.links_unavailable) this.linkRead = { link: me.tech_link && me.tech_link.roster_id ? me.tech_link : null };
    } catch (_) { /* keep the last good link */ }
    const linkKnown = !!this.linkRead;
    const link: any = this.linkRead ? this.linkRead.link : null;
    const rowsLoaded = this.cleanersLoadedOnce;
    const r = resolveRosterId({ link, kind: 'cleaner', rows: this.cleaners, email: HousekeepingPage.profileEmail(), rowsLoaded });
    this.cleanerProvisional = !rowsLoaded || !linkKnown;
    this.linkUnknown = !linkKnown;
    this.staleLink = r.stale_link && !r.id;
    const rosterId = r.id;
    if (rosterId) {
      if (this.dayCleaner === rosterId) return false;
      this.dayCleaner = rosterId; await this.reloadDay(); await this.loadMine();
      return true;
    }
    if (this.dayCleaner && !this.cleanerProvisional) {
      // the provisional match (a link trusted while the roster was down) no longer holds
      this.dayCleaner = ''; this.dayTurns = []; this.recentTurns = []; this.upcomingTurns = [];
      this.dayJobOpen = null; this.myStats = null; this.myReviews = null; this.myOrders = [];
      return true;
    }
    return false;
  }

  // ── R14: the cleaner's own performance + requests ───────────────────────
  private myStats: any = null;      // own row from /housekeeping/cleaner-stats
  private myReviews: any = null;    // /housekeeping/my-reviews payload
  private myOrders: any[] = [];     // supply orders I requested
  private myPerfOpen = false;
  private myOrdersOpen = false;
  private async loadMine(): Promise<void> {
    if (!this.dayCleaner) return;
    const [stats, revs, orders] = await Promise.all([
      fetch(`${API}/housekeeping/cleaner-stats?days=90`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/housekeeping/my-reviews?cleaner_id=${encodeURIComponent(this.dayCleaner)}&days=120`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/supplies/orders`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.myStats = ((stats && stats.cleaners) || []).find((c: any) => String(c.cleaner_id ?? c.id ?? '') === this.dayCleaner)
      || ((stats && stats.cleaners) || []).find((c: any) => String(c.name || '') === (this.cleaners.find(x => x.id === this.dayCleaner)?.name || '')) || null;
    this.myReviews = revs;
    // R30 #5: on a shared company login the profile name is "Company — Person";
    // match the whole crew's orders (company prefix) plus pre-R30 exact-name
    // orders so nothing filed under the bare company name disappears.
    const myName = HousekeepingPage.profileName().toLowerCase();
    const companyName = (() => { try { return String(JSON.parse(sessionStorage.getItem('userProfile') || '{}').company_name || '').toLowerCase(); } catch { return ''; } })();
    this.myOrders = (((orders && orders.orders) || []) as any[])
      .filter(o => {
        const rb = String(o.requested_by || '').toLowerCase();
        if (!rb) return false;
        if (myName && rb === myName) return true;
        return !!companyName && (rb === companyName || rb.startsWith(companyName + ' — '));
      })
      .sort((a, b) => (a.requested_at < b.requested_at ? 1 : -1))
      .slice(0, 15);
  }
  private static fieldRole(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  // R120 (D27): how far ahead the cleaner's read-only upcoming list looks.
  private static readonly UPCOMING_DAYS = 14;
  private static profileEmail(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim(); } catch { return ''; } }
  private static profileName(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.name || '').trim(); } catch { return ''; } }
  // Actor string for WRITES: under view-as, records must not read as if the
  // viewed person made them — the real admin is appended for the audit trail.
  private static actorName(): string {
    const name = HousekeepingPage.profileName();
    try {
      if (sessionStorage.getItem('viewAsUserId')) {
        const real = sessionStorage.getItem('realUserRole') || 'admin';
        return `${name || 'cleaner'} (via ${real} view-as)`;
      }
    } catch (_) { /* */ }
    return name;
  }

  // R112 (Jesse #6/#7): HostBuddy restyle — every page-local family now
  // converges on the global tokens (--accent for the one action colour,
  // --radius / --shadow-card for cards, .chip / .kpi-card / .btn for the
  // shared idiom). Status pills, the risk / same-day rails and severity
  // chips stay SEMANTIC (never swept to blue). Class names referenced by
  // bind() are unchanged.
  private static readonly CSS = `
    .emg-bar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; background:var(--danger-soft); border:1px solid var(--danger-border); border-radius:var(--radius-sm); padding:10px 12px; margin:0 0 14px; }
    .emg-t { font-size:12.5px; font-weight:700; color:var(--danger-text); }
    .emg-btn { display:inline-flex; align-items:center; justify-content:center; min-height:40px; padding:6px 16px; border-radius:var(--radius-btn); background:var(--danger); color:#fff; font-size:13px; font-weight:700; text-decoration:none; }
    .emg-num { font-size:12.5px; font-weight:600; color:var(--danger-text); }
    .hk-tabs { display:flex; gap:6px; margin:2px 0 18px; flex-wrap:wrap; }
    .hk-tab { padding:8px 15px; font-size:12.5px; font-weight:500; color:var(--text-muted); cursor:pointer; border:1px solid var(--border); background:var(--card); border-radius:var(--radius-pill); box-shadow:var(--shadow-card); transition:background .14s ease, color .14s ease, border-color .14s ease; }
    .hk-tab:hover { color:var(--text); border-color:var(--accent-border); }
    .hk-tab.on { color:var(--accent); background:var(--accent-soft); border-color:var(--accent-border); font-weight:600; }
    .hk-basis { font-size:11.5px; color:var(--text-faint); line-height:1.5; margin:0 0 16px; }
    .hk-audit-hdr { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:18px 0 10px; padding-bottom:6px; border-bottom:1px solid var(--border); }
    .hk-audit-hdr .n { font-weight:600; color:var(--text-faint); text-transform:none; letter-spacing:0; }
    .hk-toolbar { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
    .hk-toolbar label { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
    .hk-chipgroup { display:flex; gap:6px 14px; align-items:center; flex-wrap:wrap; margin-bottom:10px; }
    .hk-chips { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
    .hk-tlabel { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); margin-right:2px; }
    .hk-sep { width:1px; height:22px; background:var(--border); }
    .hk-fsel { width:auto; min-width:150px; max-width:230px; }
    .hk-date { width:auto; }
    .hk-date.on { border-color:var(--accent); background:var(--accent-soft); color:var(--accent-hover); font-weight:600; }
    .hk-search { position:relative; display:flex; align-items:center; flex:1 1 180px; min-width:160px; max-width:300px; }
    .hk-search svg { position:absolute; left:10px; color:var(--text-faint); pointer-events:none; }
    .hk-search .input { padding-left:30px; }
    .hk-spacer { flex:1; }
    .hk-seg { display:inline-flex; border:1px solid var(--border); border-radius:var(--radius-btn); overflow:hidden; background:var(--card); }
    .hk-seg button { padding:6px 12px; font-size:11.5px; font-weight:600; background:transparent; border:none; border-right:1px solid var(--border); cursor:pointer; color:var(--text-muted); transition:background 0.12s ease, color 0.12s ease; }
    .hk-seg button:last-child { border-right:none; }
    .hk-seg button:hover:not(.on) { background:var(--bg-subtle); color:var(--text); }
    .hk-seg button.on { background:var(--accent-soft); color:var(--accent-hover); }
    .hk-btn { padding:7px 13px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:12px; font-weight:600; background:var(--card); cursor:pointer; color:var(--text); transition:background 0.12s ease, border-color 0.12s ease; }
    .hk-btn:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
    .hk-btn.primary { background:var(--accent); color:#fff; border-color:var(--accent); }
    .hk-btn.primary:hover { background:var(--accent-hover); border-color:var(--accent-hover); }
    .hk-btn.sm { padding:5px 10px; font-size:11px; }
    .hk-btn[disabled] { opacity:0.55; cursor:default; }
    .num { font-variant-numeric:tabular-nums; }
    .mono { font-family:ui-monospace, monospace; }
    .hk-band { display:grid; grid-template-columns:repeat(5, 1fr); gap:12px; margin-bottom:20px; }
    .hk-stat { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); padding:15px 17px; box-shadow:var(--shadow-card); }
    .hk-stat.alert { border-color:var(--danger-border); background:var(--danger-soft); }
    .hk-stat-label { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
    .hk-stat.alert .hk-stat-label { color:var(--danger-text); }
    .hk-stat-val { font-size:24px; font-weight:700; letter-spacing:-0.02em; margin-top:6px; }
    .hk-stat.alert .hk-stat-val { color:var(--danger-text); }
    /* R112 board KPI band: six clickable cards (6 → 3 → 2 columns) */
    .kpi-grid.hk-kpis { grid-template-columns:repeat(6, 1fr); gap:12px; margin-bottom:16px; }
    .hk-kpis .kpi-card { padding:14px 16px; }
    .hk-kpis .kpi-card-value { font-size:26px; margin-top:10px; }
    .hk-kpis .kpi-card-ic { width:34px; height:34px; border-radius:10px; }
    .hk-kpis .kpi-card.hk-kpi-on { border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-soft), var(--shadow-card); }
    @media (max-width:1180px) { .kpi-grid.hk-kpis { grid-template-columns:repeat(3, 1fr); } }
    @media (max-width:620px) { .kpi-grid.hk-kpis { grid-template-columns:repeat(2, 1fr); gap:10px; } }
    /* turn cards */
    .hk-turn { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card); box-shadow:var(--shadow-card); overflow:hidden; border-left:4px solid var(--border-soft); transition:box-shadow 0.12s ease, border-color 0.12s ease; }
    .hk-turn:hover { box-shadow:var(--shadow-hover); }
    .hk-turn.open { border-color:var(--accent-border); }
    .hk-turn.risk { border-left-color:var(--danger); }
    .hk-turn.sameday { border-left-color:var(--warning); }
    .hk-turn-row { display:grid; grid-template-columns:minmax(0,1.6fr) minmax(0,1.5fr) minmax(0,1fr) minmax(0,1.2fr) 18px; gap:14px; align-items:center; padding:14px 16px; cursor:pointer; transition:background 0.12s ease; }
    .hk-turn-row:hover { background:var(--bg-subtle); }
    .hk-turn-prop { font-weight:600; font-size:14px; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .hk-turn-sub { font-size:11.5px; color:var(--text-muted); margin-top:3px; }
    .hk-turn-k { display:block; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-faint); margin-bottom:3px; }
    .hk-turn-dl { font-size:12.5px; color:var(--text); }
    .hk-turn-dl .none { color:var(--text-faint); font-style:italic; }
    .hk-turn-badges { display:flex; flex-wrap:wrap; gap:4px; }
    .hk-turn-caret { text-align:right; color:var(--text-faint); font-size:12px; }
    .hk-badge { display:inline-block; font-size:10px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill); white-space:nowrap; }
    .hk-badge.risk { background:var(--danger-soft); color:var(--danger-text); }
    .hk-badge.sameday { background:var(--warning-soft); color:var(--warning-text); }
    .hk-badge.gap { background:var(--bg-subtle); color:var(--text-muted); }
    .hk-badge.info { background:var(--info-soft); color:var(--info-text); }
    .hk-pill { display:inline-block; font-size:10px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill); white-space:nowrap; }
    .hk-who-row { display:flex; flex-wrap:wrap; gap:4px; margin-top:5px; }
    .hk-who { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:500; color:var(--text); background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-pill); padding:2px 8px 2px 3px; white-space:nowrap; max-width:100%; }
    .hk-ini { width:18px; height:18px; border-radius:999px; background:var(--accent-soft); color:var(--accent-hover); font-size:9px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; letter-spacing:0.02em; flex:none; }
    .hk-who.none { color:var(--text-faint); font-style:italic; padding-left:8px; }
    .hk-who.none.risk { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); font-style:normal; font-weight:600; }
    .hk-turn-body { border-top:1px solid var(--border); padding:16px; background:var(--bg-subtle); }
    .hk-kv { display:grid; grid-template-columns:repeat(auto-fit, minmax(240px, 1fr)); gap:3px 18px; font-size:12.5px; line-height:1.7; }
    .hk-kv .k { color:var(--text-muted); display:inline-block; min-width:118px; }
    .hk-turn-controls { display:flex; gap:12px; flex-wrap:wrap; align-items:flex-end; margin-top:14px; padding-top:14px; border-top:1px solid var(--border); }
    .hk-turn-controls label { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; }
    .hk-turn-controls select, .hk-turn-controls input[type=date], .hk-turn-controls input[type=text] { padding:7px 10px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:12.5px; font-family:inherit; background:var(--card); color:var(--text); }
    .hk-turn-controls select:focus, .hk-turn-controls input:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
    .hk-unassigned { background:var(--danger-soft); color:var(--danger-text); }
    .hk-turn-report { margin-top:14px; padding-top:14px; border-top:1px solid var(--border); display:flex; gap:8px; align-items:center; flex-wrap:wrap; font-size:12.5px; color:var(--text-muted); }
    /* R112 photo strips — one component for the board, past cleans and the job sheet */
    .hk-strip { display:flex; gap:6px; overflow-x:auto; padding:2px 0 6px; -webkit-overflow-scrolling:touch; scrollbar-width:thin; }
    .hk-strip.wrap { flex-wrap:wrap; overflow:visible; padding-bottom:2px; }
    .hk-th { position:relative; flex:none; width:76px; height:76px; margin:0; border-radius:var(--radius-sm); overflow:hidden; border:1px solid var(--border); background:var(--bg-subtle); cursor:zoom-in; }
    .hk-th img { width:100%; height:100%; object-fit:cover; display:block; }
    .hk-th.sm { width:54px; height:54px; }
    .hk-th.lg { width:96px; height:96px; }
    .hk-th.mine { border:2px solid var(--success); }
    .hk-th.broken img { display:none; }
    .hk-th.broken::after { content:'📷 unavailable'; position:absolute; inset:0; display:flex; align-items:center; justify-content:center; text-align:center; font-size:9.5px; font-weight:600; color:var(--text-faint); padding:4px; line-height:1.2; }
    .hk-th.heic::before { content:'HEIC'; position:absolute; top:3px; left:3px; z-index:1; font-size:8.5px; font-weight:700; padding:1px 4px; border-radius:4px; background:var(--warning-soft); color:var(--warning-text); }
    .hk-th.vid { display:flex; align-items:center; justify-content:center; background:var(--primary); color:#fff; font-size:22px; }
    .hk-th.vid small { position:absolute; bottom:4px; left:0; right:0; text-align:center; font-size:9px; font-weight:600; opacity:0.85; }
    .hk-th.more { display:flex; align-items:center; justify-content:center; font-size:12.5px; font-weight:700; color:var(--accent-hover); background:var(--accent-soft); border-color:var(--accent-border); cursor:pointer; }
    .hk-strip-cap { font-size:11px; color:var(--text-faint); margin:0 0 4px; }
    .hk-turn-strip { padding:0 16px 12px; }
    .hk-cap-thumbs { margin:8px 0; }
    .hk-cap-empty { font-size:13px; color:var(--text-faint); line-height:1.5; }
    /* Cleaner roster table */
    .hk-table { width:100%; border-collapse:collapse; font-size:12.5px; }
    .hk-table th { text-align:left; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); padding:9px 12px; border-bottom:1px solid var(--border); }
    .hk-table td { padding:11px 12px; border-bottom:1px solid var(--border-soft); vertical-align:top; }
    .hk-table tr:last-child td { border-bottom:none; }
    .hk-table tr:hover td { background:var(--bg-subtle); }
    .hk-chip { display:inline-block; font-size:10px; font-weight:600; padding:2px 8px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); margin:1px 3px 1px 0; }
    /* Phase 4 — performance / cost-vs-fee / payouts */
    .hk-perf-cap { font-size:11.5px; font-style:italic; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning-soft); border-radius:var(--radius-sm); padding:9px 13px; margin:0 0 14px; }
    .hk-perf-sec { margin:0 0 26px; }
    .hk-perf-sec > h3 { margin:0 0 6px; font-size:14px; font-weight:600; letter-spacing:-0.01em; color:var(--text); }
    .hk-num-pos { color:var(--success-text); font-weight:600; }
    .hk-num-neg { color:var(--danger); font-weight:600; }
    .hk-sub-th { font-size:10px; font-weight:500; color:var(--text-faint); text-transform:none; letter-spacing:0; margin-top:2px; }
    .hk-payout-turns { background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:10px 12px; margin:8px 0 0; }
    .hk-dot { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:6px; vertical-align:middle; }
    .hk-dot.on { background:var(--success); }
    .hk-dot.off { background:var(--text-faint); }
    .hk-form { border:1px solid var(--border); border-radius:var(--radius); padding:18px; background:var(--card); box-shadow:var(--shadow-card); margin-bottom:18px; }
    .hk-form .grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:14px 16px; }
    .hk-form label { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; }
    .hk-form input, .hk-form select { padding:8px 10px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:13px; font-family:inherit; background:var(--card); color:var(--text); }
    .hk-form input:focus, .hk-form select:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
    .hk-form .row { display:flex; align-items:center; gap:8px; font-size:12.5px; text-transform:none; color:var(--text); font-weight:500; letter-spacing:0; }
    .hk-form-actions { display:flex; gap:10px; margin-top:16px; }
    /* Cleaner-day (mobile job) view */
    .hk-day-pick { display:flex; flex-direction:column; gap:6px; margin-bottom:18px; }
    .hk-day-pick label { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
    .hk-day-pick select, .hk-day-pick input { padding:10px 13px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:14px; font-family:inherit; background:var(--card); color:var(--text); max-width:420px; }
    .hk-job { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:12px; background:var(--card); box-shadow:var(--shadow-card); overflow:hidden; border-left:4px solid var(--border-soft); }
    .hk-job.sameday { border-left-color:var(--warning); }
    .hk-job.risk { border-left-color:var(--danger); }
    .hk-job.open { border-color:var(--accent-border); }
    .hk-job-head { padding:15px 16px; cursor:pointer; display:flex; gap:10px; align-items:flex-start; transition:background 0.12s ease; }
    .hk-job-head:hover { background:var(--bg-subtle); }
    .hk-job-head-main { flex:1; min-width:0; }
    .hk-job-prop { font-weight:700; font-size:16.5px; letter-spacing:-0.01em; color:var(--text); line-height:1.25; }
    .hk-job-meta { font-size:12.5px; color:var(--text-muted); margin-top:6px; display:flex; flex-wrap:wrap; gap:5px 8px; align-items:center; }
    .hk-job-stamp { font-size:11.5px; color:var(--text-muted); margin-top:5px; }
    .hk-job-caret { color:var(--text-faint); font-size:14px; padding-top:2px; flex:none; }
    .hk-job-body { border-top:1px solid var(--border); padding:14px 16px 16px; background:var(--bg-subtle); max-width:100%; overflow-x:hidden; }
    .hk-job-body img { max-width:100%; }
    /* R112: every section of the open ticket is its own card */
    .hk-job-sec { margin-bottom:12px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:12px 14px; }
    .hk-job-sec h4 { margin:0 0 8px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
    .hk-job-line { font-size:14px; line-height:1.55; }
    .hk-job-line .lab { color:var(--text-muted); display:inline-block; min-width:96px; }
    .hk-code { font-family:ui-monospace, SFMono-Regular, Menlo, monospace; font-size:17px; font-weight:700; letter-spacing:0.03em; }
    .hk-update { color:var(--warning-text); font-weight:600; font-size:12.5px; }
    /* R112 codes card: big values, tap-to-copy, address → Maps */
    .hk-codes-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:8px; }
    .hk-code-card { position:relative; padding:10px 12px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--bg-subtle); cursor:pointer; min-height:62px; transition:border-color 0.12s ease, background 0.12s ease; -webkit-tap-highlight-color:transparent; }
    .hk-code-card:hover, .hk-code-card:active { border-color:var(--accent-border); background:var(--accent-soft); }
    .hk-code-card.static { cursor:default; }
    .hk-code-card.static:hover { border-color:var(--border); background:var(--bg-subtle); }
    .hk-code-card.addr { grid-column:1 / -1; }
    .hk-code-lab { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-faint); }
    .hk-code-val { margin-top:3px; font-size:15px; font-weight:600; color:var(--text); word-break:break-word; line-height:1.3; }
    .hk-code-act { margin-top:4px; font-size:11px; font-weight:600; color:var(--accent); }
    .hk-code-card.copied { border-color:var(--success-border); background:var(--success-soft); }
    .hk-code-card.copied .hk-code-act { color:var(--success-text); }
    .hk-toast { position:fixed; left:50%; bottom:calc(72px + env(safe-area-inset-bottom, 0px)); transform:translateX(-50%); z-index:1300; background:var(--primary); color:#fff; font-size:13px; font-weight:600; padding:9px 16px; border-radius:var(--radius-pill); box-shadow:var(--shadow-pop); pointer-events:none; }
    /* Two-reports first step (R18 #2) — amber on purpose: it is the walk-in habit, not an action */
    .hk-2r { background:var(--warning-soft); border:1px solid var(--warning); border-radius:var(--radius-sm); padding:13px 14px; }
    .hk-2r h4 { font-size:14px; font-weight:700; text-transform:none; letter-spacing:0; color:var(--warning-text); margin:0 0 6px; }
    .hk-2r-hint { font-size:12.5px; color:var(--text-muted); margin-bottom:10px; line-height:1.45; }
    .hk-2r-row { margin:8px 0; }
    .hk-2r-btn { width:100%; padding:14px; border-radius:var(--radius-btn); border:1.5px solid var(--warning); background:var(--card); color:var(--warning-text); font-size:15px; font-weight:700; cursor:pointer; min-height:52px; }
    .hk-2r-btn:hover { background:var(--warning-soft); }
    .hk-2r-done { padding:12px 14px; border-radius:var(--radius-btn); background:var(--success-soft); color:var(--success-text); font-size:14px; font-weight:600; display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .hk-2r-done.reported { background:var(--info-soft); color:var(--info-text); }
    .hk-2r-undo { border:none; background:none; color:var(--text-muted); font-size:12px; text-decoration:underline; cursor:pointer; }
    /* Past issues + re-flag vote (R18 #8) */
    .hk-past-iss { display:flex; align-items:center; gap:10px; padding:9px 0; border-bottom:1px solid var(--border-soft); flex-wrap:wrap; }
    .hk-past-iss-main { flex:1; min-width:0; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
    .hk-past-iss-note { font-size:13.5px; }
    .hk-past-iss-date { font-size:11px; color:var(--text-faint); }
    .hk-flag { flex:0 0 auto; padding:8px 12px; border-radius:var(--radius-btn); border:1px solid var(--border); background:var(--card); color:var(--text-muted); font-size:12.5px; font-weight:600; cursor:pointer; min-height:40px; }
    .hk-flag.on { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger); }
    .hk-job-actions { display:flex; gap:10px; margin-top:6px; }
    .hk-primary-action { margin:0 0 12px; }
    .hk-big { flex:1; padding:15px; border-radius:var(--radius-btn); font-size:15px; font-weight:600; border:1px solid var(--border); background:var(--card); color:var(--text); cursor:pointer; min-height:52px; transition:background 0.12s ease, opacity 0.12s ease; }
    .hk-big:hover { background:var(--bg-subtle); }
    .hk-big.go { background:var(--accent); color:#fff; border-color:var(--accent); box-shadow:0 1px 2px rgba(37,99,235,0.25); }
    .hk-big.go:hover { background:var(--accent-hover); border-color:var(--accent-hover); }
    .hk-big.done { background:var(--success); color:#fff; border-color:var(--success); }
    .hk-big.done:hover { opacity:0.92; }
    .hk-big[disabled] { opacity:0.55; cursor:default; }
    .hk-big.busy { cursor:progress; }
    /* R112 B4: a photo the uploader could not deliver */
    .hk-dropped { padding:9px 12px; border-radius:var(--radius-sm); background:var(--danger-soft); border:1px solid var(--danger-border); color:var(--danger-text); font-size:13px; font-weight:600; margin-bottom:12px; display:flex; justify-content:space-between; gap:8px; align-items:center; }
    .hk-dropped button { border:none; background:none; color:var(--danger-text); font-size:16px; cursor:pointer; line-height:1; }
    /* Phase 3 — capture + clean-verification report */
    .hk-cap-vid { width:120px; height:80px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); background:var(--primary); }
    .hk-cap-add { display:inline-flex; align-items:center; gap:6px; padding:11px 15px; border:1px dashed var(--border-strong); border-radius:var(--radius-btn); font-size:14px; font-weight:600; color:var(--text); background:var(--card); cursor:pointer; transition:background 0.12s ease, border-color 0.12s ease; }
    .hk-cap-add:hover { background:var(--bg-subtle); border-color:var(--accent); }
    .hk-cap-add input { display:none; }
    .hk-cap-add.busy { opacity:0.6; cursor:progress; }
    /* R66 (Marina): before any photo exists, the add button IS the next
       action — full-width primary so it can't be missed on a phone. */
    .hk-cap-add.primary { display:flex; justify-content:center; width:100%; box-sizing:border-box; padding:15px; font-size:16px; background:var(--accent); border:none; color:#fff; }
    .hk-cap-add.primary:hover { background:var(--accent-hover); }
    .hk-analyze { width:100%; padding:15px; border-radius:var(--radius-btn); border:none; background:var(--accent); color:#fff; font-size:15px; font-weight:600; cursor:pointer; margin-top:10px; }
    .hk-analyze:hover { background:var(--accent-hover); }
    .hk-analyze:disabled { background:var(--bg-subtle); color:var(--text-muted); cursor:not-allowed; border:1px solid var(--border); }
    .hk-vnote { font-size:12.5px; color:var(--text-muted); margin-top:10px; line-height:1.5; }
    .hk-vnote.warn { color:var(--warning-text); }
    .hk-grade-row { display:flex; align-items:center; gap:18px; flex-wrap:wrap; }
    .hk-ring { flex:none; }
    .hk-grade-num { font-size:32px; font-weight:700; letter-spacing:-0.02em; line-height:1; }
    .hk-rb { display:inline-block; font-size:12px; font-weight:600; padding:5px 13px; border-radius:var(--radius-pill); margin-top:8px; }
    .hk-rb.ready { background:var(--success-soft); color:var(--success-text); }
    .hk-rb.notready { background:var(--danger-soft); color:var(--danger-text); }
    .hk-callout { border:1px solid var(--warning-soft); background:var(--warning-soft); border-radius:var(--radius-sm); padding:13px 15px; margin-top:14px; }
    .hk-callout .h { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--warning-text); margin-bottom:6px; }
    .hk-callout ul { margin:0; padding-left:18px; font-size:13px; line-height:1.55; }
    .hk-issue { border:1px solid var(--border); border-radius:var(--radius-sm); padding:13px 15px; margin-top:10px; background:var(--card); }
    .hk-issue-tags { display:flex; gap:6px; align-items:center; margin-bottom:6px; flex-wrap:wrap; }
    .hk-sev { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; padding:3px 9px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); }
    .hk-sev.high { background:var(--danger-soft); color:var(--danger-text); }
    .hk-sev.med { background:var(--warning-soft); color:var(--warning-text); }
    .hk-sev.low { background:var(--bg-subtle); color:var(--text-muted); }
    .hk-itype { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); border:1px solid var(--border); border-radius:var(--radius-pill); padding:3px 9px; }
    .hk-issue-note { font-size:13px; line-height:1.5; }
    .hk-act-row { display:flex; flex-wrap:wrap; gap:7px; margin-top:10px; }
    .hk-act-btn { padding:8px 13px; min-height:36px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:12px; font-weight:600; background:var(--card); color:var(--text); cursor:pointer; transition:background 0.12s ease, border-color 0.12s ease; }
    .hk-act-btn:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
    .hk-act-btn.primary { background:var(--accent); color:#fff; border-color:var(--accent); }
    .hk-act-btn.primary:hover { background:var(--accent-hover); }
    .hk-act-btn:disabled { opacity:0.55; cursor:progress; }
    .hk-acted { font-size:12.5px; font-weight:600; color:var(--success-text); margin-top:10px; }
    .hk-acted.dismissed { color:var(--text-muted); }
    .hk-dis-acts { align-items:center; }
    .hk-dis-reasons { display:flex; flex-wrap:wrap; gap:7px; }
    .hk-act-btn.hk-dis-reason { border-radius:var(--radius-pill); color:var(--text-muted); }
    .hk-act-btn.hk-dis-reason.on { border-color:var(--accent-border); background:var(--accent-soft); color:var(--accent); }
    .hk-act-btn.hk-dis-undo { border-color:var(--accent-border); color:var(--accent); margin-left:auto; }
    /* In-house cleaning: timer, checklist, field-action forms */
    .hk-timer { display:flex; align-items:center; gap:14px; }
    .hk-timer-val { font-size:30px; font-weight:700; letter-spacing:-0.02em; font-variant-numeric:tabular-nums; color:var(--text); }
    .hk-timer-run { font-size:12px; font-weight:600; color:var(--success-text); }
    .hk-timer-idle { font-size:12px; font-weight:600; color:var(--text-muted); }
    .hk-checklist { display:flex; flex-direction:column; gap:6px; }
    .hk-check { display:flex; align-items:flex-start; gap:10px; padding:12px 13px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:14px; line-height:1.4; cursor:pointer; background:var(--card); transition:background 0.12s ease, border-color 0.12s ease; }
    .hk-check input { margin-top:1px; width:19px; height:19px; flex:none; accent-color:var(--success); }
    .hk-check.on { background:var(--success-soft); border-color:var(--success-border); }
    .hk-check.on span { color:var(--text-muted); text-decoration:line-through; }
    /* Smart scheduler (R6 #6) */
    .hk-sched-row { display:flex; align-items:center; gap:14px; flex-wrap:wrap; padding:12px 14px; border:1px solid var(--border); border-radius:var(--radius); background:var(--card); margin-bottom:8px; }
    .hk-sched-name { min-width:150px; font-size:13.5px; }
    .hk-sched-days { display:inline-flex; gap:3px; }
    .hk-dow { width:30px; height:30px; border-radius:var(--radius-sm); border:1px solid var(--border); background:var(--card); font-size:11px; font-weight:700; color:var(--text-faint); cursor:pointer; }
    .hk-dow.on { background:var(--accent); border-color:var(--accent); color:#fff; }
    .hk-sched-num { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
    .hk-sched-num input { width:76px; padding:6px 8px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:13px; font-family:inherit; }
    .hk-sched-props summary { font-size:12px; font-weight:600; color:var(--accent); cursor:pointer; list-style:none; }
    .hk-prop-list { position:relative; max-height:200px; overflow-y:auto; border:1px solid var(--border); border-radius:var(--radius-sm); padding:8px 10px; margin-top:6px; background:var(--card); min-width:240px; }
    .hk-prop-opt { display:flex; gap:8px; align-items:center; font-size:12.5px; padding:3px 0; cursor:pointer; }
    .hk-sqft { width:90px; padding:5px 8px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12.5px; font-family:inherit; }
    .hk-schedgrid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:12px; }
    .hk-schedday { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); padding:12px 14px; }
    .hk-schedday-empty { opacity:.6; }
    .hk-schedday-h { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; font-size:13.5px; }
    .hk-staff { font-size:10.5px; font-weight:700; padding:3px 9px; border-radius:var(--radius-pill); }
    .hk-staff.ok { background:var(--success-soft); color:var(--success-text); }
    .hk-staff.warn { background:var(--warning-soft); color:var(--warning-text); }
    .hk-staff.bad { background:var(--danger-soft); color:var(--danger-text); }
    .hk-loadrow { display:flex; align-items:center; gap:8px; margin-bottom:5px; }
    .hk-loadname { font-size:11.5px; font-weight:600; min-width:86px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .hk-loadbar { flex:1; height:7px; border-radius:4px; background:var(--bg-subtle); overflow:hidden; }
    .hk-loadfill { height:100%; background:var(--accent-fill); border-radius:4px; }
    .hk-loadfill.over { background:var(--danger); }
    .hk-loadval { font-size:10.5px; color:var(--text-muted); white-space:nowrap; font-variant-numeric:tabular-nums; }
    .hk-schturn { display:flex; align-items:center; gap:8px; padding:6px 0; border-top:1px solid var(--border-soft); font-size:12px; }
    .hk-schname { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-weight:500; }
    .hk-schest { color:var(--text-muted); font-size:11px; white-space:nowrap; }
    .hk-schwho { font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:var(--radius-pill); white-space:nowrap; }
    .hk-schwho.locked { background:var(--bg-subtle); color:var(--text-muted); }
    .hk-schwho.prop { background:var(--accent-soft); color:var(--accent-hover); }
    .hk-schwho.none { background:var(--danger-soft); color:var(--danger-text); }
    /* Board filters + calendar view (R6 #4) */
    .hk-fsel.on { border-color:var(--accent); color:var(--accent-hover); font-weight:600; background:var(--accent-soft); }
    .hk-cal { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); overflow:hidden; box-shadow:var(--shadow-card); }
    .hk-cal-head { display:grid; grid-template-columns:repeat(7, 1fr); border-bottom:1px solid var(--border); }
    .hk-cal-head div { padding:8px 6px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); text-align:center; }
    .hk-cal-week { display:grid; grid-template-columns:repeat(7, 1fr); border-bottom:1px solid var(--border-soft); }
    .hk-cal-week:last-child { border-bottom:none; }
    .hk-cal-cell { min-height:86px; padding:6px; border-right:1px solid var(--border-soft); display:flex; flex-direction:column; gap:3px; min-width:0; }
    .hk-cal-cell:last-child { border-right:none; }
    .hk-cal-cell.off { background:var(--bg-subtle); opacity:0.55; }
    .hk-cal-cell.today { background:var(--accent-soft); }
    .hk-cal-date { font-size:10.5px; font-weight:700; color:var(--text-muted); margin-bottom:2px; }
    .hk-cal-cell.today .hk-cal-date { color:var(--accent); }
    .hk-cal-chip { display:block; width:100%; text-align:left; font-size:10.5px; font-weight:600; padding:3px 6px; border-radius:6px; border:none; cursor:pointer; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#fff; }
    .hk-cal-chip.open { background:var(--text-muted); }
    .hk-cal-chip.assigned { background:var(--accent); }
    .hk-cal-chip.prog { background:var(--warning); }
    .hk-cal-chip.done { background:var(--success); }
    .hk-cal-chip.risk { background:var(--danger); }
    .hk-cal-legend { display:flex; gap:14px; flex-wrap:wrap; font-size:11px; color:var(--text-muted); margin-bottom:10px; align-items:center; }
    .hk-cal-dot { display:inline-block; width:9px; height:9px; border-radius:3px; margin-right:4px; vertical-align:middle; }
    .hk-cal-dot.open { background:var(--text-muted); } .hk-cal-dot.assigned { background:var(--accent); }
    .hk-cal-dot.prog { background:var(--warning); } .hk-cal-dot.done { background:var(--success); }
    .hk-cal-dot.risk { background:var(--danger); }
    /* R31 #7: per-clean quick actions — the one place to report for THIS clean */
    .hk-quick { display:flex; flex-direction:column; gap:8px; margin:2px 0 12px; }
    .hk-quick .hk-big { width:100%; }
    .hk-quick .hk-big.outline { background:var(--card); border:1px dashed var(--accent); color:var(--accent); }
    /* R31 #4: reference-doc links on the ticket (R112: chips) */
    .hk-doclinks { display:flex; gap:6px; flex-wrap:wrap; margin:0 0 12px; }
    .hk-doclink { min-height:38px; font-size:13px; }
    /* R31 #3: reference photos inline under their checklist item */
    .hk-check-thumbs { margin:6px 0 2px 42px; }
    /* R35 #10: checklist sections + per-item photo capture */
    .hk-check-sec { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:10px 0 2px; }
    .hk-item-cam { display:inline-flex; align-items:center; gap:4px; margin:4px 0 6px 42px; padding:7px 12px; border:1px dashed var(--accent); border-radius:var(--radius-sm); font-size:12.5px; font-weight:600; color:var(--accent); background:var(--card); cursor:pointer; min-height:34px; }
    .hk-item-cam.busy { opacity:0.6; cursor:progress; }
    .hk-item-cam input { display:none; }
    /* Ad-hoc field storefront + damage report */
    .hk-fieldbar-wrap { margin:4px 0 16px; padding:12px 14px; border:1px dashed var(--border-strong); border-radius:var(--radius); background:var(--card); }
    .hk-fieldbar-cap { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin-bottom:8px; }
    .hk-fieldbar-hint { font-size:11.5px; color:var(--text-faint); margin-top:8px; line-height:1.45; }
    .hk-fieldbar { display:flex; gap:10px; }
    .hk-fieldbtn { flex:1; padding:14px; border-radius:var(--radius-btn); font-size:14px; font-weight:600; border:1px solid var(--border); background:var(--card); color:var(--text); cursor:pointer; min-height:54px; transition:background .12s ease, border-color .12s ease; }
    .hk-fieldbtn:hover { background:var(--bg-subtle); }
    .hk-fieldbtn.store { border-color:var(--accent-border); color:var(--accent); }
    .hk-fieldbtn.damage { border-color:var(--warning); color:var(--warning-text); }
    .hk-modal { position:fixed; inset:0; z-index:1000; background:rgba(15,23,42,0.55); display:flex; align-items:flex-end; justify-content:center; padding:0; }
    .hk-modal-card { background:var(--card); width:100%; max-width:560px; max-height:92vh; max-height:92dvh; border-radius:var(--radius-lg) var(--radius-lg) 0 0; display:flex; flex-direction:column; box-shadow:var(--shadow-pop); padding-bottom:env(safe-area-inset-bottom, 0px); }
    @media (min-width:640px) { .hk-modal { align-items:center; } .hk-modal-card { border-radius:var(--radius-lg); padding-bottom:0; } }
    .hk-modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 18px; border-bottom:1px solid var(--border); }
    .hk-modal-head h3 { margin:0; font-size:16px; font-weight:600; }
    .hk-modal-x { border:none; background:none; font-size:20px; color:var(--text-muted); cursor:pointer; line-height:1; padding:4px 8px; }
    .hk-modal-body { padding:16px 18px; overflow-y:auto; flex:1; }
    .hk-modal-lab { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:14px 0 6px; }
    .hk-modal-lab:first-child { margin-top:0; }
    .hk-modal-sel, .hk-modal-ta { width:100%; padding:12px 13px; border:1px solid var(--border); border-radius:var(--radius-btn); font-size:15px; font-family:inherit; background:var(--card); box-sizing:border-box; }
    .hk-modal-ta { resize:vertical; }
    .hk-modal-foot { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 18px; border-top:1px solid var(--border); }
    .hk-cart-sum { font-size:13px; font-weight:600; color:var(--text-muted); }
    .hk-store-grid { margin-top:8px; }
    .hk-store-cat { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:16px 0 6px; }
    .hk-store-item { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 0; border-bottom:1px solid var(--border-soft); }
    .hk-store-item-name { font-size:14.5px; font-weight:500; }
    .hk-store-item-sub { font-size:12px; color:var(--text-muted); margin-top:2px; }
    .hk-qty { display:flex; align-items:center; gap:6px; flex:none; }
    .hk-qbtn { width:34px; height:34px; border-radius:var(--radius-sm); border:1px solid var(--border); background:var(--card); font-size:18px; font-weight:600; cursor:pointer; color:var(--text); }
    .hk-qbtn:disabled { opacity:0.4; cursor:default; }
    .hk-qval { min-width:22px; text-align:center; font-variant-numeric:tabular-nums; font-weight:600; font-size:15px; }
    /* R51: field header day banner — big and unmissable */
    .hk-daybanner { font-size:20px; font-weight:700; letter-spacing:-0.01em; color:var(--text); margin:4px 0 2px; }
    /* R51: pulsing "add your after photos" prompt (zero after-photos) */
    .hk-photo-prompt { background:var(--warning-soft); border:2px solid var(--warning); border-radius:var(--radius-sm); padding:13px 15px; margin:8px 0 10px; font-size:14.5px; font-weight:700; color:var(--warning-text); text-align:center; animation:hkPulse 1.6s ease-in-out infinite; }
    @keyframes hkPulse { 0%, 100% { box-shadow:0 0 0 0 rgba(245, 158, 11, 0.45); } 50% { box-shadow:0 0 0 8px rgba(245, 158, 11, 0); } }
    /* R51: checkout confirmation card */
    .hk-confirm { position:relative; background:var(--success-soft); border:2px solid var(--success); border-radius:var(--radius); padding:15px 16px; margin-bottom:14px; }
    .hk-confirm-x { position:absolute; top:8px; right:10px; border:none; background:none; font-size:18px; color:var(--text-muted); cursor:pointer; line-height:1; padding:4px; }
    /* R112 past cleans: clean cards grouped by day */
    .hk-hist { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); padding:14px 16px; margin-bottom:10px; transition:border-color .12s ease; }
    .hk-hist.open { border-color:var(--accent-border); }
    .hk-hist-row { display:flex; gap:12px; align-items:flex-start; }
    .hk-hist-main { flex:1; min-width:0; }
    .hk-hist-prop { font-weight:600; font-size:14px; color:var(--text); }
    .hk-hist-sub { display:flex; flex-wrap:wrap; gap:4px 10px; align-items:center; font-size:12px; color:var(--text-muted); margin-top:6px; }
    .hk-hist-chips { display:flex; flex-wrap:wrap; gap:4px; margin-top:8px; }
    .hk-hist-grade { flex:none; width:46px; height:46px; border-radius:999px; display:flex; align-items:center; justify-content:center; font-size:13.5px; font-weight:700; font-variant-numeric:tabular-nums; border:3px solid var(--border-soft); color:var(--text-muted); background:var(--card); }
    .hk-hist-foot { display:flex; gap:8px; align-items:center; margin-top:8px; flex-wrap:wrap; }
    .hk-hist-detail { margin-top:12px; display:grid; gap:10px; }
    .hk-hist-sec { border:1px solid var(--border-soft); border-radius:var(--radius-sm); background:var(--bg-subtle); padding:10px 12px; }
    .hk-hist-sec-t { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); margin:0 0 6px; }
    .hk-rep { display:inline-block; font-size:11.5px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); white-space:nowrap; }
    .hk-rep.reported { background:var(--warning-soft); color:var(--warning-text); }
    .hk-rep.none { background:var(--success-soft); color:var(--success-text); }
    .hk-rep.danger { background:var(--danger-soft); color:var(--danger-text); }
    @media (max-width: 760px) {
      .hk-band { grid-template-columns:repeat(2, 1fr); }
      .hk-turn-row { grid-template-columns:1fr; gap:8px; padding:12px 14px; }
      .hk-turn-caret { display:none; }
      .hk-turn-strip { padding:0 14px 10px; }
      .hk-chipgroup .hk-sep { display:none; }
      .hk-fsel { min-width:0; flex:1 1 140px; max-width:none; }
      .hk-search { max-width:none; }
      .hk-form .grid { grid-template-columns:1fr; }
      .hk-table thead { display:none; }
      .hk-table, .hk-table tbody, .hk-table tr, .hk-table td { display:block; width:100%; }
      .hk-table tr { border:1px solid var(--border); border-radius:var(--radius-sm); margin-bottom:10px; padding:4px 0; }
      .hk-table td { border:none; padding:6px 14px; }
      /* R31 #6: the cleaner ticket on a phone — tighter gutters, wrapping
         labels instead of horizontal squeeze, thumb-friendly rows */
      .hk-job-head { padding:14px; }
      .hk-job-body { padding:12px; }
      .hk-job-sec { padding:11px 12px; }
      .hk-job-line { display:flex; flex-wrap:wrap; column-gap:8px; }
      .hk-job-line .lab { min-width:96px; flex:none; }
      .hk-2r { padding:12px; }
      .hk-check { padding:12px 11px; }
      .hk-check-thumbs { margin-left:0; }
      .hk-doclink { flex:1 1 45%; justify-content:center; }
      .hk-fieldbar { flex-direction:column; }
      .hk-timer { flex-wrap:wrap; gap:10px; }
      .hk-hist { padding:12px 13px; }
    }
    /* ── R115 mobile (CSS only): the board header was ~1,290px tall before the
       first turn — six tabs wrapped to two rows, three chip groups wrapped,
       then two selects, search and three buttons. Tabs and each chip group
       are now ONE scrolling line; the toolbar is a 2-up grid; a turn's
       controls take the full width (selects stop being 12.5px slivers). */
    @media (max-width: 640px) {
      .hk-tabs { flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; margin-bottom:14px; padding:2px 0 4px; }
      .hk-tabs::-webkit-scrollbar { display:none; }
      .hk-tab { flex:none; white-space:nowrap; }
      .hk-chipgroup { flex-direction:column; flex-wrap:nowrap; align-items:stretch; gap:8px; }   /* nowrap: a wrapping column stretches to its widest line */
      .hk-chips { min-width:0; max-width:100%; flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
      .hk-chips::-webkit-scrollbar { display:none; }
      .hk-chips > * { flex:none; }
      .hk-toolbar { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; }
      .hk-toolbar .hk-fsel { max-width:none; width:100%; }
      .hk-toolbar .hk-search { grid-column:1 / -1; max-width:none; }
      .hk-toolbar .hk-spacer { display:none; }
      .hk-toolbar .btn { justify-content:center; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .hk-toolbar [data-hk-refresh], .hk-toolbar .hk-update { grid-column:1 / -1; }
      .hk-kpis .kpi-card { padding:12px 14px; }   /* the shared compact phone KPI (main.css G4) — this page's own rule outranks it */
      .hk-kpis .kpi-card-value { font-size:22px; margin-top:6px; }
      .hk-kpis .kpi-card-ic { width:28px; height:28px; border-radius:var(--radius-sm); }
      .hk-kpis .kpi-card:last-child:nth-child(odd) { grid-column:1 / -1; }
      .hk-turn-controls { gap:10px; }
      .hk-turn-controls > label { flex:1 1 100%; }
      .hk-turn-controls select, .hk-turn-controls input[type=date], .hk-turn-controls input[type=text] { width:100%; box-sizing:border-box; }
      .hk-turn-controls .hk-seg { display:flex; }
      .hk-turn-controls .hk-seg button { flex:1; }
    }
  `;

  private draw(): void {
    const body = document.getElementById('hk-body');
    if (!body) return;
    // R5 #3 — a CLEANER gets a field view: only their day's job cards, no
    // board/roster/performance tabs and no cleaner picker. Resolve the logged-in
    // cleaner to their roster id by email match (admin can hard-link via add-user).
    const field = HousekeepingPage.fieldRole() === 'cleaner';
    if (field) {
      this.tab = 'day';
      if (!this.cleanerResolved) {
        // R112: the match runs under a spinner (cleanerResolving) — the first
        // paint was the "couldn't match your login" empty state until it settled.
        this.cleanerResolved = true; this.cleanerResolving = true;
        void this.resolveCleaner().catch(() => { /* best effort */ }).then(() => { this.cleanerResolving = false; this.draw(); });
      }
    }
    const tabs: Array<[string, string]> = field ? [] : [['board', 'Board'], ['scheduler', 'Scheduler'], ['cleaners', 'Cleaners'], ['day', 'Cleaner day'], ['history', 'Past cleans'], ['performance', 'Performance']];
    const asOf = this.board?.as_of || '';
    body.innerHTML = `
      <style>${HousekeepingPage.CSS}</style>
      <div class="dash-head">
        <h1>${field ? 'My cleanings' : 'Housekeeping'}</h1>
        ${field ? fieldViewSwitcherHtml() : ''}
        ${field ? `<div class="hk-daybanner">📅 ${this.esc(this.fmtDayBanner(this.localToday()))}</div>` : ''}
        <p>${field ? `${this.esc(HousekeepingPage.profileName() || 'Today')} · your turns — tap a job for the sheet (address, codes, beds), check in at the door, capture before/after, complete the clean` : `Turnover board — every checkout is a clean against the next check-in${asOf ? ` · as of ${this.esc(asOf)}` : ''}`}</p>
      </div>
      ${field ? `
      <div class="emg-bar">
        <span class="emg-t">🚨 Emergency or immediate need?</span>
        <a class="emg-btn" href="tel:+16198155111">📞 Call</a>
        <a class="emg-btn" href="sms:+16198155111">💬 Text</a>
        <span class="emg-num">(619) 815-5111</span>
      </div>` : ''}
      ${tabs.length ? `<div class="hk-tabs">
        ${tabs.map(([k, label]) => `<button class="hk-tab ${this.tab === k ? 'on' : ''}" data-hk-tab="${k}">${label}</button>`).join('')}
      </div>` : ''}
      <div id="hk-tab-body">
        ${field ? this.renderDay(true)
          : this.tab === 'board' ? this.renderBoard()
          : this.tab === 'scheduler' ? this.renderScheduler()
          : this.tab === 'cleaners' ? this.renderCleaners()
          : this.tab === 'performance' ? this.renderPerformance()
          : this.tab === 'history' ? this.renderHistory()
          : this.renderDay()}
      </div>`;
    this.bind();
  }

  // ── tab: board (air-traffic control) ─────────────────────────
  // The board's filtered turn set — status, cleaner (incl. Unassigned), property
  // search. The horizon seg is the timeframe. Both views + the KPI band use this.
  // R112: the pipeline takes its inputs explicitly so the chips / KPI cards
  // can ask "what would I show" (their own dimension overridden) for counts.
  private applyFilters(f: BoardFilters, focusDay: string | null, horizonEnd?: string | null): any[] {
    const done = ['cleaned', 'verified', 'ready'];
    let rows: any[] = (this.board && this.board.turns) || [];
    // R45/R46 day focus: only the focus day's checkouts, keyed to the SERVER's
    // business day (board.as_of, America/LA) so a PT-evening device doesn't
    // drift a day. Focus = a picked date, or today in Today mode.
    if (focusDay) rows = rows.filter(t => t.check_out === focusDay);
    else if (horizonEnd) rows = rows.filter(t => String(t.check_out || '') <= horizonEnd);
    if (f.status === 'done') rows = rows.filter(t => done.includes(t.status));
    else if (f.status !== 'all') rows = rows.filter(t => t.status === f.status);
    if (f.cleaner === 'none') rows = rows.filter(t => !t.assigned_to);
    else if (f.cleaner !== 'all') rows = rows.filter(t => String(t.assigned_to || '') === f.cleaner);
    if (f.prop.trim()) { const q = f.prop.trim().toLowerCase(); rows = rows.filter(t => String(t.listing_name || '').toLowerCase().includes(q)); }
    // R10 #4: coverage — each turn's EFFECTIVE mode (per-turn override beats
    // global). Skipped when the modes map never loaded: filtering on a failed
    // fetch would silently mislabel every property as in-house.
    if (f.coverage !== 'all' && this.modesLoaded) rows = rows.filter(t => this.turnMode(t) === f.coverage);
    // R112: the KPI cards' own lenses — exactly the KPI definitions.
    if (f.only) {
      const asOf = (this.board && this.board.as_of) || this.localToday();
      if (f.only === 'at_risk') rows = rows.filter(t => (focusDay && focusDay !== asOf) ? (t.same_day && !done.includes(t.status)) : !!t.at_risk);
      else if (f.only === 'same_day') rows = rows.filter(t => t.same_day && t.check_out === (focusDay || asOf));
    }
    return rows;
  }
  private filteredTurns(): any[] { return this.applyFilters(this.boardFilters, this.boardFocusDay()); }

  // R33 #3: manually add a clean (deep clean, owner visit, make-ready) — a
  // turn the reservation calendar wouldn't generate.
  // R78 (Jesse): the global standard cleaning checklist, editable in place.
  // One item per line; the server stores it (hk_standard_checklist) and every
  // job sheet serves it. Save stays disabled until a GET succeeded — saving a
  // defaults-seeded textarea over a custom list after a failed load is the
  // wipe class we guard everywhere.
  private renderStdEdit(): string {
    const d = this.stdEditData;
    if (!d) return `<div style="padding:14px 16px; border:1px solid var(--border); border-radius:12px; margin-bottom:14px; background:var(--card,#fff);">${LoadingSpinner.render()}</div>`;
    if (d.error) return `
      <div style="padding:14px 16px; border:1px solid var(--danger,#fca5a5); border-radius:12px; margin-bottom:14px; background:var(--danger-soft,#fef2f2);">
        ⚠ Couldn't load the standard checklist — editing is disabled so a blind save can't overwrite it.
        <button class="hk-btn sm" data-hk-stdretry style="margin-left:8px;">Retry</button>
      </div>`;
    const items: string[] = Array.isArray(d.items) ? d.items : [];
    // Draft guard (review): draw() AND render() (auto-refresh, ↻, month
    // change) rebuild innerHTML — the unsaved text lives in stdDraft instance
    // state (kept fresh by the input listener), so no rebuild can eat it.
    const text = this.stdDraft != null ? this.stdDraft : items.join('\n');
    return `
      <div style="padding:14px 16px; border:1px solid var(--border); border-radius:12px; margin-bottom:14px; background:var(--card,#fff);">
        <div style="font-size:14px; font-weight:700; margin-bottom:2px;">✎ Standard cleaning checklist <span style="font-weight:400; font-size:12px; color:var(--text-muted);">— shows on every cleaning ticket, all properties</span></div>
        <div class="hk-basis" style="margin:0 0 8px;">One item per line. Rewording an item resets its checkbox on in-flight cleans (the text is the tick's identity). Beds, property quirks, reference photos and pool/hot-tub items are added automatically per property. ${d.customized ? `Customized${d.by ? ` by ${this.esc(d.by)}` : ''}${d.updated_at ? ` · ${this.esc(String(d.updated_at).slice(0, 10))}` : ''}.` : 'Currently the built-in defaults.'}</div>
        <textarea data-hk-stdtext rows="${Math.max(6, Math.min(16, items.length + 2))}" style="width:100%; box-sizing:border-box; font-family:inherit; font-size:13px; line-height:1.6; padding:10px 12px; border:1px solid var(--border); border-radius:8px; resize:vertical;">${this.esc(text)}</textarea>
        <div style="display:flex; gap:8px; margin-top:8px; flex-wrap:wrap; align-items:center;">
          <button class="hk-btn primary" data-hk-stdsave ${this.stdEditSaving ? 'disabled' : ''}>${this.stdEditSaving ? 'Saving…' : 'Save list'}</button>
          <button class="hk-btn" data-hk-stdreset title="Fill the box with the built-in defaults — nothing saves until you hit Save">Reset to defaults</button>
          <button class="hk-btn" data-hk-stdclose>Close</button>
        </div>
      </div>`;
  }

  private renderAddClean(): string {
    const f = this.acForm;
    const props = [...this.pickerListings].sort((a, b) => String(a.short_name || a.name || '').localeCompare(String(b.short_name || b.name || '')));
    // R46 (Kai): type-to-search property picker — the same input+datalist
    // pattern as the supply-store picker (datalist resolves BY LABEL, so
    // pickerLabelFor guarantees unique labels).
    const selListing = f && f.listing_id ? props.find(l => String(l.id) === f.listing_id) : null;
    const picker = props.length
      ? `<input data-hk-ac-listing list="hk-ac-props" autocomplete="off" placeholder="Type to search…"
           value="${this.esc(selListing ? this.pickerLabelFor(selListing) : '')}">
         <datalist id="hk-ac-props">${props.map(l => `<option value="${this.esc(this.pickerLabelFor(l))}"></option>`).join('')}</datalist>`
      : `<span class="hk-update" style="padding:8px 0;">⚠ Couldn't load the property list — close and reopen this form to retry.</span>`;
    const cleanerOpts = [`<option value="">— Unassigned —</option>`]
      .concat(this.cleaners.map(c => `<option value="${this.esc(c.id)}" ${f && f.cleaner_id === c.id ? 'selected' : ''}>${this.esc(c.name)}${c.available === false ? ' (off)' : ''}</option>`))
      .join('');
    return `
      <div class="hk-form" style="margin-top:-6px;">
        <div class="grid">
          <label>Property ${picker}</label>
          <label>Date
            <input type="date" data-hk-ac-date value="${this.esc(f ? f.date : this.localToday())}" min="${this.esc(this.localToday())}">
          </label>
          <label>Type
            <select data-hk-ac-type>${TURN_TYPES.map(([v, l]) => `<option value="${v}" ${(f ? f.type === v : v === 'turnover') ? 'selected' : ''}>${l}</option>`).join('')}</select>
          </label>
          <label>Cleaner <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(optional)</span>
            <select data-hk-ac-cleaner>${cleanerOpts}</select>
          </label>
          <label>Note <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(optional)</span>
            <input type="text" data-hk-ac-note value="${this.esc(f ? f.note : '')}" placeholder="e.g. deep clean before owner stay">
          </label>
        </div>
        <div class="hk-form-actions">
          <button class="hk-btn primary" data-hk-ac-submit ${this.addingClean ? 'disabled' : ''}>${this.addingClean ? 'Adding…' : 'Add clean'}</button>
          <button class="hk-btn" data-hk-ac-cancel>Cancel</button>
        </div>
      </div>`;
  }

  // A clean placed past the current board window is real but invisible —
  // say so instead of letting it look like the save vanished.
  private noteIfBeyondHorizon(date: string): void {
    // R45/R46: in a day-focus mode (Today or a picked date) the visible window
    // is ONE day, not this.days — any other date would silently vanish once
    // its card collapses, reading as a lost save (and a retry hits the
    // server's duplicate-clean 409).
    const focusDay = this.boardFocusDay();
    if (focusDay) {
      if (date !== focusDay) {
        alert(`Scheduled for ${date} — you're viewing ${focusDay === ((this.board && this.board.as_of) || this.localToday()) ? 'Today' : focusDay} only, so it won't show here. Switch the timeframe (7d/14d…) or pick that date to see it on the board.`);
      }
      return;
    }
    const d = new Date(this.localToday() + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + this.days);
    if (date > d.toISOString().slice(0, 10)) {
      alert(`Scheduled for ${date} — that's beyond the current ${this.days}-day view. Switch the timeframe to see it on the board.`);
    }
  }

  private renderBoard(): string {
    const f = this.boardFilters;
    const asOfB = (this.board && this.board.as_of) || this.localToday();
    const cleanerOpts = [
      `<option value="all" ${f.cleaner === 'all' ? 'selected' : ''}>All cleaners</option>`,
      `<option value="none" ${f.cleaner === 'none' ? 'selected' : ''}>— Unassigned —</option>`,
    ].concat(this.cleaners.map(c => `<option value="${this.esc(c.id)}" ${f.cleaner === c.id ? 'selected' : ''}>${this.esc(c.name)}</option>`)).join('');
    const statusOpts = [
      ['all', 'All statuses'], ['unstarted', 'Unstarted'], ['assigned', 'Assigned'],
      ['in_progress', 'In progress'], ['done', 'Done (cleaned+verified+ready)'],
    ].map(([v, l]) => `<option value="${v}" ${f.status === v ? 'selected' : ''}>${l}</option>`).join('');
    // Chip counts: the same filter pipeline, the chip's own dimension swapped in.
    const focus = this.boardFocusDay();
    const n = (over: Partial<BoardFilters>, focusDay: string | null, horizonDays?: number) =>
      this.applyFilters({ ...f, ...over }, focusDay, horizonDays ? this.addDaysLocal(asOfB, horizonDays) : null).length;
    const cnt = (k: number | null) => k == null ? '' : `<span class="chip-count">${k}</span>`;
    const isToday = this.todayMode && !this.selectedDate;
    const horizonChips = [7, 14, 30, 60].map(d => `<button type="button" class="chip ${!this.todayMode && !this.selectedDate && this.days === d ? 'active' : ''}" data-hk-days="${d}" title="Checkouts in the next ${d} days">${d}d${this.board && d <= this.days ? cnt(n({}, null, d)) : ''}</button>`).join('');

    const toolbar = `
      <div class="hk-chipgroup">
        <div class="hk-chips" data-hk-horizon>
          <span class="hk-tlabel">Timeframe</span>
          <button type="button" class="chip ${isToday ? 'active' : ''}" data-hk-today title="Today's cleans — the audit list">Today${this.board ? cnt(n({}, asOfB)) : ''}</button>
          ${horizonChips}
          <input class="input hk-date ${this.selectedDate ? 'on' : ''}" type="date" data-hk-date
            min="${asOfB}" max="${this.addDaysLocal(asOfB, 60)}" value="${this.selectedDate || ''}" title="Jump to a specific day">
        </div>
        <span class="hk-sep"></span>
        <div class="hk-chips">
          <span class="hk-tlabel">View</span>
          <button type="button" class="chip ${this.boardView === 'list' ? 'active' : ''}" data-hk-view="list">List</button>
          <button type="button" class="chip ${this.boardView === 'calendar' ? 'active' : ''}" data-hk-view="calendar">Calendar</button>
        </div>
        <span class="hk-sep"></span>
        <div class="hk-chips" data-hk-coverage>
          <span class="hk-tlabel">Cleaned by</span>
          ${([['in_house', 'In-house'], ['vendor', 'Vendor'], ['all', 'All']] as const).map(([v, l]) =>
            `<button type="button" class="chip ${f.coverage === v ? 'active' : ''}" data-hk-cov="${v}">${l}${this.board ? cnt(n({ coverage: v }, focus)) : ''}</button>`).join('')}
        </div>
      </div>
      <div class="hk-toolbar">
        <select class="input hk-fsel" data-hk-fstatus>${statusOpts}</select>
        <select class="input hk-fsel" data-hk-fcleaner>${cleanerOpts}</select>
        <label class="hk-search">${icon('search', 14)}<input class="input" type="search" placeholder="Filter property…" value="${this.esc(f.prop)}" data-hk-fprop autocomplete="off"></label>
        <span class="hk-spacer"></span>
        <button type="button" class="btn btn-secondary" data-hk-addclean>＋ Add clean</button>
        <button type="button" class="btn ${this.stdEditOpen ? 'btn-primary' : 'btn-secondary'}" data-hk-stdedit title="Edit the standard cleaning checklist every ticket shows (all properties)">✎ Standard checklist</button>
        <button type="button" class="btn btn-primary" data-hk-refresh ${this.generating ? 'disabled' : ''}>${this.generating ? 'Refreshing…' : '↻ Refresh turns'}</button>
        ${this.cleanersLoadFailed ? `<span class="hk-update">⚠ cleaner roster didn't load — dropdowns may look incomplete; refresh the page to retry</span>` : ''}
      </div>
      ${this.addCleanOpen ? this.renderAddClean() : ''}
      ${this.stdEditOpen ? this.renderStdEdit() : ''}`;

    if (!this.board) return toolbar + emptyState('Board unavailable', `GET ${API}/turnover-board failed — check the server log.`, '⚠️');

    // KPI band recomputes from the FILTERED set so it always matches the view.
    const turns = this.filteredTurns();
    const todayStr = this.localToday();
    const doneSet = ['cleaned', 'verified', 'ready'];
    // "Today" for KPI/badge math = the SERVER business day (as_of), matching
    // the row data's keying — localToday() drifts on non-Pacific devices after
    // their local midnight and would zero this card while badges still show.
    // R46: for a picked FUTURE day both pressure cards would be structurally 0
    // (same_day_today keys on as_of; server at_risk requires check_out ≤ today)
    // while the rows visibly wear same-day badges — so in day-focus mode count
    // same-day against the focus day, and at-risk as same-day-and-not-done.
    const boardAsOf = this.board.as_of || todayStr;
    const kpiFocus = this.boardFocusDay();
    const s = {
      total: turns.length,
      same_day_today: turns.filter(t => t.same_day && t.check_out === (kpiFocus || boardAsOf)).length,
      in_progress: turns.filter(t => t.status === 'in_progress').length,
      done: turns.filter(t => doneSet.includes(t.status)).length,
      at_risk: kpiFocus && kpiFocus !== boardAsOf
        ? turns.filter(t => t.same_day && !doneSet.includes(t.status)).length
        : turns.filter(t => t.at_risk).length,
      unassigned: turns.filter(t => !t.assigned_to).length,
    };
    const filtered = f.status !== 'all' || f.cleaner !== 'all' || !!f.prop.trim() || !!f.only;
    // R112: every KPI card is a lens — tap to filter the list to exactly what it counts, tap again to clear.
    const kpi = (key: string, label: string, ico: string, val: number, cap: string, tone: string, on: boolean, title: string) => `
      <div class="kpi-card clickable ${tone} ${on ? 'hk-kpi-on' : ''}" data-hk-kpi="${key}" role="button" tabindex="0" title="${this.esc(title)}" aria-pressed="${on ? 'true' : 'false'}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${val}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const band = `
      <div class="kpi-grid hk-kpis">
        ${kpi('all', 'Turns', 'sparkles', s.total, filtered ? 'matching the filters · tap to clear' : `${f.coverage === 'in_house' ? 'in-house cleans' : f.coverage === 'vendor' ? 'vendor cleans' : 'cleans'} ${this.selectedDate && this.selectedDate !== boardAsOf ? `on ${this.selectedDate}` : this.boardFocusDay() ? 'today' : 'in this timeframe'}`, '', false, filtered ? 'Clear the status / cleaner / property filters' : 'Every clean in view')}
        ${kpi('unassigned', 'Unassigned', 'users', s.unassigned, 'need a cleaner', s.unassigned > 0 ? 'tone-bad' : '', f.cleaner === 'none', 'Show only unassigned cleans')}
        ${kpi('same_day', 'Same-day', 'calendar', s.same_day_today, kpiFocus && kpiFocus !== boardAsOf ? 'back-to-back that day' : 'back-to-back today', s.same_day_today > 0 ? 'tone-warn' : '', f.only === 'same_day', 'Show only same-day (back-to-back) cleans')}
        ${kpi('in_progress', 'In progress', 'wrench', s.in_progress, 'turns being worked', '', f.status === 'in_progress', 'Show only cleans in progress')}
        ${kpi('done', 'Done', 'target', s.done, 'cleaned + verified + ready', s.done > 0 ? 'tone-good' : '', f.status === 'done', 'Show only finished cleans')}
        ${kpi('at_risk', 'At risk', 'flag', s.at_risk, 'same-day &amp; not yet done', s.at_risk > 0 ? 'tone-bad' : '', f.only === 'at_risk', 'Show only at-risk cleans')}
      </div>`;

    const covNote = f.coverage === 'all' ? ''
      : !this.modesLoaded ? ' · ⚠ cleaning-mode data unavailable — showing ALL turns until it loads (refresh to retry)'
      : ` · ${f.coverage === 'in_house' ? 'in-house' : 'vendor'}-cleaned only (effective per-turn mode — "Cleaned by: All" shows everything)`;
    const focusDay = this.boardFocusDay();
    const focusLabel = focusDay === boardAsOf ? `Today's cleans (${focusDay})` : `Cleans on ${focusDay}`;
    const basis = (focusDay
      ? `<p class="hk-basis">${focusLabel}${filtered ? ' matching the filters above' : ''}${covNote} · grouped by status so the team can audit at a glance · "cleaned" = cleaned + verified + ready. KPIs follow the filters.</p>`
      : `<p class="hk-basis">Checkouts in the next ${this.board.days}d${filtered ? ' matching the filters above' : ''}${covNote} · at-risk (same-day &amp; not yet done) first, then same-day, then by checkout · "done" = cleaned + verified + ready. KPIs follow the filters.</p>`)
      + this.streakBandHtml();

    // Keep an expanded card visible even when a just-set per-turn mode override
    // moves it out of the active coverage view — otherwise the card (and its
    // undo control) vanishes mid-click. Display set only; KPIs above stay strict.
    let displayTurns = turns;
    if (this.expandedTurn && !turns.some(t => t.id === this.expandedTurn)) {
      const kept = ((this.board && this.board.turns) || []).filter((t: any) =>
        t.id === this.expandedTurn || turns.includes(t));
      if (kept.length > turns.length) displayTurns = kept;
    }

    if (!displayTurns.length) {
      const narrowed = filtered || f.coverage !== 'all';
      const emptyTitle = narrowed ? 'Nothing matches'
        : this.selectedDate ? 'No cleans on this date'
        : this.todayMode ? 'No cleans today' : 'No turns in this window';
      const emptyMsg = narrowed
        ? 'No turns match the current filters — clear a filter (tap the Turns card), switch "Cleaned by" to All, or widen the timeframe.'
        : this.selectedDate
          ? `No checkouts on ${this.selectedDate} (cleans within the next 60 days show here). Clear the date to go back to Today, or "Refresh turns" after new bookings land.`
          : this.todayMode
          ? 'No checkouts today — nothing to clean. Switch to 7d to see the week ahead, or "Refresh turns" after new bookings land.'
          : 'No confirmed checkouts fall in the selected timeframe. Widen it, or "Refresh turns" after new bookings land.';
      return toolbar + band + basis + emptyState(emptyTitle, emptyMsg, '🧹');
    }

    const hdr = (label: string, rows: any[]) => `<div class="hk-audit-hdr">${label} <span class="n">(${rows.length})</span></div>`;
    // Day-focus mode renders BEFORE the calendar branch: the audit list is the
    // only valid single-day rendering (view-toggle handlers keep the states
    // exclusive, but a leaked combo must never hand a 1-day set to the grid).
    if (focusDay) {
      // R45/R46 audit view: the focus day's list grouped by status. Headers
      // always render (a "Cleaned (0)" is itself the audit signal); bucket
      // order puts active work first and finished work last. Server sort is
      // kept inside buckets. The expandedTurn keep-alive can inject an off-day
      // turn (e.g. a clean just added or moved elsewhere) — those render under
      // their own header, never inside the day's buckets or counts.
      const todayRows = displayTurns.filter(t => t.check_out === focusDay);
      const keptRows = displayTurns.filter(t => t.check_out !== focusDay);
      const buckets: Array<[string, (t: any) => boolean]> = [
        ['🔄 In progress', t => t.status === 'in_progress'],
        ['⏳ Not started', t => t.status !== 'in_progress' && !doneSet.includes(t.status)],
        ['✅ Cleaned', t => doneSet.includes(t.status)],
      ];
      const grouped = buckets.map(([label, match]) => {
        const rows = todayRows.filter(match);
        return hdr(label, rows) + rows.map(t => this.renderTurn(t)).join('');
      }).join('');
      const kept = keptRows.length
        ? hdr('📌 Open card — not this day', keptRows) + keptRows.map(t => this.renderTurn(t)).join('')
        : '';
      return toolbar + band + basis + grouped + kept;
    }
    if (this.boardView === 'calendar') return toolbar + band + basis + this.renderBoardCalendar(displayTurns) + this.renderCalAssignModal();
    // R63 (CEO Loom): "have like a today, a tomorrow… this week and a next
    // week" — the multi-day list groups under date-bucket headers instead of
    // one flat run of cards. Weeks are Sunday-anchored like the calendar view,
    // and the anchor is the SERVER business day (as_of), not device-local.
    const asOf = (this.board && this.board.as_of) || this.localToday();
    const dow = new Date(asOf + 'T00:00:00Z').getUTCDay();
    const tomorrow = this.addDaysLocal(asOf, 1);
    const endOfWeek = this.addDaysLocal(asOf, 6 - dow);
    const endOfNextWeek = this.addDaysLocal(asOf, 13 - dow);
    const buckets: Array<[string, (d: string) => boolean]> = [
      ['⚠ Overdue', d => d < asOf],
      ['📅 Today', d => d === asOf],
      ['➡️ Tomorrow', d => d === tomorrow],
      // Saturday anchor: endOfWeek === asOf, so tomorrow (Sunday) would also
      // match "Next week" — exclude it there or the card renders twice.
      ['🗓 Later this week', d => d > tomorrow && d <= endOfWeek],
      ['🗓 Next week', d => d > endOfWeek && d !== tomorrow && d <= endOfNextWeek],
      ['📆 Beyond', d => d > endOfNextWeek],
    ];
    const cards = buckets.map(([label, match]) => {
      const rows = displayTurns.filter((t: any) => match(String(t.check_out || '')));
      if (!rows.length) return '';
      return hdr(label, rows) + rows.map((t: any) => this.renderTurn(t)).join('');
    }).join('');
    return toolbar + band + basis + cards;
  }

  // Calendar view — weeks covering [today, today+days]; each cell holds the day's
  // filtered turns as status-colored chips. Click a chip → that turn in the list.
  private renderBoardCalendar(turns: any[]): string {
    const byDay = new Map<string, any[]>();
    for (const t of turns) {
      if (!t.check_out) continue;
      if (!byDay.has(t.check_out)) byDay.set(t.check_out, []);
      byDay.get(t.check_out)!.push(t);
    }
    // Anchor the grid on the SERVER business day (as_of) — the chips' dates
    // are server-keyed, so a device-local anchor strands them in grayed cells
    // (or off-grid entirely) for non-Pacific devices after their local midnight.
    const today = new Date(((this.board && this.board.as_of) || this.localToday()) + 'T00:00:00Z');
    const end = new Date(today.getTime() + this.days * 86400000);
    // Start the grid on the Sunday of today's week.
    const start = new Date(today.getTime() - today.getUTCDay() * 86400000);
    const todayStr = today.toISOString().split('T')[0];
    const chip = (t: any) => {
      const cls = ['cleaned', 'verified', 'ready'].includes(t.status) ? 'done'
        : t.status === 'in_progress' ? 'prog'
        : t.at_risk ? 'risk' : (t.assigned_to ? 'assigned' : 'open');
      const flags = `${t.same_day ? '⚡' : ''}${!t.assigned_to ? '•' : ''}`;
      return `<button class="hk-cal-chip ${cls}" data-hk-calchip="${this.esc(t.id)}" title="${this.esc(t.listing_name)} · ${this.esc(t.status)}${t.assigned_to ? ` · ${this.esc(this.cleanerName(t.assigned_to))}` : ' · unassigned'}${t.same_day ? ' · same-day' : ''}">${flags}${this.esc(String(t.listing_name || '').slice(0, 14))}</button>`;
    };
    const weeks: string[] = [];
    for (let w = new Date(start); w <= end; w = new Date(w.getTime() + 7 * 86400000)) {
      const cells: string[] = [];
      for (let d = 0; d < 7; d++) {
        const day = new Date(w.getTime() + d * 86400000);
        const ds = day.toISOString().split('T')[0];
        const inRange = day >= today && day <= end;
        const items = (byDay.get(ds) || []);
        cells.push(`
          <div class="hk-cal-cell ${inRange ? '' : 'off'} ${ds === todayStr ? 'today' : ''}">
            <div class="hk-cal-date">${day.getUTCDate() === 1 || (w.getTime() === start.getTime() && d === 0) ? this.esc(day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })) : day.getUTCDate()}</div>
            ${items.map(chip).join('')}
            ${!items.length && inRange ? '<div class="hk-cal-none"></div>' : ''}
          </div>`);
      }
      weeks.push(`<div class="hk-cal-week">${cells.join('')}</div>`);
    }
    return `
      <div class="hk-cal-legend">
        <span><i class="hk-cal-dot open"></i> Unassigned</span><span><i class="hk-cal-dot assigned"></i> Assigned</span>
        <span><i class="hk-cal-dot prog"></i> In progress</span><span><i class="hk-cal-dot done"></i> Done</span>
        <span><i class="hk-cal-dot risk"></i> At risk</span><span>⚡ same-day</span>
      </div>
      <div class="hk-cal">
        <div class="hk-cal-head">${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => `<div>${d}</div>`).join('')}</div>
        ${weeks.join('')}
      </div>`;
  }

  // R48 — chip quick-assign popup. Stays open across assign redraws so the
  // chips recolor behind it; the turn can vanish on reload (filters, window),
  // so a missing id clears the state and renders nothing.
  private renderCalAssignModal(): string {
    if (!this.calAssignTurn) return '';
    const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === this.calAssignTurn);
    if (!t) { this.calAssignTurn = null; return ''; }
    const assignedIds: string[] = (t.assignees && t.assignees.length) ? t.assignees : (t.assigned_to ? [t.assigned_to] : []);
    return `
      <div class="hk-modal" data-hk-calassign-bg>
        <div class="hk-modal-card">
          <div class="hk-modal-head"><h3>🧹 ${this.esc(t.listing_name)}</h3><button class="hk-modal-x" data-hk-calassign-x>✕</button></div>
          <div class="hk-modal-body">
            <div style="font-size:13px; color:var(--text-muted); margin-bottom:4px;">checkout <span class="num">${this.esc(this.fmtDate(t.check_out))}</span> · ${this.esc(this.relDay(t.check_out))} &nbsp;${this.statusPill(t.status)}</div>
            <label class="hk-modal-lab">Cleaner</label>
            <select class="hk-modal-sel" data-hk-calassign-sel="${this.esc(t.id)}" data-hk-calassign-slot="0">${this.assignOptsFor(assignedIds[0] || '', '— Unassigned —')}</select>
            <label class="hk-modal-lab">Second cleaner</label>
            <select class="hk-modal-sel" data-hk-calassign-sel="${this.esc(t.id)}" data-hk-calassign-slot="1">${this.assignOptsFor(assignedIds[1] || '', '— none —')}</select>
          </div>
          <div class="hk-modal-foot">
            <button class="hk-btn" data-hk-calassign-open="${this.esc(t.id)}">Open in list ↗</button>
          </div>
        </div>
      </div>`;
  }

  // Cleaner roster as assign options ('' = unassign). Archived cleaners are
  // not in the loaded list; if a turn points at one we keep the raw id row.
  // Shared by the turn card and the calendar quick-assign popup (R48).
  private assignOptsFor(selId: string, unassignedLabel: string): string {
    const opts = [`<option value="" ${!selId ? 'selected' : ''}>${unassignedLabel}</option>`]
      .concat(this.cleaners.map(c => `<option value="${this.esc(c.id)}" ${selId === c.id ? 'selected' : ''}>${this.esc(c.name)}${c.available ? '' : ' (off)'}</option>`));
    if (selId && !this.cleaners.some(c => c.id === selId)) opts.push(`<option value="${this.esc(selId)}" selected>${this.esc(selId)} (archived)</option>`);
    return opts.join('');
  }

  // Initials for the assignee disc: "Marina Gomez" → MG, "Naomi" → NA.
  private initials(name: string): string {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '?';
    const a = words[0][0] || '';
    const b = words.length > 1 ? (words[words.length - 1][0] || '') : (words[0][1] || '');
    return (a + b).toUpperCase();
  }
  private whoChip(name: string): string {
    return `<span class="hk-who"><i class="hk-ini">${this.esc(this.initials(name))}</i>${this.esc(name)}</span>`;
  }
  // Capture photo + video urls as plain strings (old turns stored {url}).
  private captureUrls(t: any): { photos: string[]; videos: string[] } {
    const cap = (t && t.capture) || {};
    const norm = (arr: any) => (Array.isArray(arr) ? arr : []).map((p: any) => (typeof p === 'string' ? p : (p && p.url) || '')).filter(Boolean);
    return { photos: norm(cap.photos), videos: norm(cap.videos) };
  }

  private renderTurn(t: any): string {
    const open = this.expandedTurn === t.id;
    const cls = t.at_risk ? 'risk' : (t.same_day ? 'sameday' : '');
    const deadline = t.next_check_in
      ? `<span class="num">${this.esc(this.fmtDate(t.next_check_in))}</span> <span style="color:var(--text-muted);">(${this.esc(this.relDay(t.next_check_in))})</span>`
      : `<span class="none">no next booking</span>`;
    const badges = [
      t.at_risk ? `<span class="hk-badge risk">⚠ At risk</span>` : '',
      t.same_day ? `<span class="hk-badge sameday">⚡ Same-day</span>` : '',
      t.eci ? `<span class="hk-badge sameday" title="Next guest arrives EARLY">⏰ ECI${t.eci_time ? ` ${this.esc(t.eci_time)}` : ''}</span>` : '',
      t.lco ? `<span class="hk-badge gap" title="Departing guest leaves LATE">🌙 LCO${t.lco_time ? ` ${this.esc(t.lco_time)}` : ''}</span>` : '',
      // R50: cleaner checked out past the photo gate on bad signal — office accountability flag.
      t.photo_gate_skipped ? `<span class="hk-badge risk">📵 photos skipped — connectivity</span>` : '',
      (!t.same_day && t.gap_days != null) ? `<span class="hk-badge gap">${t.gap_days}d gap</span>` : '',
      this.turnMode(t) === 'vendor' ? `<span class="hk-badge gap">Vendor clean${t.mode_override ? ' · this turn' : ''}</span>` : (t.mode_override === 'in_house' ? `<span class="hk-badge gap">In-house · this turn</span>` : ''),
      // R82 (the Sept-8 mystery clean): manual adds say so, and a pending turn
      // on a home cleaned within the prior 2 days carries that context.
      t.source === 'manual' ? `<span class="hk-badge gap" title="Added by hand, not from a reservation checkout">✋ manual${t.created_by ? ` · ${this.esc(t.created_by)}` : ''}</span>` : '',
      t.recent_clean ? `<span class="hk-badge info" title="A completed clean already exists just before this one — double-check this turn is really needed">🧽 cleaned ${this.esc(this.fmtDate(t.recent_clean.check_out))}${t.recent_clean.assigned_to ? ` by ${this.esc(this.cleanerName(t.recent_clean.assigned_to))}` : ''}</span>` : '',
      t.type && t.type !== 'turnover' ? `<span class="hk-badge gap">${this.esc(this.typeLabel(t.type))}</span>` : '',
    ].join('');
    const assignedIds: string[] = (t.assignees && t.assignees.length) ? t.assignees : (t.assigned_to ? [t.assigned_to] : []);
    // Surface "unassigned" loudly on at-risk turns — that's the dispatch gap.
    const who = assignedIds.length
      ? assignedIds.map(id => this.whoChip(this.cleanerName(id))).join('')
      : `<span class="hk-who none ${t.at_risk ? 'risk' : ''}">${t.at_risk ? '⚠ ' : ''}Unassigned</span>`;
    const caps = this.captureUrls(t);
    const nMedia = caps.photos.length + caps.videos.length;

    const row = `
      <div class="hk-turn-row" data-hk-toggle="${this.esc(t.id)}">
        <div style="min-width:0;">
          <div class="hk-turn-prop" title="${this.esc(t.listing_name)}">${this.esc(t.listing_name)}</div>
          <div class="hk-turn-sub">checkout <span class="num">${this.esc(this.fmtDate(t.check_out))}</span> · ${this.esc(this.relDay(t.check_out))}${nMedia ? ` · 📷 ${nMedia}` : ''}</div>
          ${t.note ? `<div class="hk-turn-sub" style="color:var(--warning-text);" title="${this.esc(t.note)}">📝 ${this.esc(String(t.note).slice(0, 60))}${String(t.note).length > 60 ? '…' : ''}</div>` : ''}
        </div>
        <div class="hk-turn-badges">${badges || '<span style="color:var(--text-faint); font-size:11px;">standard turn</span>'}</div>
        <div class="hk-turn-dl"><span class="hk-turn-k">Next check-in</span>${deadline}</div>
        <div>${this.statusPill(t.status)}<div class="hk-who-row">${who}</div></div>
        <div class="hk-turn-caret">${open ? '▾' : '▸'}</div>
      </div>
      ${nMedia && !open ? `<div class="hk-turn-strip">${this.thumbStrip(caps.photos, caps.videos, { max: 8, attr: 'data-hk-photfull' })}</div>` : ''}`;

    if (!open) return `<div class="hk-turn ${cls}">${row}</div>`;

    const statusOpts = TURN_STATUSES.map(([k, label]) =>
      `<option value="${k}" ${t.status === k ? 'selected' : ''}>${label}</option>`).join('');
    const typeOpts = TURN_TYPES.map(([k, label]) =>
      `<option value="${k}" ${t.type === k ? 'selected' : ''}>${label}</option>`).join('');
    // Multi-cleaner (R18 #6): a primary select + an optional second cleaner.
    // Both post together as cleaner_ids[]. primary = assignees[0], second = [1].
    const primaryId = assignedIds[0] || '';
    const secondId = assignedIds[1] || '';
    const assigned = assignedIds.length
      ? `<span class="num">${assignedIds.map(id => this.esc(this.cleanerName(id))).join(' + ')}</span>`
      : `<span style="color:var(--text-faint);">unassigned</span>`;

    const body = `
      <div class="hk-turn-body">
        <div class="hk-kv">
          <div><span class="k">Property</span> ${this.esc(t.listing_name)} <span style="color:var(--text-faint);" class="mono">(${this.esc(t.listing_id)})</span></div>
          <div><span class="k">Clean date</span> <span class="num">${this.esc(this.fmtDate(t.check_out))}</span> · ${this.esc(this.relDay(t.check_out))}${t.moved_from ? ` <span class="hk-badge gap" title="Rescheduled — original date ${this.esc(t.moved_from)}">↪ moved from ${this.esc(this.fmtDate(t.moved_from))}</span>` : ''}${t.source === 'manual' ? ' <span class="hk-badge gap" title="Added manually — not from the reservation calendar">manual</span>' : ''}</div>
          <div><span class="k">Departing guest</span> ${t.checkout_guest ? this.esc(t.checkout_guest) : '<span style="color:var(--text-faint);">—</span>'}</div>
          <div><span class="k">Next check-in</span> ${deadline}</div>
          <div><span class="k">Gap</span> ${t.gap_days == null ? '<span style="color:var(--text-faint);">no next booking</span>' : `<span class="num">${t.gap_days}d</span>${t.same_day ? ' <span class="hk-badge sameday">same-day</span>' : ''}`}</div>
          <div><span class="k">Type</span> ${this.esc(this.typeLabel(t.type))}</div>
          <div><span class="k">Assigned cleaner</span> ${assigned}</div>
        </div>
        ${t.photo_gate_skipped ? (() => {
          // R50: which required photos were missing at the skipped checkout — clears as queued uploads land.
          const titles = (t.photo_gate_skipped.missing || []).map((m: any) => this.esc(typeof m === 'string' ? m : (m?.title || m?.name || ''))).filter(Boolean);
          return `<div class="hk-turn-sub" style="color:var(--warning-text); margin:2px 0 8px;">📵 Checked out without required photos (connectivity)${titles.length ? ` — missing: ${titles.join(', ')}` : ''} · will clear as queued photos land</div>`;
        })() : ''}
        <div class="hk-turn-controls">
          <label>Cleaner
            <select data-hk-assign="${this.esc(t.id)}" data-hk-assign-slot="0">${this.assignOptsFor(primaryId, '— Unassigned —')}</select>
          </label>
          <label>Second cleaner <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(big properties)</span>
            <select data-hk-assign="${this.esc(t.id)}" data-hk-assign-slot="1">${this.assignOptsFor(secondId, '— none —')}</select>
          </label>
          <label>Status
            <select data-hk-status="${this.esc(t.id)}">${statusOpts}</select>
          </label>
          <label>Type
            <select data-hk-type="${this.esc(t.id)}">${typeOpts}</select>
          </label>
          <label>Move to date <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(reschedules this clean + its inspection)</span>
            <input type="date" data-hk-movedate="${this.esc(t.id)}" value="${this.esc(t.check_out)}" min="${this.esc(this.localToday())}" ${['cleaned', 'verified', 'ready'].includes(t.status) ? 'disabled title="Already done — can\'t reschedule"' : ''}>
          </label>
          ${t.orphaned ? `<div style="flex:1 1 100%; padding:6px 9px; background:var(--warning-soft); border-left:3px solid var(--warning); border-radius:4px; font-size:12px;">⚠ <strong>No reservation checks out this day</strong> — the guest's dates changed or the booking cancelled after this clean was scheduled. If a clean already exists on the new date, cancel this one.</div>` : ''}
          ${!['cleaned', 'verified', 'ready'].includes(t.status) ? `<button class="hk-btn sm" data-hk-cancelturn="${this.esc(t.id)}" style="color:var(--danger-text);">🗑 Cancel this clean</button>` : ''}
          <label style="flex:1 1 240px;">Admin note <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(status for the office — "waiting on cleaner", …)</span>
            <input type="text" data-hk-note="${this.esc(t.id)}" value="${this.esc(t.note || '')}" placeholder="—" maxlength="300" style="width:100%; box-sizing:border-box;">
          </label>
          <button class="hk-btn sm" data-hk-editcodes="${this.esc(String(t.listing_id))}" title="Update the door / entry code cleaners see on the job sheet (writes to Property Details → Access)">✎ edit codes</button>
          <label>ECI / LCO <span style="font-weight:400; text-transform:none; color:var(--text-faint);">(early check-in · late check-out)</span>
            <div class="hk-seg" style="margin-top:2px;">
              <button class="${t.eci ? 'on' : ''}" data-hk-ecilco="${this.esc(t.id)}|eci" ${t.next_check_in ? '' : 'disabled title="No next check-in on file"'}>⏰ ECI</button>
              <button class="${t.lco ? 'on' : ''}" data-hk-ecilco="${this.esc(t.id)}|lco">🌙 LCO</button>
            </div>
            ${(t.eci || t.lco) ? `<div style="display:flex; gap:6px; margin-top:4px;">
              ${t.eci ? `<input type="text" data-hk-ecitime="${this.esc(t.id)}|eci" value="${this.esc(t.eci_time || '')}" placeholder="1 PM" maxlength="20" style="width:70px;" title="ECI time — cleaners &amp; inspectors see it on the job">` : ''}
              ${t.lco ? `<input type="text" data-hk-ecitime="${this.esc(t.id)}|lco" value="${this.esc(t.lco_time || '')}" placeholder="11 AM" maxlength="20" style="width:70px;" title="LCO time — cleaners &amp; inspectors see it on the job">` : ''}
            </div>` : ''}
          </label>
          <label>Cleaned by — this turn
            <div class="hk-seg" style="margin-top:2px;">
              <button class="${!t.mode_override ? 'on' : ''}" data-hk-modeover="${this.esc(t.id)}|" title="Use the property's global setting (Scheduler → Properties)">Default (${this.modeOf(t.listing_id) === 'vendor' ? 'Vendor' : 'In-house'})</button>
              <button class="${t.mode_override === 'in_house' ? 'on' : ''}" data-hk-modeover="${this.esc(t.id)}|in_house" title="Override just this turn to the hourly in-house team">In-house</button>
              <button class="${t.mode_override === 'vendor' ? 'on' : ''}" data-hk-modeover="${this.esc(t.id)}|vendor" title="Override just this turn to the outside vendor">Vendor</button>
            </div>
          </label>
          ${(() => {
            // R51: per-property standard-checklist override (ao_checklist_overrides).
            // Fetched lazily when the card expands; disabled until we know the state.
            const lid = String(t.listing_id);
            const known = this.checklistOverrides.has(lid);
            const ov = this.checklistOverrides.get(lid);
            return `<label style="flex-direction:row; align-items:center; gap:8px; text-transform:none; letter-spacing:0; font-size:12.5px; font-weight:500; color:var(--text);" title="Hides the STANDARD checklist items on this property's cleaning tickets — quirks, reference areas and flagged items still show.">
              <input type="checkbox" data-hk-skipstd="${this.esc(lid)}" ${known && ov && ov.disable_standard ? 'checked' : ''} ${known ? '' : 'disabled'}>
              Skip standard checklist for this property${known ? '' : ' <span style="color:var(--text-faint);">(loading…)</span>'}
            </label>`;
          })()}
        </div>
        ${nMedia ? `<div style="margin-top:14px;">
          <div style="font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin-bottom:6px;">📸 Clean photos &amp; video (${nMedia}) <span style="font-weight:400; text-transform:none; letter-spacing:0;">— tap one to page through them</span></div>
          ${this.thumbStrip(caps.photos, caps.videos, { attr: 'data-hk-photfull', wrap: true })}
        </div>` : ''}
        <div class="hk-turn-report">
          ${(Number(t.total_minutes) > 0 || (t.sla && t.sla.started_at)) ? `<span>⏱ ${Number(t.total_minutes) > 0 ? `Tracked <strong>${this.fmtDur(Number(t.total_minutes))}</strong>` : ((t.timer_started_at && !['cleaned', 'verified', 'ready'].includes(t.status)) ? 'Timer running' : 'No time tracked')}${t.sla && t.sla.started_at ? ` · started ${this.esc(this.fmtStampPT(t.sla.started_at))}` : ''}${t.sla && t.sla.cleaned_at ? ` · completed ${this.esc(this.fmtStampPT(t.sla.cleaned_at))}` : ''}</span><span class="hk-spacer"></span>` : ''}
          <button class="btn btn-secondary" data-hk-clean-report="${this.esc(t.listing_id)}" title="Open the photo-forward owner proof-of-clean — Print / Save as PDF from there">📄 Proof of clean</button>
          <button class="btn btn-secondary" data-hk-clean-pdf="${this.esc(t.listing_id)}" title="Same report with the print dialog already open — hit Save as PDF and send it to the owner">⬇ PDF</button>
        </div>
      </div>`;
    return `<div class="hk-turn ${cls} open">${row}${body}</div>`;
  }

  // ── tab: cleaners (the roster) ───────────────────────────────
  private renderCleaners(): string {
    const editing = this.editCleaner != null;
    const cur = (this.editCleaner && this.cleaners.find(c => c.id === this.editCleaner)) || null;
    const v = (s: any) => this.esc(s == null ? '' : s);
    const form = editing ? `
      <div class="hk-form">
        <div class="grid">
          <label>Name <input data-cf="name" value="${v(cur?.name)}" placeholder="Maria S."></label>
          <label>Type
            <select data-cf="kind">
              <option value="w2" ${(cur?.kind || 'w2') === 'w2' ? 'selected' : ''}>In-house (W2)</option>
              <option value="vendor" ${cur?.kind === 'vendor' ? 'selected' : ''}>Vendor / company</option>
            </select>
          </label>
          <label>Vendor company <span style="font-weight:400; color:var(--text-faint);">(if vendor)</span> <input data-cf="company" value="${v(cur?.company)}" placeholder="Acme Cleaning Co."></label>
          <label>Phone <input data-cf="phone" value="${v(cur?.phone)}" placeholder="(555) 123-4567"></label>
          <label>Email <input data-cf="email" value="${v(cur?.email)}" placeholder="maria@…"></label>
          <label>Rate / turn ($) <input data-cf="rate_per_turn" value="${cur?.rate_per_turn ?? ''}" placeholder="85" inputmode="decimal"></label>
          <label title="Advisory only — used to value tracked time vs the flat per-turn fee">Hourly rate ($) <input data-cf="hourly_rate" value="${cur?.hourly_rate ?? ''}" placeholder="25" inputmode="decimal"></label>
          <label>Zones (comma-sep) <input data-cf="zones" value="${v((cur?.zones || []).join(', '))}" placeholder="Bend, Sunriver"></label>
          <label>Skills (comma-sep) <input data-cf="skills" value="${v((cur?.skills || []).join(', '))}" placeholder="deep clean, linens"></label>
          <label>Rating (0–5) <input data-cf="rating" value="${cur?.rating ?? ''}" placeholder="4.8" inputmode="decimal"></label>
          <label class="row"><input type="checkbox" data-cf="available" ${cur ? (cur.available ? 'checked' : '') : 'checked'}> Available for dispatch</label>
        </div>
        ${this.cfDup ? `<div class="hk-cf-dup" role="alert" style="margin:10px 0 0; padding:10px 12px; background:var(--warning-soft); border:1px solid var(--warning-border); border-radius:var(--radius-sm); font-size:13px; color:var(--warning-text); display:flex; gap:10px; align-items:center; flex-wrap:wrap;">
          <span style="flex:1 1 220px;">${this.esc(this.cfDup.msg)}</span>
          <button class="hk-btn sm" data-cf-dupedit="${this.esc(this.cfDup.id)}" style="min-height:36px;">Edit ${this.esc(this.cfDup.name || 'that cleaner')}</button>
        </div>` : ''}
        <div class="hk-form-actions">
          <button class="hk-btn primary" data-cf-save="${v(cur?.id || '')}">${cur ? 'Save changes' : 'Add cleaner'}</button>
          <button class="hk-btn" data-cf-cancel>Cancel</button>
        </div>
      </div>` : `
      <div class="hk-toolbar">
        <button class="hk-btn primary" data-cf-new>+ Add cleaner</button>
      </div>`;

    const basis = `<p class="hk-basis">Roster of dispatchable cleaners, A–Z · assigned/completed counts are derived live from turns (assigned_to) — not stored, so they never drift. Available cleaners show in the board's assign picker. Login: the app login that works as this cleaner — linked on the Users page, or matched by the same email.</p>`;

    if (!this.cleaners.length) {
      return form + basis + emptyState('No cleaners yet', 'Add a cleaner to start dispatching turns. Their assigned/completed counts populate as you assign turns on the Board.', '🧽');
    }

    // R120 (team: the invited cleaner "doesn't show up" — she sat at #37 of 40
    // in stored order): A–Z, a Login column, a "possible duplicate" badge and an
    // "added by invite — set rate" hint, so a twin is visible before it is made.
    const byName = new Map(this.cleaners.map(c => [String(c.id), String(c.name || '')]));
    const sorted = this.cleaners.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
    const loginCell = (c: any) => {
      const l = c.login;
      if (!l) return `<span style="color:var(--text-faint);" title="${this.loginsUnavailable ? 'Logins could not be read just now — reload to retry' : ''}">—</span>`;
      if (l.linked) return `<span class="hk-login" data-hk-login="linked" style="color:var(--success-text); font-weight:600;">linked${l.name_first ? `: ${this.esc(l.name_first)}` : ''}</span>`;
      if (l.via === 'email') return `<span class="hk-login" data-hk-login="email" style="color:var(--text-muted);">matched by email${l.name_first ? ` (${this.esc(l.name_first)})` : ''}</span>`;
      return `<span class="hk-login" data-hk-login="none" style="color:var(--text-faint);">no login</span>`;
    };
    const inviteHint = (c: any) => (c.rate_per_turn == null && c.hourly_rate == null && (c.source === 'invite' || c.kind === undefined))
      ? `<div class="hk-sub-th" data-hk-invitehint style="color:var(--warning-text);">added by invite — set rate</div>` : '';
    const dupBadge = (c: any) => c.dup_of
      ? ` <span class="hk-chip" data-hk-dup="${this.esc(c.dup_of)}" title="Another active cleaner has the same email or name: ${this.esc(byName.get(String(c.dup_of)) || '')}" style="background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border);">possible duplicate</span>` : '';
    const rows = sorted.map(c => `
      <tr data-hk-clrow="${this.esc(c.id)}">
        <td><strong>${this.esc(c.name)}</strong>${dupBadge(c)}${(c.rating != null) ? ` <span style="color:var(--text-muted); font-size:11px;">★ ${this.esc(c.rating)}</span>` : ''}${c.kind === 'vendor' ? `<div class="hk-sub-th">🏢 ${this.esc(c.company || 'vendor')}</div>` : ''}${inviteHint(c)}</td>
        <td><span aria-hidden="true" title="App login">🔑 </span>${loginCell(c)}</td>
        <td>${c.phone ? this.esc(c.phone) : '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
        <td>${(c.zones || []).map((z: string) => `<span class="hk-chip">${this.esc(z)}</span>`).join('') || '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
        <td>${(c.skills || []).map((s: string) => `<span class="hk-chip">${this.esc(s)}</span>`).join('') || '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
        <td><span class="hk-dot ${c.available ? 'on' : 'off'}"></span>${c.available ? 'Available' : 'Off'}</td>
        <td class="num">${this.esc(this.fmtMoney(c.rate_per_turn))}${c.hourly_rate != null ? `<div class="hk-sub-th">${this.esc(this.fmtMoney(c.hourly_rate))}/hr</div>` : ''}</td>
        <td class="num">${c.assigned ?? 0} open · ${c.completed ?? 0} done</td>
        <td style="white-space:nowrap;">
          <button class="hk-btn sm" data-cf-edit="${this.esc(c.id)}">Edit</button>
          <button class="hk-btn sm" data-cf-del="${this.esc(c.id)}">Remove</button>
        </td>
      </tr>`).join('');

    const table = `
      <table class="hk-table">
        <thead><tr><th>Name</th><th>Login</th><th>Phone</th><th>Zones</th><th>Skills</th><th>Available</th><th>Rate</th><th>Workload</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    return form + basis + table;
  }

  // ── tab: scheduler (R6 #6 — smart scheduling) ─────────────────────────────
  private renderScheduler(): string {
    if (this.schedLoading) return `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    const basis = `<p class="hk-basis">Set each cleaner's working days, shift length, daily clean cap and (optionally) their properties. The engine sizes every clean from bed/bath count + square footage (Hostaway), schedules same-day turns first, never reassigns a clean you dispatched by hand, and tells you how many cleaners each day actually needs. Nothing changes until you apply.</p>`;
    return basis + this.renderSchedCoverage() + this.renderSchedCleaners() + this.renderSchedFacts() + this.renderSchedProposal();
  }

  // R81 #1.1 (Jesse: "a list of properties, and see that they've been
  // assigned a cleaner or not — too hard to see properties without
  // assignments"): the INVERTED view. Dedicated = the property appears in a
  // cleaner's list; any-property cleaners are a shared pool, shown as such;
  // vendor-mode homes clean through their vendor. ⚠ rows have nobody.
  private renderSchedCoverage(): string {
    if (!this.factRows.length) return '';
    // Review #3: with the cleaning-modes fetch failed, modeOf() calls every
    // home in-house — vendor properties would all flag "⚠ nobody covers this".
    // Say the data is missing instead of inventing red flags.
    const modesBanner = !this.modesLoaded
      ? '<div style="margin-bottom:8px; padding:7px 10px; background:var(--warning-soft,#fffbeb); border-left:3px solid var(--warning,#f59e0b); border-radius:6px; font-size:12px;">⚠ Cleaning-mode data didn\'t load — vendor-cleaned homes may show as uncovered until it does (refresh to retry).</div>'
      : '';
    const anyPool = this.schedRows.filter(sr => !(sr.listing_ids || []).length);
    const q2 = this.covSearch.trim().toLowerCase();
    let rows = this.factRows.map((p: any) => {
      const lid = String(p.listing_id);
      const dedicated = this.schedRows.filter(sr => (sr.listing_ids || []).map(String).includes(lid));
      const mode = this.modeOf(lid);
      const covered = dedicated.length > 0 || anyPool.length > 0 || mode === 'vendor';
      return { lid, name: String(p.name || lid), dedicated, mode, covered };
    });
    if (q2) rows = rows.filter(r => r.name.toLowerCase().includes(q2));
    if (this.covUnassignedFirst) {
      rows.sort((a, b) => (Number(a.dedicated.length > 0) - Number(b.dedicated.length > 0))
        || (Number(a.covered) - Number(b.covered))
        || a.name.localeCompare(b.name));
    } else {
      rows.sort((a, b) => a.name.localeCompare(b.name));
    }
    const noDedicated = rows.filter(r => !r.dedicated.length && r.mode !== 'vendor').length;
    const rowHtml = (r: any) => {
      const modeChip = r.mode === 'vendor'
        ? '<span class="hk-chip">Vendor</span>'
        : '<span class="hk-chip" style="background:var(--success-soft,#f0fdf4); color:var(--success-text,#166534);">In-house</span>';
      const who = r.dedicated.length
        ? `<span>${r.dedicated.map((sr: any) => this.esc(sr.name)).join(', ')}</span>`
        : r.mode === 'vendor'
          ? '<span style="color:var(--text-muted);">cleaned by the vendor</span>'
          : anyPool.length
            ? `<span style="color:var(--warning-text,#92400e);">no dedicated cleaner — falls to the any-property pool (${anyPool.length})</span>`
            : '<span style="color:var(--danger-text,#b91c1c); font-weight:700;">⚠ nobody covers this property</span>';
      return `<tr style="${!r.dedicated.length && r.mode !== 'vendor' ? 'background:var(--warning-soft,#fffbeb);' : ''}">
        <td style="padding:7px 12px; font-weight:600;">${this.esc(r.name)}</td>
        <td style="padding:7px 10px;">${modeChip}</td>
        <td style="padding:7px 12px; font-size:12.5px;">${who}</td>
      </tr>`;
    };
    return `
      <div class="hk-perf-sec">
        <h3>Property coverage <span class="num" style="font-weight:400; font-size:12px; color:var(--text-muted);">${noDedicated ? `· ${noDedicated} in-house propert${noDedicated === 1 ? 'y' : 'ies'} without a dedicated cleaner` : '· every in-house property has a dedicated cleaner'}</span></h3>
        <p class="hk-basis">Every active property and who covers it. "Dedicated" = named in a cleaner's property list below; any-property cleaners (${anyPool.length}) pick up the rest; vendor-mode homes clean through their vendor.</p>
        ${modesBanner}
        <div style="display:flex; gap:10px; align-items:center; margin-bottom:8px; flex-wrap:wrap;">
          <input type="search" data-sc-covsearch placeholder="Search properties…" value="${this.esc(this.covSearch)}" style="min-width:200px; padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12.5px;">
          <label style="display:flex; gap:6px; align-items:center; font-size:12px; color:var(--text-muted); cursor:pointer;"><input type="checkbox" data-sc-covsort ${this.covUnassignedFirst ? 'checked' : ''}> unassigned first</label>
        </div>
        <div style="overflow-x:auto; max-height:380px; overflow-y:auto; border:1px solid var(--border-soft,#f1f5f9); border-radius:10px;">
          <table style="width:100%; border-collapse:collapse; font-size:13px;">
            <thead><tr style="position:sticky; top:0; background:var(--card,#fff);"><th style="text-align:left; padding:8px 12px; font-size:10.5px; text-transform:uppercase; color:var(--text-muted);">Property</th><th style="text-align:left; padding:8px 10px; font-size:10.5px; text-transform:uppercase; color:var(--text-muted);">Cleaned by</th><th style="text-align:left; padding:8px 12px; font-size:10.5px; text-transform:uppercase; color:var(--text-muted);">Assignment</th></tr></thead>
            <tbody>${rows.map(rowHtml).join('') || '<tr><td colspan="3" style="padding:16px; text-align:center; color:var(--text-muted);">No properties match.</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;
  }

  private renderSchedCleaners(): string {
    if (!this.schedRows.length) return emptyState('No cleaners yet', 'Add cleaners on the Cleaners tab first — their availability is what the scheduler fills.', '🧽');
    const dayLabels: [string, string][] = [['sun', 'S'], ['mon', 'M'], ['tue', 'T'], ['wed', 'W'], ['thu', 'T'], ['fri', 'F'], ['sat', 'S']];
    const rows = this.schedRows.map(s => {
      const cid = this.esc(s.cleaner_id);
      const dayBtns = dayLabels.map(([d, l]) =>
        `<button class="hk-dow ${s.days && s.days[d] ? 'on' : ''}" data-sc-day="${cid}|${d}" title="${d}">${l}</button>`).join('');
      const propCount = (s.listing_ids || []).length;
      const propList = this.factRows.map(p => `
        <label class="hk-prop-opt"><input type="checkbox" data-sc-prop="${cid}" value="${this.esc(p.listing_id)}" ${(s.listing_ids || []).includes(String(p.listing_id)) ? 'checked' : ''}> ${this.esc(p.name)}</label>`).join('');
      const isVendor = s.kind === 'vendor';
      return `
        <div class="hk-sched-row" data-sc-row="${cid}">
          <div class="hk-sched-name"><strong>${this.esc(s.name)}</strong>
            ${isVendor ? '<span style="font-size:9.5px; font-weight:700; color:#7c3aed; background:#f3e8ff; border-radius:999px; padding:1px 7px; margin-left:4px;" title="Vendor — capacity is cleans per day/week, hours don\'t apply">VENDOR</span>' : ''}
            ${s.hourly_rate != null && !isVendor ? `<div class="hk-sub-th">${this.fmtMoney(s.hourly_rate)}/hr</div>` : ''}</div>
          <div class="hk-sched-days">${dayBtns}</div>
          ${isVendor
            ? '<label class="hk-sched-num" title="Vendors have no shift-hours dimension — only clean counts cap their capacity">Shift (h)<span style="display:block; padding:6px 2px; color:var(--text-faint,#9ca3af);">n/a</span></label>'
            : `<label class="hk-sched-num">Shift (h)<input type="number" data-sc-shift="${cid}" value="${(Number(s.shift_minutes) || 480) / 60}" min="1" max="16" step="0.5"></label>`}
          <label class="hk-sched-num">Max/day<input type="number" data-sc-max="${cid}" value="${this.esc(s.max_cleans)}" min="1" max="12"></label>
          <label class="hk-sched-num" title="Cleans per week cap — blank = no weekly cap (defaults to the max the daily caps allow)">Max/wk<input type="number" data-sc-maxwk="${cid}" value="${s.max_cleans_week != null ? this.esc(s.max_cleans_week) : ''}" min="1" max="60" placeholder="max"></label>
          <label class="hk-sched-num" title="Most same-day turnovers (checkout and check-in on the same day) this cleaner takes in one day. Blank = the team default (${this.schedRules.max_same_day_default == null ? 'no limit' : this.schedRules.max_same_day_default}).">Max ⚡/day<input type="number" data-sc-maxsd="${cid}" value="${s.max_same_day != null ? this.esc(s.max_same_day) : ''}" min="0" max="12" placeholder="${this.schedRules.max_same_day_default == null ? '–' : this.esc(this.schedRules.max_same_day_default)}"></label>
          <details class="hk-sched-props">
            <summary>${propCount ? `${propCount} propert${propCount === 1 ? 'y' : 'ies'}` : 'Any property'}</summary>
            <input type="search" data-sc-propfilter="${cid}" placeholder="Search properties…" style="width:100%; box-sizing:border-box; margin:6px 0 4px; padding:5px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px;">
            <div class="hk-prop-list">${propList}</div>
          </details>
          <button class="hk-btn primary sm" data-sc-save="${cid}">Save</button>
        </div>`;
    }).join('');
    return `
      <div class="hk-perf-sec">
        <h3>Cleaner schedules</h3>
        <p class="hk-basis">Days they work · shift length · most cleans they can take in a day · most same-day turns (⚡) in a day · which properties they cover (leave at "Any property" for a floater).</p>
        <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin:0 0 12px; padding:8px 12px; border:1px solid var(--border-soft,#f1f5f9); border-radius:10px; font-size:12.5px;">
          <strong>⚡ Same-day turn limit</strong>
          <span style="color:var(--text-muted);">Most same-day turnovers one cleaner takes in a day, unless their own "Max ⚡/day" says otherwise. The scheduler never goes over it; assigning or moving a clean past it asks first.</span>
          <label style="display:flex; gap:6px; align-items:center;">Team default <input type="number" data-sc-sddefault min="0" max="12" value="${this.sdDefaultDraft != null ? this.esc(this.sdDefaultDraft) : this.schedRules.max_same_day_default == null ? '' : this.esc(this.schedRules.max_same_day_default)}" placeholder="no limit" style="width:72px; padding:4px 6px; border:1px solid var(--border); border-radius:6px;"></label>
          <button class="hk-btn sm" data-sc-sdsave>Save limit</button>
        </div>
        ${rows}
      </div>`;
  }

  private renderSchedFacts(): string {
    const missing = this.factRows.filter(p => p.sqft == null).length;
    const vendorCount = this.factRows.filter(p => this.modeOf(p.listing_id) === 'vendor').length;
    const rows = this.factRows.map(p => {
      const mode = this.modeOf(p.listing_id);
      return `
      <tr>
        <td><strong>${this.esc(p.name)}</strong></td>
        <td>
          <div class="hk-seg">
            <button class="${mode === 'in_house' ? 'on' : ''}" data-hk-mode="${this.esc(p.listing_id)}|in_house" title="Hourly in-house housekeeper — time-tracked & scheduled">In-house</button>
            <button class="${mode === 'vendor' ? 'on' : ''}" data-hk-mode="${this.esc(p.listing_id)}|vendor" title="Outside vendor — excluded from the smart schedule">Vendor</button>
          </div>
        </td>
        <td class="num mono">${p.bedrooms ?? '—'}</td>
        <td class="num mono">${p.bathrooms ?? '—'}</td>
        <td><input class="hk-sqft" type="number" data-sc-sqft="${this.esc(p.listing_id)}" value="${p.sqft ?? ''}" placeholder="${p.sqft_estimated ? '~est.' : ''}" min="0">
          ${p.sqft_source === 'hostaway' ? '<span class="hk-sub-th">Hostaway</span>' : p.sqft_source === 'manual' ? '<span class="hk-sub-th">manual</span>' : '<span class="hk-sub-th">estimated</span>'}</td>
        <td class="num mono">${this.fmtDur(p.est_minutes)}</td>
      </tr>`;
    }).join('');
    return `
      <div class="hk-perf-sec">
        <h3 style="cursor:pointer;" data-sc-factstoggle>Properties — cleaning coverage &amp; effort <span style="color:var(--text-muted); font-weight:400; font-size:12px;">${this.factsOpen ? '▾ hide' : '▸ show'} · ${this.factRows.length} properties · ${vendorCount} vendor-cleaned${missing ? ` · ${missing} missing sqft (estimated from bedrooms)` : ''}</span></h3>
        ${this.factsOpen ? `
          <div class="hk-toolbar">
            <button class="hk-btn" data-sc-syncfacts ${this.syncingFacts ? 'disabled' : ''}>${this.syncingFacts ? 'Syncing…' : '⇄ Sync sqft from Hostaway'}</button>
            <span class="hk-basis" style="margin:0;">"Cleaned by" is the property's GLOBAL setting (a turn can override it on the board). Estimated clean = 50min + 25/bed + 20/bath + 0.02/sqft; type a sqft to override (manual wins over Hostaway).</span>
          </div>
          <table class="hk-table">
            <thead><tr><th>Property</th><th>Cleaned by</th><th>Beds</th><th>Baths</th><th>Sqft</th><th>Est. clean</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>` : ''}
      </div>`;
  }

  private renderSchedProposal(): string {
    const gen = `
      <div class="hk-toolbar">
        <label>Horizon</label>
        <div class="hk-seg">
          ${[7, 14].map(d => `<button class="${this.schedHorizon === d ? 'on' : ''}" data-sc-horizon="${d}">${d}d</button>`).join('')}
        </div>
        <button class="hk-btn primary" data-sc-propose ${this.proposing ? 'disabled' : ''}>${this.proposing ? 'Scheduling…' : '⚙ Generate schedule'}</button>
        ${this.schedProposal ? `<button class="hk-btn primary" data-sc-apply ${this.applying ? 'disabled' : ''} style="background:var(--success-text, #16a34a); border-color:var(--success-text, #16a34a);">${this.applying ? 'Applying…' : `✓ Apply ${this.proposalCount()} assignment${this.proposalCount() === 1 ? '' : 's'}`}</button>` : ''}
      </div>
      ${this.schedNote ? `<div style="font-size:12.5px; margin-bottom:12px; color:${this.schedNote.startsWith('✓') ? 'var(--success-text)' : 'var(--danger-text)'};">${this.esc(this.schedNote)}</div>` : ''}`;

    if (!this.schedProposal) return `<div class="hk-perf-sec"><h3>Proposed schedule</h3>${gen}<p class="hk-basis">Generate to see who cleans what, day by day — locked cleans stay put; proposals fill the gaps.</p></div>`;

    const p = this.schedProposal;
    const dayCards = (p.days || []).map((d: any) => {
      const short = new Date(d.date + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
      const short2 = d.turns.length ? '' : ' hk-schedday-empty';
      const staffTone = d.needed_cleaners > d.available_cleaners ? 'bad' : (d.unassigned > 0 ? 'warn' : 'ok');
      const bars = (d.cleaners || []).filter((c: any) => c.cleans > 0 || d.turns.length).map((c: any) => {
        const raw = Math.round((c.minutes_used / (c.shift_minutes || 480)) * 100);
        const pct = Math.min(100, raw);
        return `
          <div class="hk-loadrow">
            <span class="hk-loadname">${this.esc(c.name)}</span>
            <div class="hk-loadbar"><div class="hk-loadfill ${raw > 100 ? 'over' : ''}" style="width:${pct}%;"></div></div>
            <span class="hk-loadval">${this.fmtDur(c.minutes_used)} · ${c.cleans}/${c.max_cleans}${c.same_day_cleans ? ` · ⚡${c.same_day_cleans}${c.max_same_day != null ? `/${c.max_same_day}` : ''}` : ''}${raw > 100 || (c.max_same_day != null && c.same_day_cleans > c.max_same_day) ? ' ⚠' : ''}</span>
          </div>`;
      }).join('');
      const turnRows = (d.turns || []).map((t: any) => {
        const who = t.locked
          ? `<span class="hk-schwho locked">🔒 ${this.esc(this.cleanerName(t.assigned_to))}</span>`
          : t.proposed_cleaner
            ? `<span class="hk-schwho prop">→ ${this.esc(t.proposed_cleaner)}</span>`
            : `<span class="hk-schwho none">⚠ ${this.esc(t.reason || 'unassigned')}</span>`;
        return `
          <div class="hk-schturn">
            <span class="hk-schname">${t.same_day ? '⚡ ' : ''}${this.esc(t.listing)}</span>
            <span class="hk-schest mono" title="${t.beds}bd/${t.baths}ba · ${t.sqft}sqft${t.sqft_estimated ? ' (est.)' : ''}">${this.fmtDur(t.est_minutes)}</span>
            ${who}
          </div>`;
      }).join('') || '<div class="hk-cap-empty">No cleans due.</div>';
      return `
        <div class="hk-schedday${short2}">
          <div class="hk-schedday-h">
            <strong>${this.esc(short)}</strong>
            <span class="hk-staff ${staffTone}">${d.turns.length ? `needs ${d.needed_cleaners} · has ${d.available_cleaners}` : 'clear'}</span>
            ${d.same_day_turns ? `<span class="hk-staff ${(d.turns || []).some((t: any) => t.same_day && !t.locked && !t.proposed_cleaner_id) ? 'bad' : 'ok'}" title="Same-day turnovers still to clean that day${d.same_day_capacity != null ? ` · the day's crew can take ${d.same_day_capacity} under the same-day limit` : ''}">⚡ ${d.same_day_turns} same-day</span>` : ''}
          </div>
          ${bars}
          ${turnRows}
        </div>`;
    }).join('');

    return `
      <div class="hk-perf-sec">
        <h3>Proposed schedule</h3>
        ${gen}
        <p class="hk-basis">${this.esc(p.note || '')}${p.vendor_turns_skipped ? ` · ${this.esc(p.vendor_turns_skipped)} vendor-cleaned turn${p.vendor_turns_skipped === 1 ? '' : 's'} excluded (their vendor handles those).` : ''}</p>
        <div class="hk-schedgrid">${dayCards}</div>
      </div>`;
  }

  private proposalCount(): number {
    if (!this.schedProposal) return 0;
    return (this.schedProposal.days || []).reduce((a: number, d: any) =>
      a + (d.turns || []).filter((t: any) => !t.locked && t.proposed_cleaner_id).length, 0);
  }

  // ── tab: performance (Phase 4 — scorecards + cost-vs-fee + payouts) ──
  // READ-ONLY intelligence over completed turns in a lookback window. Every
  // cost/margin/payout surface carries the honesty caption verbatim, and every
  // rate shows its denominator inline (PRINCIPLES.md #4).
  private renderPerformance(): string {
    // Cost-vs-fee, payouts and time-value are financial → Admin + Director only.
    let role = ''; try { role = sessionStorage.getItem('userRole') || ''; } catch (_) { /* */ }
    if (role !== 'admin' && role !== 'director') {
      return emptyState('Financials are restricted', 'Housekeeping cost, margin, payouts and labor time-value are limited to Admin and Director roles. Board, cleaners and the cleaner day view stay available.', '🔒');
    }
    const toolbar = `
      <div class="hk-toolbar">
        <label>Window</label>
        <div class="hk-seg" data-hk-perfwin>
          ${[30, 90, 180].map(d => `<button class="${this.perfDays === d ? 'on' : ''}" data-hk-perfdays="${d}">${d}d</button>`).join('')}
        </div>
        <label>Cleaned by <span style="font-weight:400;text-transform:none;">(cost report)</span></label>
        <div class="hk-seg" data-hk-perfcov>
          ${([['in_house', 'In-house'], ['vendor', 'Vendor'], ['all', 'All']] as const).map(([v, l]) => `<button class="${this.perfCoverage === v ? 'on' : ''}" data-hk-perfcovval="${v}" ${this.perfLoading ? 'disabled' : ''}>${l}</button>`).join('')}
        </div>
      </div>`;

    if (this.perfLoading) {
      return toolbar + `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    }
    if (!this.perfStats && !this.perfCosts && !this.perfPayouts && !this.perfDaily && !this.perfPeriods) {
      return toolbar + emptyState('Performance unavailable', `The reports under ${API}/housekeeping/* failed — check the server log.`, '⚠️');
    }

    // R78 (Payroll & Time Tracking Audit): escalations first, then the daily
    // hours tracker (Part 4), pay-period close (Part 5), unit cost vs target —
    // the original three reports follow.
    return toolbar
      + this.renderPayrollEscalations()
      + this.renderDailyHours()
      + this.renderPayPeriods()
      + this.renderUnitCost()
      + this.renderScorecards()
      + this.renderCosts()
      + this.renderPayouts();
  }

  // ── R78: Part 6 — what to escalate, in one band ─────────────────────────
  // Composes: 5/6+ consecutive-day streaks (/staff/streaks), >12h tracker days
  // (daily-hours), properties over target (unit-cost), and the latest period's
  // reconciliation mismatches. Quiet single line when everything is clean.
  private renderPayrollEscalations(): string {
    const items: string[] = [];
    for (const f of (this.streakFlags || [])) {
      items.push(`<strong>${this.esc(f.name)}</strong> is on <strong>${f.len} consecutive days</strong> (${this.esc(f.start)} → ${this.esc(f.end)}) — day 7 is overtime no matter how short; raise with the scheduler before it's booked.`);
    }
    if (this.streakFlags === null) this.streakBandHtml();   // kicks off the lazy fetch once
    const dcleaners = new Map<string, string>(((this.perfDaily?.cleaners || []) as any[]).map((c: any) => [String(c.id), c.name]));
    for (const o of (this.perfDaily?.over12 || [])) {
      items.push(`<strong>${this.esc(dcleaners.get(String(o.cleaner_id)) || o.cleaner_id)}</strong> logged <strong>${this.esc(o.hours)}h on ${this.esc(o.date)}</strong> — over 12h is double time and a second meal period is owed. Verify the punch is real.`);
    }
    for (const e of (this.perfUnit?.escalations || [])) {
      items.push(`<strong>${this.esc(e.listing_name)}</strong> runs over its target clean time — <strong>${this.esc(e.hours_behind)}h behind</strong> in the window (avg ${this.fmtDur(e.avg_minutes || 0)} vs target ${this.fmtDur(e.target_minutes || 0)}). Losing margin on every turnover.`);
    }
    const latest = (this.perfPeriods?.periods || [])[0];
    if (latest) {
      for (const r of (latest.rows || [])) {
        if (r.delta_hours != null && Math.abs(r.delta_hours) > 0.25) {
          items.push(`<strong>${this.esc(r.name)}</strong>: tracker ${this.esc(r.tracker_hours)}h vs register ${this.esc(r.hours)}h (Δ ${r.delta_hours > 0 ? '+' : ''}${this.esc(r.delta_hours)}h) for ${this.esc(latest.start)} → ${this.esc(latest.end)} — something was corrected after the daily look. Reconcile before closing.`);
        }
      }
      for (const m of (latest.tracker_only || [])) {
        items.push(`<strong>${this.esc(m.name)}</strong> has ${this.esc(m.tracker_hours)}h in the tracker but no register row for ${this.esc(latest.start)} → ${this.esc(latest.end)} — worked and not on payroll?`);
      }
    }
    // Honesty guard (review): a failed feed must not read as an all-clear —
    // "nothing to escalate" is only claimable when every source loaded.
    const missing = [
      !this.perfDaily && 'daily hours', !this.perfUnit && 'unit cost', !this.perfPeriods && 'pay periods',
    ].filter(Boolean) as string[];
    const missingLine = missing.length
      ? `<div style="padding:8px 12px; background:var(--danger-soft,#fef2f2); border-left:3px solid var(--danger,#ef4444); border-radius:6px; font-size:12.5px; margin-bottom:6px;">⚠ Couldn't check everything — ${missing.join(', ')} failed to load. What's below covers only the sources that loaded.</div>`
      : '';
    if (!items.length) {
      return `<div class="hk-perf-sec"><h3>🚨 Escalations</h3>${missingLine}${missing.length ? '' : `<p class="hk-basis" style="margin-bottom:0;">Nothing to escalate — no 6-day streaks, no >12h days, no over-target properties, and the latest period reconciles.</p>`}</div>`;
    }
    return `<div class="hk-perf-sec"><h3>🚨 Escalations</h3>
      <p class="hk-basis">The audit's Part 6 triggers, from live data. Consecutive days and >12h shifts are scheduling decisions — fix them before payroll does.</p>
      ${missingLine}
      ${items.map(x => `<div style="padding:8px 12px; background:var(--warning-soft,#fffbeb); border-left:3px solid var(--warning,#f59e0b); border-radius:6px; font-size:12.5px; margin-bottom:6px;">${x}</div>`).join('')}
    </div>`;
  }

  // ── R78: Part 4 — daily hours tracker ───────────────────────────────────
  // One editable grid: cleaners × the last 14 days, each cell the total hours
  // that cleaner logged that day (from the timekeeping app, entered here as
  // part of the daily audit). Saves on change, serialized.
  private renderDailyHours(): string {
    const d = this.perfDaily;
    if (!d) return `<div class="hk-perf-sec"><h3>⏱ Daily hours tracker</h3>${emptyState('Daily hours unavailable', 'GET /housekeeping/daily-hours failed — check the server log.', '⚠️')}</div>`;
    const cleaners: any[] = d.cleaners || [];
    if (!cleaners.length) return `<div class="hk-perf-sec"><h3>⏱ Daily hours tracker</h3>${emptyState('No cleaners on the roster', 'Add cleaners on the Cleaners tab first.', '🧽')}</div>`;
    const to = d.to || this.localToday();
    const days: string[] = Array.from({ length: 14 }, (_, i) => this.addDaysLocal(to, -(13 - i)));
    const hrs = d.hours || {};
    const val = (date: string, cid: string): number | null => {
      const v = (hrs[date] || {})[cid];
      return v == null ? null : Number(v);
    };
    const dayTotal = (date: string) => cleaners.reduce((s, c) => s + (val(date, String(c.id)) || 0), 0);
    const rowTotal = (cid: string) => Object.keys(hrs).reduce((s, date) => s + ((hrs[date] || {})[cid] != null ? Number(hrs[date][cid]) : 0), 0);
    const head = days.map(date => `<th class="num" style="min-width:52px;" title="${this.esc(date)}">${this.esc(date.slice(5))}</th>`).join('');
    const rows = cleaners.map(c => {
      const cid = String(c.id);
      const cells = days.map(date => {
        const v = val(date, cid);
        const over = v != null && v > 12;
        return `<td style="padding:2px;"><input type="number" min="0" max="24" step="0.25" value="${v == null ? '' : this.esc(v)}" data-hk-dh="${this.esc(date)}|${this.esc(cid)}" style="width:48px; padding:4px 4px; border:1px solid ${over ? 'var(--danger,#ef4444)' : 'var(--border-soft,#e2e8f0)'}; border-radius:6px; font-size:12px; text-align:right; background:${over ? 'var(--danger-soft,#fef2f2)' : 'var(--card,#fff)'};" title="${over ? 'Over 12h — double time + second meal period owed' : `${this.esc(c.name)} · ${this.esc(date)}`}"></td>`;
      }).join('');
      return `<tr><td style="position:sticky; left:0; background:var(--card,#fff); z-index:1;"><strong>${this.esc(c.name)}</strong></td>${cells}<td class="num mono" style="font-weight:700;">${rowTotal(cid) ? rowTotal(cid).toFixed(2).replace(/\.?0+$/, '') + 'h' : '—'}</td></tr>`;
    }).join('');
    const totals = days.map(date => `<td class="num mono" style="font-weight:600;">${dayTotal(date) ? dayTotal(date).toFixed(2).replace(/\.?0+$/, '') : '—'}</td>`).join('');
    return `
      <div class="hk-perf-sec">
        <h3>⏱ Daily hours tracker</h3>
        <p class="hk-basis">${this.esc(d.basis || '')} Last 14 days shown (data kept for ${this.esc(d.from)} onward); the 31-day window feeds each pay period's reconciliation. Row total = the full loaded window.</p>
        <div style="overflow-x:auto;">
          <table class="hk-table" style="width:auto; min-width:100%;">
            <thead><tr><th style="position:sticky; left:0; background:var(--card,#fff); z-index:1;">Cleaner</th>${head}<th class="num" title="Sum of every loaded day (${this.esc(d.from)} → ${this.esc(d.to)}), not just the 14 shown">Window total</th></tr></thead>
            <tbody>${rows}
              <tr><td style="position:sticky; left:0; background:var(--card,#fff); z-index:1; color:var(--text-muted); font-weight:600;">Day total</td>${totals}<td></td></tr>
            </tbody>
          </table>
        </div>
      </div>`;
  }

  // ── R78: Part 5 — pay period close ──────────────────────────────────────
  private renderPayPeriods(): string {
    const pp = this.perfPeriods;
    if (!pp) return `<div class="hk-perf-sec"><h3>💵 Pay periods & loaded rates</h3>${emptyState('Pay periods unavailable', 'GET /housekeeping/pay-periods failed — check the server log.', '⚠️')}</div>`;
    const periods: any[] = pp.periods || [];
    // Suggest the semi-monthly period containing today (1–15 / 16–EOM).
    const today = this.localToday();
    const [y, m, dd] = today.split('-').map(Number);
    const eom = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const sug = dd <= 15
      ? { start: `${today.slice(0, 8)}01`, end: `${today.slice(0, 8)}15` }
      : { start: `${today.slice(0, 8)}16`, end: `${today.slice(0, 8)}${String(eom).padStart(2, '0')}` };
    const sugExists = periods.some(p => p.start === sug.start && p.end === sug.end);
    const newBar = `
      <div style="display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap; margin-bottom:10px;">
        <button class="hk-btn primary sm" data-hk-ppnew="${this.esc(sug.start)}|${this.esc(sug.end)}" ${sugExists ? 'disabled' : ''}>＋ ${sugExists ? 'Current period exists' : `Start ${this.esc(sug.start)} → ${this.esc(sug.end)}`}</button>
        <label style="font-size:11px; color:var(--text-muted); font-weight:600; display:flex; flex-direction:column; gap:3px;">Custom start<input type="date" data-hk-ppstart style="padding:6px 8px; border:1px solid var(--border); border-radius:8px; font-family:inherit;"></label>
        <label style="font-size:11px; color:var(--text-muted); font-weight:600; display:flex; flex-direction:column; gap:3px;">end<input type="date" data-hk-ppend style="padding:6px 8px; border:1px solid var(--border); border-radius:8px; font-family:inherit;"></label>
        <button class="hk-btn sm" data-hk-ppnewcustom>Create</button>
      </div>`;
    const cards = periods.slice(0, 8).map(p => this.payPeriodCard(p)).join('');
    return `
      <div class="hk-perf-sec">
        <h3>💵 Pay periods & loaded rates</h3>
        <p class="hk-basis">${this.esc(pp.basis || '')}</p>
        ${newBar}
        ${cards || `<div class="hk-basis">No pay periods yet — start one when Paul's reports arrive (we run semi-monthly).</div>`}
        ${periods.length > 8 ? `<div class="hk-basis">Showing the 8 most recent of ${periods.length} periods.</div>` : ''}
      </div>`;
  }
  private payPeriodCard(p: any): string {
    const open = this.ppOpen === p.id;
    const t = p.totals || {};
    const head = `
      <div data-hk-pptoggle="${this.esc(p.id)}" style="display:flex; gap:10px; align-items:center; cursor:pointer; padding:10px 12px;">
        <strong>${this.esc(p.start)} → ${this.esc(p.end)}</strong>
        ${p.closed ? '<span class="hk-pill" style="background:var(--success-soft,#f0fdf4); color:var(--success-text,#166534);">closed</span>' : '<span class="hk-pill" style="background:var(--bg-subtle,#f8fafc); color:var(--text-muted);">open</span>'}
        <span class="mono num" style="font-size:12px; color:var(--text-muted);">register ${this.esc(t.register_hours ?? 0)}h · tracker ${this.esc(t.tracker_hours ?? 0)}h${t.blended_loaded_rate != null ? ` · blended loaded ${this.fmtMoney(t.blended_loaded_rate)}/hr` : ''}</span>
        <span style="margin-left:auto; color:var(--text-muted);">${open ? '▾' : '▸'}</span>
      </div>`;
    if (!open) return `<div style="border:1px solid var(--border); border-radius:10px; margin-bottom:8px;">${head}</div>`;
    const rowByCid = new Map<string, any>(((p.rows || []) as any[]).map((r: any) => [String(r.cleaner_id), r]));
    const trackerOnly = new Map<string, any>(((p.tracker_only || []) as any[]).map((r: any) => [String(r.cleaner_id), r]));
    // Rows = the full roster, so a register can be typed for anyone — plus
    // anyone with existing register or tracker data (review: a cleaner the
    // escalation band flags as "worked and not on payroll" needs a row here
    // to enter their register into).
    const roster: any[] = (this.perfDaily?.cleaners || []).map((c: any) => ({ id: String(c.id), name: c.name }));
    for (const r of (p.rows || [])) if (!roster.some(x => x.id === String(r.cleaner_id))) roster.push({ id: String(r.cleaner_id), name: r.name });
    for (const m of (p.tracker_only || [])) if (!roster.some(x => x.id === String(m.cleaner_id))) roster.push({ id: String(m.cleaner_id), name: m.name });
    const inp = (cid: string, field: string, v: any, step: string) =>
      `<input type="number" min="0" step="${step}" value="${v == null ? '' : this.esc(v)}" data-hk-ppcell="${this.esc(p.id)}|${this.esc(cid)}|${field}" style="width:74px; padding:5px 6px; border:1px solid var(--border-soft,#e2e8f0); border-radius:6px; font-size:12px; text-align:right;">`;
    const rows = roster.map(c => {
      const r = rowByCid.get(c.id) || {};
      const tr = r.tracker_hours ?? trackerOnly.get(c.id)?.tracker_hours ?? null;
      const delta = r.delta_hours;
      const flag = delta != null && Math.abs(delta) > 0.25;
      return `<tr>
        <td><strong>${this.esc(c.name)}</strong></td>
        <td class="num">${inp(c.id, 'hours', r.hours, '0.25')}</td>
        <td class="num">${inp(c.id, 'gross', r.gross, '0.01')}</td>
        <td class="num">${inp(c.id, 'taxes', r.taxes, '0.01')}</td>
        <td class="num mono">${r.loaded_rate != null ? `<strong>${this.fmtMoney(r.loaded_rate)}/hr</strong>` : '<span style="color:var(--text-faint);">—</span>'}</td>
        <td class="num mono">${tr != null ? `${this.esc(tr)}h` : '<span style="color:var(--text-faint);">—</span>'}</td>
        <td class="num mono" style="${flag ? 'color:var(--danger-text,#b91c1c); font-weight:700;' : 'color:var(--text-muted);'}">${delta != null ? `${delta > 0 ? '+' : ''}${this.esc(delta)}h${flag ? ' ⚠' : ' ✓'}` : (tr != null && r.hours == null ? 'no register ⚠' : '—')}</td>
      </tr>`;
    }).join('');
    return `
      <div style="border:1px solid var(--border); border-radius:10px; margin-bottom:8px;">
        ${head}
        <div style="padding:0 12px 12px;">
          <div style="overflow-x:auto;">
            <table class="hk-table">
              <thead><tr><th>Cleaner</th><th class="num">Register hrs</th><th class="num">Gross wages</th><th class="num">Employer taxes</th><th class="num">Loaded rate</th><th class="num">Tracker hrs</th><th class="num">Δ (tracker − reg)</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
          <div class="hk-basis" style="margin-top:6px;">Loaded rate = (gross + employer taxes) ÷ register hours — recalculated from this period only. Δ within ±0.25h reconciles; bigger means a correction landed after the daily look, or hours were never entered here.</div>
          <div style="display:flex; gap:8px; margin-top:8px;">
            <button class="hk-btn sm ${p.closed ? '' : 'primary'}" data-hk-ppclose="${this.esc(p.id)}|${p.closed ? '0' : '1'}">${p.closed ? 'Reopen period' : 'Close period'}</button>
            <button class="hk-btn sm" data-hk-ppdel="${this.esc(p.id)}" style="color:var(--danger-text,#b91c1c);">Delete</button>
          </div>
        </div>
      </div>`;
  }

  // ── R78: cost per clean by property vs target ───────────────────────────
  private renderUnitCost(): string {
    const u = this.perfUnit;
    if (!u) return `<div class="hk-perf-sec"><h3>🎯 Cost per clean vs target</h3>${emptyState('Unit cost unavailable', 'GET /housekeeping/unit-cost failed — check the server log.', '⚠️')}</div>`;
    const props: any[] = u.properties || [];
    const chip = (v: string, why: string) => {
      const map: Record<string, [string, string]> = {
        'on target': ['var(--success-soft,#f0fdf4)', 'var(--success-text,#166534)'],
        'over target': ['var(--danger-soft,#fef2f2)', 'var(--danger-text,#b91c1c)'],
        'under target': ['var(--warning-soft,#fffbeb)', 'var(--warning-text,#92400e)'],
        'wildly variable': ['var(--warning-soft,#fffbeb)', 'var(--warning-text,#92400e)'],
      };
      const [bg, fg] = map[v] || ['var(--bg-subtle,#f8fafc)', 'var(--text-muted,#64748b)'];
      return `<span class="hk-pill" style="background:${bg}; color:${fg};" title="${this.esc(why)}">${this.esc(v)}</span>`;
    };
    const rows = props.map(pr => `
      <tr>
        <td><strong>${this.esc(pr.listing_name)}</strong></td>
        <td class="num mono">${this.esc(pr.timed_cleans)}/${this.esc(pr.cleans)}</td>
        <td class="num mono">${pr.avg_minutes != null ? this.fmtDur(pr.avg_minutes) : '—'}</td>
        <td class="num mono">${pr.target_minutes != null ? this.fmtDur(pr.target_minutes) : '<span style="color:var(--text-faint);" title="' + this.esc(pr.why) + '">—</span>'}</td>
        <td class="num mono">${pr.cost_per_clean != null ? this.fmtMoney(pr.cost_per_clean) : '—'}</td>
        <td class="num mono">${pr.net_fee != null ? this.fmtMoney(pr.net_fee) : '—'}</td>
        <td class="num mono ${pr.margin_per_clean != null ? (pr.margin_per_clean >= 0 ? 'hk-num-pos' : 'hk-num-neg') : ''}">${pr.margin_per_clean != null ? this.fmtMoney(pr.margin_per_clean) : '—'}</td>
        <td>${chip(pr.verdict, pr.why)}</td>
        <td class="num mono">${pr.hours_behind ? `<span style="color:var(--danger-text,#b91c1c);">${this.esc(pr.hours_behind)}h</span>` : '—'}</td>
      </tr>`).join('');
    return `
      <div class="hk-perf-sec">
        <h3>🎯 Cost per clean vs target</h3>
        <p class="hk-basis">${this.esc(u.basis || '')}</p>
        <div style="display:flex; gap:8px; align-items:center; margin-bottom:8px; font-size:12px; color:var(--text-muted);">
          Channel fee <input type="number" min="0" max="50" step="0.5" value="${this.esc(u.channel_fee_pct)}" data-hk-chanfee style="width:58px; padding:4px 6px; border:1px solid var(--border); border-radius:6px; text-align:right;"> % of the cleaning fee — the target uses what we actually keep.
        </div>
        ${props.length ? `
        <div style="overflow-x:auto;">
          <table class="hk-table">
            <thead><tr><th>Property</th><th class="num">Timed/cleans</th><th class="num">Avg time</th><th class="num">Target</th><th class="num">Cost/clean</th><th class="num">Net fee</th><th class="num">Margin</th><th>Verdict</th><th class="num">Hours behind</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>` : emptyState('No in-house cleans in the window', 'Unit cost covers the hourly in-house team — vendor cleans are a flat invoice.', '🧽')}
      </div>`;
  }

  // Cleaner scorecard table — turns, on-time % (with its basis inline), avg grade
  // (+ graded count), AI-override rate, issues caught. No cost here, so no caption.
  private renderScorecards(): string {
    const s = this.perfStats;
    if (!s) return '';
    const cleaners: any[] = s.cleaners || [];
    const basis = `<p class="hk-basis">Completed turns (cleaned + verified + ready) with a checkout in the last ${this.esc(s.window_days)}d, grouped by assigned cleaner${s.unassigned_completed ? ` · ${this.esc(s.unassigned_completed)} completed turn${s.unassigned_completed === 1 ? '' : 's'} had no assigned cleaner` : ''} · all cleans — the "Cleaned by" toggle scopes the cost report only.</p>`;

    if (!cleaners.length) {
      return `<div class="hk-perf-sec"><h3>Cleaner scorecards</h3>${basis}${emptyState('No completed turns yet', 'No completed turns with a cleaner fall in this window. Assign and complete turns on the Board, then check back.', '🧹')}</div>`;
    }

    // Accountability column appears only when the server sent strike fields
    // (admin/director) — cleaners never see their own strike counts here.
    const showStrikes = cleaners.some(c => c.active_strikes != null || c.pending_strike_candidates != null);
    const strikeCell = (c: any) => {
      const active = Number(c.active_strikes || 0);
      const pending = Number(c.pending_strike_candidates || 0);
      if (!active && !pending) return `<td><span style="color:var(--text-faint,#9ca3af);">clear</span></td>`;
      const bits = [];
      if (active) bits.push(`<span style="color:#b91c1c; font-weight:700;">${active} active</span>`);
      if (pending) bits.push(`<span style="color:#c2410c;">${pending} to review</span>`);
      return `<td>${bits.join(' · ')}<div class="hk-sub-th">Performance tab</div></td>`;
    };

    const rows = cleaners.map(c => `
      <tr>
        <td><strong>${this.esc(c.name)}</strong></td>
        <td class="num mono">${this.esc(c.turns_completed)}</td>
        <td>
          <span class="num mono">${this.fmtPct(c.on_time_pct)}</span>
          <div class="hk-sub-th">${this.esc(c.on_time_basis)}</div>
        </td>
        <td>
          ${c.avg_grade == null ? '<span style="color:var(--text-faint, #9ca3af);">—</span>' : `<span class="num mono" style="color:${this.gradeHex(c.avg_grade)};">${this.esc(c.avg_grade)}</span>`}
          <div class="hk-sub-th">${this.esc(c.graded_count)} graded</div>
        </td>
        <td>
          <span class="num mono">${this.fmtPct(c.override_rate)}</span>
          ${c.override_rate != null ? `<div class="hk-sub-th">${this.esc(c.override_count)} of AI-verified released</div>` : '<div class="hk-sub-th">no AI-verified releases</div>'}
        </td>
        <td class="num mono">${this.esc(c.issues_caught)}</td>
        ${showStrikes ? strikeCell(c) : ''}
      </tr>`).join('');

    const table = `
      <table class="hk-table">
        <thead><tr>
          <th>Cleaner</th><th>Turns done</th><th>On-time</th><th>Avg grade</th><th>AI override</th><th>Issues caught</th>${showStrikes ? '<th>Accountability</th>' : ''}
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    return `<div class="hk-perf-sec"><h3>Cleaner scorecards</h3>${basis}${table}</div>`;
  }

  // Cost-vs-fee — totals + by-property + by-cleaner margin. Honesty caption first.
  private renderCosts(): string {
    const c = this.perfCosts;
    if (!c) return '';
    const cap = `<p class="hk-perf-cap">${this.esc(c.note || '')}</p>`;
    const t = c.totals || { turns: 0, cost_known: 0, fee_known: 0, total_cost: 0, total_fee: 0, total_margin: 0, total_minutes: 0, timed_turns: 0, total_time_value: 0 };
    const hrs = (Number(t.total_minutes || 0) / 60);
    const covLabel = c.coverage === 'in_house' ? ' · in-house cleans only' : c.coverage === 'vendor' ? ' · vendor cleans only' : '';
    const basis = `<p class="hk-basis">Completed turns in the last ${this.esc(c.window_days)}d${covLabel} · cost = turn cost, else cleaner rate/turn (the pay of record) · fee = turn fee, else the property cleaning rate · margin only where BOTH are known. <strong>Time-value is advisory</strong> — in-house tracked hours × the cleaner's hourly rate — shown to sanity-check the flat fee against how long cleans actually took; it is NOT what cleaners are paid.</p>`;

    const marginCls = (n: number) => n > 0 ? 'hk-num-pos' : (n < 0 ? 'hk-num-neg' : '');
    const band = `
      <div class="hk-band" style="grid-template-columns:repeat(auto-fit, minmax(150px, 1fr));">
        <div class="hk-stat"><div class="hk-stat-label">Total cost (paid)</div><div class="hk-stat-val mono num">${this.fmtMoney(t.total_cost)}</div><div class="hk-sub-th">${this.esc(t.cost_known)} of ${this.esc(t.turns)} turns had a cost</div></div>
        <div class="hk-stat"><div class="hk-stat-label">Total fee</div><div class="hk-stat-val mono num">${this.fmtMoney(t.total_fee)}</div><div class="hk-sub-th">${this.esc(t.fee_known)} of ${this.esc(t.turns)} turns had a fee</div></div>
        <div class="hk-stat"><div class="hk-stat-label">Total margin</div><div class="hk-stat-val mono num ${marginCls(Number(t.total_margin))}">${this.fmtMoney(t.total_margin)}</div><div class="hk-sub-th">fee − cost, where both known</div></div>
        <div class="hk-stat"><div class="hk-stat-label">Hours tracked</div><div class="hk-stat-val mono num">${hrs.toFixed(1)}h</div><div class="hk-sub-th">${this.esc(t.timed_turns || 0)} of ${this.esc(t.turns)} turns timed</div></div>
        <div class="hk-stat"><div class="hk-stat-label">Time-value <span style="font-weight:400; text-transform:none;">(advisory)</span></div><div class="hk-stat-val mono num">${this.fmtMoney(t.total_time_value)}</div><div class="hk-sub-th">in-house hrs × hourly rate</div></div>
      </div>`;

    const modeChip = (m: string) => m === 'vendor'
      ? '<span class="hk-chip">Vendor</span>'
      : '<span class="hk-chip" style="background:var(--success-soft, #f0fdf4); color:var(--success-text, #166534);">In-house</span>';
    const props: any[] = c.by_property || [];
    const propRows = props.map(p => `
      <tr>
        <td><strong>${this.esc(p.listing_name)}</strong></td>
        <td>${modeChip(p.mode || 'in_house')}</td>
        <td class="num mono">${this.esc(p.turns)}</td>
        <td class="num mono">${this.fmtMoney(p.total_cost)}</td>
        <td class="num mono">${this.fmtMoney(p.total_fee)}</td>
        <td class="num mono ${marginCls(Number(p.total_margin))}">${this.fmtMoney(p.total_margin)}</td>
        <td class="num mono">${p.total_minutes ? this.fmtDur(Number(p.total_minutes)) : '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
      </tr>`).join('');
    const propTable = props.length ? `
      <table class="hk-table">
        <thead><tr><th>Property</th><th>Cleaned by</th><th>Turns</th><th>Cost</th><th>Fee</th><th>Margin</th><th>Time</th></tr></thead>
        <tbody>${propRows}</tbody>
      </table>` : '';

    const clnrs: any[] = c.by_cleaner || [];
    const clnrRows = clnrs.map(p => `
      <tr>
        <td><strong>${this.esc(p.name)}</strong>${p.hourly_rate != null ? ` <span style="color:var(--text-muted); font-size:11px;">${this.fmtMoney(p.hourly_rate)}/hr</span>` : ''}</td>
        <td class="num mono">${this.esc(p.turns)}</td>
        <td class="num mono">${this.fmtMoney(p.total_cost)}</td>
        <td class="num mono">${p.total_minutes ? this.fmtDur(Number(p.total_minutes)) : '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
        <td class="num mono">${p.total_time_value ? this.fmtMoney(p.total_time_value) : '<span style="color:var(--text-faint, #9ca3af);">—</span>'}</td>
      </tr>`).join('');
    const clnrTable = clnrs.length ? `
      <table class="hk-table">
        <thead><tr><th>Cleaner</th><th>Turns</th><th>Cost (paid)</th><th>Time tracked</th><th>Time-value <span class="hk-sub-th" style="display:inline;">advisory</span></th></tr></thead>
        <tbody>${clnrRows}</tbody>
      </table>` : '';

    const breakdowns = (props.length || clnrs.length)
      ? `${propTable ? `<h4 style="margin:16px 0 6px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted);">By property</h4>${propTable}` : ''}
         ${clnrTable ? `<h4 style="margin:16px 0 6px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted);">By cleaner</h4>${clnrTable}` : ''}`
      : emptyState('No completed turns yet', 'No completed turns fall in this window, so there is no cost or fee to total.', '💵');

    return `<div class="hk-perf-sec"><h3>Cost vs fee</h3>${cap}${basis}${band}${breakdowns}</div>`;
  }

  // Payouts — per-cleaner unpaid/paid totals from completed turns' cost +
  // payout_status. Honesty caption. Expand a row to mark individual turns
  // paid/unpaid (POST /turns/:id {payout_status}).
  private renderPayouts(): string {
    const p = this.perfPayouts;
    if (!p) return '';
    const cap = `<p class="hk-perf-cap">${this.esc(p.note || '')}</p>`;
    const basis = `<p class="hk-basis">Per-cleaner payout from completed turns' cost + payout_status · totals sum known costs only · all cleans — the "Cleaned by" toggle scopes the cost report only · expand a cleaner to mark a turn paid/unpaid.</p>`;
    const payouts: any[] = p.payouts || [];

    if (!payouts.length) {
      return `<div class="hk-perf-sec"><h3>Payouts</h3>${cap}${basis}${emptyState('No payouts yet', 'No completed turns with an assigned cleaner. Assign and complete turns first.', '💸')}</div>`;
    }

    const rows = payouts.map(row => {
      const open = this.payoutTurnsOpen === row.cleaner_id;
      const main = `
        <tr data-hk-payouttoggle="${this.esc(row.cleaner_id)}" style="cursor:pointer;">
          <td><strong>${this.esc(row.name)}</strong> <span style="color:var(--text-muted);">${open ? '▾' : '▸'}</span></td>
          <td class="num mono">${this.esc(row.unpaid_turns)} · ${this.fmtMoney(row.unpaid_total)}</td>
          <td class="num mono">${this.esc(row.paid_turns)} · ${this.fmtMoney(row.paid_total)}</td>
        </tr>`;
      if (!open) return main;
      const turns = this.payoutTurns[row.cleaner_id] || [];
      const detail = `
        <tr><td colspan="3">
          <div class="hk-payout-turns">
            ${turns.length ? turns.map(t => {
              const paid = String(t.payout_status || '') === 'paid';
              return `<div class="hk-job-line" style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:4px 0; border-bottom:1px solid var(--border);">
                <span>${this.esc(t.listing_name)} <span style="color:var(--text-muted);" class="num">${this.esc(this.fmtDate(t.check_out))}</span> · ${this.fmtMoney(t.cost)}</span>
                <span style="display:flex; align-items:center; gap:8px;">
                  <span class="hk-pill" style="background:${paid ? 'var(--success-soft)' : 'var(--bg-subtle)'}; color:${paid ? 'var(--success-text, #166534)' : 'var(--text-muted)'};">${paid ? 'paid' : this.esc(t.payout_status || 'unpaid')}</span>
                  <button class="hk-btn sm" data-hk-payoutset="${this.esc(t.id)}|${paid ? 'unpaid' : 'paid'}">${paid ? 'Mark unpaid' : 'Mark paid'}</button>
                </span>
              </div>`;
            }).join('') : `<div style="padding:6px; text-align:center;">${LoadingSpinner.render()}</div>`}
          </div>
        </td></tr>`;
      return main + detail;
    }).join('');

    const table = `
      <table class="hk-table">
        <thead><tr><th>Cleaner</th><th>Unpaid (turns · total)</th><th>Paid (turns · total)</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    return `<div class="hk-perf-sec"><h3>Payouts</h3>${cap}${basis}${table}</div>`;
  }

  // ── tab: cleaner day (the mobile job view) ───────────────────
  private renderDay(field = false): string {
    // R112 (rec E): today's cards come FIRST; the ad-hoc "General" order /
    // damage bar moves below them (per-clean reporting lives inside the
    // ticket), then the week ahead, recent cleans, my stats, the modals.
    if (field) return this.renderDayBody(true) + this.renderFieldActionBar() + this.renderUpcomingSection() + this.renderRecentSection() + this.renderMySections() + this.renderFieldModals();
    // Office view still gets the modals — its job cards carry the same
    // "Request supplies" button, which opens the storefront (R13 review fix).
    return this.renderDayBody(false) + this.renderFieldModals();
  }

  // R37: the last 3 days of this cleaner's turns, collapsed below today's
  // route. Cards reuse the full job sheet — a DONE turn expands to the verify
  // block (add late after-photos / videos), an UNFINISHED one to the whole
  // work UI so an interrupted clean can still be checked out.
  private renderRecentSection(): string {
    if (!this.recentTurns.length) return '';
    const n = this.recentTurns.length;
    const head = `
      <div class="hk-job-sec" style="margin-top:14px;">
        <h4 style="cursor:pointer;" data-hk-recent>🕐 Recent cleans (${n}) <span style="color:var(--text-muted); font-weight:400; font-size:12px;">${this.recentOpen ? '▾ hide' : '▸ show'}</span></h4>
        ${this.recentOpen ? `<p class="hk-basis" style="margin:4px 0 10px;">Your last 3 days. Phone died or a guest walked in before you could upload? Open the clean and add the photos here — they still count.</p>` : ''}
      </div>`;
    if (!this.recentOpen) return head;
    return head + this.recentTurns.map(t => this.renderJob(t)).join('');
  }

  // Jesse #3: the cleaner's upcoming assigned turns, collapsed below today's
  // route. Read-only rows grouped by day — no job sheet, no check-in; it's a
  // heads-up ("Thursday is a double"), not work to start early. Native
  // <details> keeps it stateless — a redraw collapsing it is fine here.
  // R120 (team: "the HK portal doesn't show future cleans"): 14 days ahead
  // (D27); OPEN by default when today is empty (the list IS the news then);
  // and always present once the login is matched — an empty window says
  // "nothing assigned" instead of silently vanishing.
  private upcomingDayHead(ymd: string): string {
    return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  private renderUpcomingSection(): string {
    const N = HousekeepingPage.UPCOMING_DAYS;
    if (!this.upcomingTurns.length) {
      if (!this.dayCleaner) return '';
      return `
      <div class="hk-job-sec" style="margin-top:14px;" data-hk-upcoming="0">
        <div style="font-size:15px; font-weight:600;">📅 Next ${N} days</div>
        <p class="hk-basis" style="margin:4px 0 0;">Nothing assigned to you in the next ${N} days yet — new cleans show up here as the office assigns them.</p>
      </div>`;
    }
    const dayHead = (ymd: string) => this.upcomingDayHead(ymd);
    let lastDay = '';
    const rows = this.upcomingTurns.map(t => {
      const hdr = t.check_out !== lastDay
        ? `<div style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:10px 0 2px;">${this.esc(dayHead(t.check_out))}</div>`
        : '';
      lastDay = t.check_out;
      return hdr + `
        <div style="display:flex; justify-content:space-between; gap:10px; padding:7px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); font-size:13px;">
          <span style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(t.listing_name || t.listing_id)}${t.same_day ? ' <span style="font-size:10.5px; font-weight:700; text-transform:uppercase; padding:1px 7px; border-radius:999px; background:#fef3c7; color:#92400e;">same-day</span>' : ''}</span>
          <span style="white-space:nowrap; color:var(--text-muted); font-size:12px;">${t.next_check_in ? `next guest ${this.esc(this.fmtDate(t.next_check_in))}` : 'no next guest yet'}</span>
        </div>`;
    }).join('');
    const open = !this.dayTurns.length;
    return `
      <div class="hk-job-sec" style="margin-top:14px;" data-hk-upcoming="${this.upcomingTurns.length}">
        <details${open ? ' open' : ''}>
          <summary style="cursor:pointer; font-size:15px; font-weight:600; line-height:36px;">📅 Next ${N} days (${this.upcomingTurns.length})</summary>
          <p class="hk-basis" style="margin:4px 0 2px;">Your assigned cleans over the next ${N} days, read-only. Each opens as a full job on its day.</p>
          ${rows}
        </details>
      </div>`;
  }

  // R14: two collapsible field cards — how MY homes are landing with guests,
  // and where MY supply requests stand. Every number states its basis.
  private renderMySections(): string {
    if (!this.dayCleaner) return '';
    const star = (v: any) => (v != null && Number.isFinite(Number(v))) ? `${Number(v).toFixed(2)}★` : '—';
    const rv = this.myReviews;
    const st = this.myStats;

    const perfBody = !this.myPerfOpen ? '' : `
      <div style="padding:4px 2px 8px;">
        ${st ? `
        <div style="display:flex; gap:14px; flex-wrap:wrap; font-size:13px; margin-bottom:10px;">
          <span><b>${this.esc(st.turns_completed ?? '—')}</b> cleans (90d)</span>
          <span>on-time <b>${st.on_time_pct != null ? this.esc(st.on_time_pct) + '%' : '—'}</b></span>
          <span>avg photo grade <b>${st.avg_grade != null ? this.esc(st.avg_grade) : '—'}</b></span>
          <span>issues caught <b>${this.esc(st.issues_caught ?? 0)}</b></span>
        </div>` : ''}
        ${rv && rv.overall && rv.overall.avg_90 != null
          ? `<div style="font-size:14px; margin-bottom:8px;">Guests rate your homes <b>${star(rv.overall.avg_90)}</b> <span style="color:var(--text-muted); font-size:12px;">(${rv.overall.count_90} reviews, last 90 days)</span></div>`
          : `<div style="font-size:13px; color:var(--text-muted); margin-bottom:8px;">No guest reviews in the last 90 days for the homes you cleaned.</div>`}
        ${rv && rv.listings && rv.listings.length ? rv.listings.map((l: any) => {
          const up = l.avg_90 != null && l.avg_prev_90 != null ? (l.avg_90 - l.avg_prev_90) : null;
          const arrow = up == null ? '' : up > 0.05 ? ' <span style="color:#16a34a;">▲</span>' : up < -0.05 ? ' <span style="color:#dc2626;">▼</span>' : '';
          return `<div style="display:flex; justify-content:space-between; gap:10px; padding:7px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); font-size:13px;">
            <span style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(l.name)} <span style="color:var(--text-faint); font-size:11px;">· ${l.my_turns} clean${l.my_turns === 1 ? '' : 's'}</span></span>
            <span style="white-space:nowrap;"><b>${star(l.avg_90)}</b>${l.count_90 ? ` <span style="color:var(--text-faint); font-size:11px;">(${l.count_90})</span>` : ''}${arrow}</span>
          </div>`;
        }).join('') : ''}
        ${rv && rv.cleanliness_mentions && rv.cleanliness_mentions.length ? `
          <div style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:12px 0 4px;">Guests on cleanliness</div>
          ${rv.cleanliness_mentions.map((m: any) => `
            <div style="font-size:12.5px; padding:6px 0; border-bottom:1px solid var(--border-soft, #f1f5f9);">
              <span style="font-weight:600;">${star(m.rating)}</span> · ${this.esc(m.listing_name)} · <span style="color:var(--text-faint);">${this.esc(m.date)}</span><br>
              <span style="color:var(--text-muted);">“${this.esc(m.snippet)}${m.snippet.length >= 220 ? '…' : ''}”</span>
            </div>`).join('')}` : ''}
        <div style="font-size:10.5px; color:var(--text-faint); margin-top:8px;">${this.esc((rv && rv.basis) || 'Guest overall stay rating on the homes you cleaned — a cleanliness sub-score is not provided by the booking channels.')}</div>
      </div>`;

    const STATUS_STYLE: Record<string, string> = {
      requested: 'background:#fef3c7;color:#92400e;', ordered: 'background:#dbeafe;color:#1d4ed8;',
      delivered: 'background:#dcfce7;color:#166534;', verified: 'background:#f1f5f9;color:#475569;',
    };
    const ordersBody = !this.myOrdersOpen ? '' : `
      <div style="padding:4px 2px 8px;">
        ${this.myOrders.length ? this.myOrders.map(o => `
          <div style="padding:8px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); font-size:13px;">
            <div style="display:flex; justify-content:space-between; gap:8px; align-items:center;">
              <span style="font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(o.listing_name || o.listing_id)}</span>
              <span style="font-size:10.5px; font-weight:700; text-transform:uppercase; padding:2px 8px; border-radius:999px; ${STATUS_STYLE[o.status] || ''}">${this.esc(o.status)}</span>
            </div>
            <div style="color:var(--text-muted); font-size:12px; margin-top:2px;">${this.esc((o.items || []).map((it: any) => `${it.name} ×${it.qty}`).join(' · ') || '—')}</div>
            ${o.needed_by ? `<div style="font-size:11.5px; margin-top:2px; color:${o.status === 'requested' && o.needed_by <= this.localToday() ? 'var(--danger-text,#b91c1c)' : 'var(--text-faint)'};">📅 needed by ${this.esc(o.needed_by)}${o.needed_by_source === 'auto' ? ' (next clean)' : ''}</div>` : ''}
          </div>`).join('') : '<div style="font-size:13px; color:var(--text-muted); padding:6px 0;">No requests yet — use 🧴 Request supplies on a job, or 🛒 Order supplies above.</div>'}
        <div style="font-size:10.5px; color:var(--text-faint); margin-top:6px;">requested → ordered → delivered → verified · your ${this.myOrders.length} most recent requests</div>
      </div>`;

    return `
      <div class="hk-job-sec" style="margin-top:14px;">
        <h4 style="cursor:pointer;" data-hk-mysec="perf">📊 My performance <span style="color:var(--text-muted); font-weight:400; font-size:12px;">${this.myPerfOpen ? '▾ hide' : '▸ show'}</span></h4>
        ${perfBody}
      </div>
      <div class="hk-job-sec" style="margin-top:10px;">
        <h4 style="cursor:pointer;" data-hk-mysec="orders">📦 My supply requests${this.myOrders.length ? ` (${this.myOrders.length})` : ''} <span style="color:var(--text-muted); font-weight:400; font-size:12px;">${this.myOrdersOpen ? '▾ hide' : '▸ show'}</span></h4>
        ${ordersBody}
      </div>`;
  }

  private renderDayBody(field = false): string {
    // Field cleaner: no manager picker — they only ever see their own day.
    const opts = [`<option value="">— Pick a cleaner —</option>`]
      .concat(this.cleaners.map(c =>
        `<option value="${this.esc(c.id)}" ${this.dayCleaner === c.id ? 'selected' : ''}>${this.esc(c.name)}${c.available ? '' : ' (off)'}</option>`))
      .join('');
    const picker = field ? '' : `
      <div class="hk-day-pick" style="flex-direction:row; gap:12px; flex-wrap:wrap; align-items:flex-end;">
        <div style="display:flex; flex-direction:column; gap:6px;"><label>Cleaner</label><select data-hk-daypick>${opts}</select></div>
        <div style="display:flex; flex-direction:column; gap:6px;"><label>Day</label><input type="date" data-hk-daydate value="${this.esc(this.dayDate)}"></div>
      </div>`;
    const basis = `<p class="hk-basis">${field ? "Today's" : `This day's`} assigned turns for this cleaner, same-day (back-to-back) first then by next check-in. Tap a turn for the job sheet — address, access codes, beds — and check in / out as you work.</p>`;

    // R112 first-paint fix: while the login→roster match is in flight the
    // field view shows a spinner, never the "couldn't match" dead end.
    if (field && !this.dayCleaner && this.cleanerResolving) {
      return `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    }
    if (!this.cleaners.length) {
      return picker + emptyState('No cleaners yet', field ? 'The cleaner roster didn\'t load — pull to refresh, or ask your admin to add your cleaner profile.' : 'Add a cleaner on the Cleaners tab first.', '🧽');
    }
    if (!this.dayCleaner) {
      // R120: a login whose roster link points at a REMOVED cleaner profile
      // (and no active profile has its email) — say exactly that, and who fixes it.
      if (field && this.staleLink) {
        return `<div class="hk-stale-link" role="alert" style="margin:4px 0 14px; padding:12px 14px; background:var(--warning-soft); border:1px solid var(--warning-border); border-left:3px solid var(--warning); border-radius:var(--radius-sm); font-size:13.5px; line-height:1.45; color:var(--warning-text);">
          <b>Your login is connected to a cleaner profile that was removed</b> — ask the office to relink it (Users).
        </div>`;
      }
      // R120 review fix (#9 class): the login's link could not be read, so
      // "couldn't match" would be a false alarm; render() matches again next pass.
      if (field && this.linkUnknown) {
        return emptyState('Couldn’t load your cleaner profile', 'Your login’s link to the cleaner roster didn’t load. Pull to refresh in a moment.', '🔄');
      }
      return picker + (field
        ? emptyState('We couldn’t match your login to a cleaner profile', 'Ask your admin to link your account to your cleaner profile (Settings → Users), or to add your login email to your cleaner record.', '👋')
        : emptyState('Pick a cleaner', 'Choose a cleaner above to see today\'s route.', '👋'));
    }
    if (this.dayLoading && !this.dayTurns.length) {
      return picker + `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    }
    if (!this.dayTurns.length) {
      if (field) {
        // R120: the empty day names the NEXT clean (field copy — the office
        // wording "assign turns on the Board" meant nothing to a cleaner).
        const nx = this.upcomingTurns[0];
        const next = nx
          ? `<span data-hk-nextclean>Next: ${this.esc(this.upcomingDayHead(nx.check_out))} · ${this.esc(nx.listing_name || nx.listing_id)}${nx.same_day ? ' (same-day)' : ''}</span>`
          : `Nothing assigned in the next ${HousekeepingPage.UPCOMING_DAYS} days yet.`;
        return basis + emptyState('Nothing today', `No cleans assigned to you with a checkout today. ${next}`, '✅');
      }
      return picker + basis + emptyState('Nothing on this day', `No turns assigned to this cleaner with a checkout on the selected day. Assign turns on the Board, or run the Scheduler and apply its proposals.`, '✅');
    }
    const cards = this.dayTurns.map(t => this.renderJob(t)).join('');
    return picker + basis + cards;
  }

  // ── Ad-hoc field storefront + damage report (not tied to a turn) ──────────
  // R31 #7: labeled GENERAL — per-clean damage/orders live inside that clean's
  // ticket, so this bar is only for anything not tied to one of today's cleans.
  private renderFieldActionBar(): string {
    return `
      <div class="hk-fieldbar-wrap">
        <div class="hk-fieldbar-cap">General — not tied to one of today's cleans</div>
        <div class="hk-fieldbar">
          <button class="hk-fieldbtn store" data-hk-openstore>🛒 General order request</button>
          <button class="hk-fieldbtn damage" data-hk-opendamage>⚠ General damage report</button>
        </div>
        <div class="hk-fieldbar-hint">Working one of the cleans below? Log damage &amp; request supplies inside that cleaning ticket.</div>
      </div>`;
  }
  private renderFieldModals(): string {
    if (this.storeOpen) return this.renderStoreModal();
    return '';
  }
  // R30: assigned-today homes first, then everything else alphabetically —
  // the cleaner's own turns shouldn't need scrolling to find.
  private sortedPickerListings(): any[] {
    const todayIds = new Set(this.dayTurns.map((t: any) => String(t.listing_id)));
    const mine = this.pickerListings.filter(l => todayIds.has(String(l.id)));
    const rest = this.pickerListings.filter(l => !todayIds.has(String(l.id)));
    return [...mine, ...rest];
  }
  // Display label per listing, disambiguated when two listings share a name —
  // the datalist resolves BY LABEL, so labels must be unique.
  private pickerLabelFor(l: any): string {
    const base = String(l.short_name || l.name || l.id);
    const dupes = this.pickerListings.filter(x => String(x.short_name || x.name || x.id) === base);
    return dupes.length > 1 ? `${base} · ${l.id}` : base;
  }
  private renderStoreModal(): string {
    const cartCount = Object.values(this.cart).reduce((a, n) => a + (n || 0), 0);
    const cartTotal = this.catalog.reduce((sum, s) => sum + (this.cart[s.id] || 0) * (Number(s.target_price) || 0), 0);
    const cats = [...new Set(this.catalog.map(s => s.category || 'Other'))];
    const grid = !this.storeLoaded
      ? `<div style="padding:24px;text-align:center;">${LoadingSpinner.render()}</div>`
      : (this.catalog.length ? cats.map(cat => `
          <div class="hk-store-cat">${this.esc(cat)}</div>
          ${this.catalog.filter(s => (s.category || 'Other') === cat).map(s => {
            const q = this.cart[s.id] || 0;
            return `<div class="hk-store-item">
              <div class="hk-store-item-main">
                <div class="hk-store-item-name">${this.esc(s.name)}</div>
                <div class="hk-store-item-sub">${Number(s.pack_qty) > 1 ? `pack of ${Number(s.pack_qty)} ${this.esc(s.unit || '')}` : this.esc(s.unit || '')}${s.target_price != null ? ` · ${this.fmtMoney(s.target_price)}${Number(s.pack_qty) > 1 ? '/pack' : ''}` : ''}</div>
              </div>
              <div class="hk-qty">
                <button class="hk-qbtn" data-hk-cart="${this.esc(s.id)}|dec" ${q ? '' : 'disabled'}>−</button>
                <input class="hk-qval" data-hk-qtyin="${this.esc(s.id)}" type="number" min="0" inputmode="numeric" value="${q}" style="width:46px; text-align:center; border:1px solid var(--border); border-radius:6px; padding:4px 2px; font-size:14px; font-family:inherit; -moz-appearance:textfield;">
                <button class="hk-qbtn" data-hk-cart="${this.esc(s.id)}|inc">+</button>
              </div>
            </div>`;
          }).join('')}`).join('')
        : `<div class="hk-cap-empty">No catalog items found.</div>`);
    // Property: fixed when opened from a turn (mis-orders happen when a tired
    // cleaner can re-pick), a picker when opened from the ad-hoc field bar.
    const lockName = this.storeLock
      ? (this.pickerListings.find(l => String(l.id) === this.storeListing)?.short_name
        || this.pickerListings.find(l => String(l.id) === this.storeListing)?.name
        || this.dayTurns.find(t => String(t.listing_id) === this.storeListing)?.listing_name
        || this.storeListing)
      : '';
    const hasCustom = this.storeCustomName.trim().length > 0;
    return `
      <div class="hk-modal" data-hk-modalbg>
        <div class="hk-modal-card">
          <div class="hk-modal-head"><h3>🛒 Order supplies</h3><button class="hk-modal-x" data-hk-closemodal>✕</button></div>
          <div class="hk-modal-body">
            <label class="hk-modal-lab">Property</label>
            ${this.storeLock
              ? `<div style="font-size:14px; font-weight:600; padding:6px 2px;">🏠 ${this.esc(lockName)}</div>`
              : `<input class="hk-modal-sel" data-hk-storelisting-in list="hk-store-props" autocomplete="off"
                   placeholder="Type to search properties… (your homes today list first)"
                   value="${this.esc((() => { const cur = this.sortedPickerListings().find(l => String(l.id) === this.storeListing); return cur ? this.pickerLabelFor(cur) : ''; })())}">
                 <datalist id="hk-store-props">${this.sortedPickerListings().map(l => `<option value="${this.esc(this.pickerLabelFor(l))}"></option>`).join('')}</datalist>`}
            <div class="hk-store-grid">${grid}</div>
            <div class="hk-store-cat">Something else?</div>
            <div class="hk-store-item">
              <input data-hk-customname placeholder="Anything not in the list…" value="${this.esc(this.storeCustomName)}"
                     style="flex:1; border:1px solid var(--border); border-radius:8px; padding:9px 12px; font-size:14px; font-family:inherit;">
              <input type="number" data-hk-customqty min="1" inputmode="numeric" value="${this.storeCustomQty}"
                     style="flex:none; width:64px; border:1px solid var(--border); border-radius:8px; padding:9px 8px; font-size:14px; text-align:center; font-family:inherit;">
            </div>
          </div>
          <div style="padding:8px 14px 0; display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
            <label style="font-size:12px; font-weight:600; color:var(--text-muted); display:flex; gap:8px; align-items:center;">📅 Soonest date needed
              <input type="date" data-hk-neededby value="${this.esc(this.storeNeededBy)}" min="${this.esc(this.localToday())}" style="padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit;">
            </label>
            <span style="font-size:11px; color:var(--text-faint);">${this.storeNeededBy ? 'matched to the next scheduled clean — change it if you need it sooner' : 'blank = matched to the next scheduled clean automatically'}</span>
          </div>
          <div class="hk-modal-foot">
            <div class="hk-cart-sum">${cartCount} item${cartCount === 1 ? '' : 's'} in cart${hasCustom ? ' + 1 custom' : ''}${cartTotal ? ` · ~${this.fmtMoney(cartTotal)}` : ''}</div>
            <button class="hk-big go" data-hk-submitstore ${((!cartCount && !hasCustom) || !this.storeListing || this.fieldBusy) ? 'disabled' : ''}>${this.fieldBusy ? 'Sending…' : 'Submit order request'}</button>
          </div>
        </div>
      </div>`;
  }
  private async loadStorefront(): Promise<void> {
    if (this.storeLoaded && this.pickerListings.length) return;
    const [cat, lst] = await Promise.all([
      // R115: consumables only (scope=field — par > 0 or a consumable
      // category); TVs / mattresses / AC units are office orders.
      fetch(`${API}/supplies/standard?scope=field`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/listings/active').then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.catalog = (cat && (cat.supplies || (Array.isArray(cat) ? cat : []))) || [];
    this.pickerListings = Array.isArray(lst) ? lst : ((lst && lst.listings) || []);
    this.storeLoaded = true;
  }

  // R39: history fetch honors the optional date range; default stays the
  // rolling 120-day window. R63: the window includes today — the server
  // returns only COMPLETED cleans for today (unstarted stay on the Board).
  private async reloadHistory(): Promise<void> {
    const req = ++this.histReq;
    const q = this.histFrom || this.histTo
      ? `from=${this.histFrom || ''}&to=${this.histTo || ''}`
      : 'days=120';
    const j = await this.get(`/turns/history?${q}&date_mode=${this.histMode}`);
    if (req !== this.histReq) return;   // superseded by a newer range pick
    this.historyRows = (j && j.rows) || [];
    this.historyCapped = !!(j && j.capped);
    this.historyBoardUnknown = !!(j && j.board_unknown);   // review R120 #14 (round 3)
  }

  // Past cleans (R18 #1) — completed turns: duration, photos/videos, damages,
  // supplies, who cleaned. For review + audit.
  // R112 (Jesse #6 "past cleans is a little clunky"): clean CARDS grouped by
  // day (same headers as the board), a photo strip on every card that opens
  // the shared lightbox, and "Open clean" expanding to stacked detail cards.
  // The query (days=120 or from/to + date_mode) and every hook are unchanged.
  private repChip(kind: string, st: string | null): string {
    const lab = kind === 'damage' ? 'Damage' : 'Supplies';
    if (st === 'reported') return `<span class="hk-rep reported">${lab}: reported</span>`;
    if (st === 'none') return `<span class="hk-rep none">${lab}: none found</span>`;
    return `<span class="hk-rep">${lab}: not answered</span>`;
  }
  private renderHistory(): string {
    const rows: any[] = this.historyRows || [];
    const maxD = this.localToday();   // R63 (CEO): today's COMPLETED cleans show here too
    const withPhotos = rows.filter(r => Number(r.photos) > 0).length;
    const filters = `
      <div class="hk-chipgroup">
        <div class="hk-chips">
          <span class="hk-tlabel">From</span><input class="input hk-date" type="date" data-hk-histfrom value="${this.esc(this.histFrom)}" max="${maxD}">
          <span class="hk-tlabel">To</span><input class="input hk-date" type="date" data-hk-histto value="${this.esc(this.histTo)}" max="${maxD}">
          ${this.histFrom || this.histTo ? '<button type="button" class="btn btn-secondary" data-hk-histclear>Clear dates</button>' : ''}
        </div>
        <span class="hk-sep"></span>
        <div class="hk-chips" title="Scheduled = everything that was SUPPOSED to happen those days (any status). Completed = everything that was ACTUALLY finished those days.">
          <span class="hk-tlabel">Dates mean</span>
          <button type="button" class="chip ${this.histMode === 'assigned' ? 'active' : ''}" data-hk-histmode="assigned">Scheduled date</button>
          <button type="button" class="chip ${this.histMode === 'completed' ? 'active' : ''}" data-hk-histmode="completed">Completed date</button>
        </div>
      </div>
      <p class="hk-basis">${this.histMode === 'completed' ? 'Cleans FINISHED in the window — whatever day they were scheduled for.' : 'Cleans SCHEDULED in the window (any status) — in-progress and upcoming turns live on the Board and Cleaner day.'}${rows.length ? ` · ${rows.length} clean${rows.length === 1 ? '' : 's'}, ${withPhotos} with photos` : ''}${this.histFrom || this.histTo ? '' : ' · last 120 days'}</p>
      ${this.historyCapped ? '<div class="hk-basis" style="margin-bottom:8px;">Showing the most recent 600 turns — older days are beyond the cap.</div>' : ''}
      ${this.historyBoardUnknown ? '<div class="hk-basis" data-hk-board-unknown style="margin-bottom:8px;">Couldn\'t check the Issues board just now, so the damage counts may include some the office dismissed there.</div>' : ''}`;
    if (!rows.length) return filters + emptyState('No cleans in the window', 'Widen the dates, or switch "Dates mean" — a clean scheduled one day and finished the next lands under whichever you pick.', '🧹');

    const card = (r: any) => {
      const id = String(r.id);
      const open = this.histOpen.has(id);
      const cleaners: string[] = Array.isArray(r.cleaners) ? r.cleaners : [];
      const who = cleaners.length ? cleaners.map(n => this.whoChip(String(n))).join('') : '<span class="hk-who none">no cleaner</span>';
      const g = r.grade != null && Number.isFinite(Number(r.grade)) ? Number(r.grade) : null;
      const gradeEl = g != null
        ? `<div class="hk-hist-grade" style="border-color:${this.gradeHex(g)}; color:${this.gradeHex(g)};" title="Verification grade">${g}</div>`
        : `<div class="hk-hist-grade" title="No verification grade">—</div>`;
      // R120 review fix (#6): a dismissed damage (native or board, flagged by
      // GET /turns/history) is not a live damage — the red chip counts live
      // ones only; the dismissed ones get a muted note.
      const dmgs: any[] = Array.isArray(r.damages) ? r.damages.filter(Boolean) : [];
      const nDmg = dmgs.filter((d: any) => !d.dismissed).length;
      const nDis = dmgs.length - nDmg;
      const rep = r.reports ? this.repChip('damage', r.reports.damage) + this.repChip('supply', r.reports.supply) : '';
      const chips = [
        nDmg ? `<span class="hk-rep danger">⚠ ${nDmg} damage${nDmg === 1 ? '' : 's'}</span>` : '',
        nDis ? `<span class="hk-rep" data-hk-dmgdismissed title="Damage reports that were dismissed (not counted)">${nDis} dismissed</span>` : '',
        rep,
        Number(r.photos) ? `<span class="hk-rep">📷 ${r.photos}</span>` : '',
        Number(r.videos) ? `<span class="hk-rep">🎥 ${r.videos}</span>` : '',
      ].join('');
      const doneNote = this.histMode === 'completed' && r.history_date && r.history_date !== r.check_out
        ? `<span title="Scheduled for ${this.esc(r.check_out)}, completed ${this.esc(r.history_date)}">scheduled ${this.esc(this.fmtDate(r.check_out))}</span>` : '';
      // The list row carries ≤12 photo urls; the full set rides the opened
      // clean (GET /turns/:id/job) — so "+N" on this strip opens the clean.
      const strip = this.thumbStrip(r.photo_urls || [], r.video_urls || [], { max: 12, total: Number(r.photos || 0) + Number(r.videos || 0), moreAttr: `data-hk-histopen="${this.esc(id)}" title="Open the clean for every photo"` });
      return `
        <div class="hk-hist ${open ? 'open' : ''}">
          <div class="hk-hist-row">
            <div class="hk-hist-main">
              <div class="hk-hist-prop">${this.esc(r.listing_name)}</div>
              <div class="hk-hist-sub">${this.statusPill(r.status)}${who}${r.minutes ? `<span>⏱ ${this.fmtDur(r.minutes)}</span>` : ''}${r.started_at ? `<span>started ${this.esc(this.fmtTimePT(r.started_at))}</span>` : ''}${r.cleaned_at ? `<span>done ${this.esc(this.fmtTimePT(r.cleaned_at))}</span>` : ''}${doneNote}</div>
              ${chips ? `<div class="hk-hist-chips">${chips}</div>` : ''}
            </div>
            ${gradeEl}
          </div>
          ${strip ? `<div style="margin-top:10px;">${strip}</div>` : ''}
          <div class="hk-hist-foot">
            <button type="button" class="btn ${open ? 'btn-primary' : 'btn-secondary'}" data-hk-histopen="${this.esc(id)}" title="Open the full clean — checklist state, photos, reports, activity">${open ? '▾ Close' : '▸ Open clean'}</button>
          </div>
          ${open ? `<div class="hk-hist-detail">${this.histDetailHtml(r)}</div>` : ''}
        </div>`;
    };
    // Grouped by day in the server's order (newest day first).
    let lastDay = '';
    const out: string[] = [];
    for (const r of rows) {
      const day = String(r.history_date || r.check_out || '');
      if (day !== lastDay) {
        lastDay = day;
        const n = rows.filter(x => String(x.history_date || x.check_out || '') === day).length;
        out.push(`<div class="hk-audit-hdr">${this.esc(this.fmtDayBanner(day))} <span class="n">· ${this.esc(this.relDay(day))} · ${n} clean${n === 1 ? '' : 's'}</span></div>`);
      }
      out.push(card(r));
    }
    return filters + out.join('');
  }

  // R78 (Jesse: "go back to past cleans and open the tasks up, see everything"):
  // read-only detail for one past clean — the checklist with what was ticked,
  // stocking checks, reports, issues, verification, every photo/video, and the
  // activity log. Built from the same /turns/:id/job payload the cleaner saw.
  // The list is reconstructed with TODAY'S standards/PDS, so ticks whose item
  // has since been reworded/removed are listed separately rather than dropped.
  // R112: stacked detail cards instead of the two-column table cell.
  private histDetailHtml(r: any): string {
    const d = this.histDetail.get(String(r.id));
    if (!d || d === 'loading') return `<div style="padding:14px; text-align:center;">${LoadingSpinner.render()}</div>`;
    if (d === 'error') return `<div class="hk-hist-sec" style="color:var(--danger-text);">⚠ Couldn't load this clean's detail — <a data-hk-histretry="${this.esc(String(r.id))}" style="cursor:pointer; text-decoration:underline;">retry</a></div>`;
    const t = d.turn || {};
    const done = new Set<string>(Array.isArray(t.checklist_done) ? t.checklist_done : []);
    const flagged: string[] = (t.capture && Array.isArray(t.capture.checklist) ? t.capture.checklist : []).filter((x: any) => typeof x === 'string');
    const listItems = [...new Set([...flagged, ...this.smartChecklist(d)])];
    const extraChecked = [...done].filter(x => !listItems.includes(x));
    const nDone = listItems.filter(it => done.has(it)).length;
    const infos = [...(((d.property_checklist || {}) as any).good_to_know || []), ...this.infoQuirks(d.property_checklist || {})];
    const sla = t.sla || {};
    const stockDone = (t.stock_check && typeof t.stock_check === 'object') ? Object.keys(t.stock_check).filter(k => t.stock_check[k]) : [];
    const v = t.verification || {};
    const issues: any[] = Array.isArray(t.issues) ? t.issues.filter((x: any) => x && typeof x === 'object') : [];
    const liveIssues = issues.filter(iss => this.turnDisposition(iss).state !== 'dismissed');
    const disIssues = issues.filter(iss => this.turnDisposition(iss).state === 'dismissed');
    const boardUnknownNote = issues.some(iss => this.turnDisposition(iss).board_unknown)
      ? `<div class="hk-vnote" data-hk-board-unknown style="margin-top:6px;">Couldn't check the Issues board just now, so some of these may already be dismissed there.</div>` : '';
    const issueLine = (iss: any) => `
        <div style="font-size:12.5px; padding:5px 0; border-bottom:1px dashed var(--border-soft);">
          <span class="hk-sev ${this.esc(iss.severity || 'med')}">${this.esc(iss.severity || 'med')}</span> <span style="font-weight:600; text-transform:capitalize;">${this.esc(iss.type || 'issue')}</span>${iss.note ? ` — ${this.esc(iss.note)}` : ''}
          ${(Array.isArray(iss.photos) && iss.photos.length) ? `<div style="margin-top:4px;">${this.thumbStrip(iss.photos.filter((u: string) => !/\/video\//.test(u)), iss.photos.filter((u: string) => /\/video\//.test(u)), { size: 'sm' })}</div>` : ''}
        </div>`;
    const rep = t.reports || { damage: null, supply: null };
    const log: any[] = Array.isArray(t.log) ? t.log : [];
    const sec = (title: string, body: string) => body ? `<div class="hk-hist-sec"><div class="hk-hist-sec-t">${title}</div>${body}</div>` : '';
    // Per-item proof (review: the point of opening a past clean is "did they
    // match the reference?") — the same reference photos + cleaner's own
    // item-keyed photos the live job sheet shows.
    const refPhotos = new Map<string, string[]>();
    for (const rr of ((d.property_checklist || {}).references || [])) {
      const label = `📸 Match reference: ${rr.label}${rr.caption ? ` (${rr.caption})` : ''}`.slice(0, 160);
      const ph: string[] = Array.isArray(rr.photos) ? rr.photos : [];
      if (ph.length) refPhotos.set(label, [...(refPhotos.get(label) || []), ...ph]);
    }
    // R83 review #15: check items carry example photos now — same keying as
    // the live sheet, or the audit view silently drops them.
    for (const cc of ((d.property_checklist || {}).checks || [])) {
      const label = `☑ ${cc.label}${cc.note ? ` — ${cc.note}` : ''}`.slice(0, 160);
      const ph: string[] = Array.isArray(cc.photos) ? cc.photos : [];
      if (ph.length) refPhotos.set(label, [...(refPhotos.get(label) || []), ...ph]);
    }
    const mine: Record<string, string[]> = (t.checklist_photos && typeof t.checklist_photos === 'object') ? t.checklist_photos : {};
    const li = (it: string) => {
      const ref = refPhotos.get(it) || [];
      const own = Array.isArray(mine[it]) ? mine[it] : [];
      return `<div style="display:flex; gap:7px; align-items:flex-start; font-size:12.5px; padding:3px 0;"><span style="flex:none; color:${done.has(it) ? 'var(--success-text)' : 'var(--text-faint)'};">${done.has(it) ? '✓' : '○'}</span><span>${this.esc(it)}</span></div>`
        + (ref.length ? `<div style="margin:2px 0 4px 19px;">${this.thumbStrip(ref, [], { size: 'sm' })}</div>` : '')
        + (own.length ? `<div style="margin:2px 0 4px 19px;">${this.thumbStrip(own, [], { size: 'sm', cls: 'mine' })}</div>` : '');
    };
    // Log lines carry {at, event, note} — the note alone is opaque ("· 120",
    // a raw cleaner id), so label with the event and resolve assignee ids.
    const logLine = (l: any) => {
      let txt = String(l.note ?? l.line ?? '');
      if (l.event === 'assigned_to' && txt) {
        const c = (this.cleaners || []).find((x: any) => String(x.id) === txt);
        if (c) txt = c.name;
      }
      const label = l.event && l.event !== 'note' ? String(l.event).replace(/_/g, ' ') : '';
      const body = [label, txt].filter(Boolean).join(': ') || String(l.status || '');
      return `<div style="font-size:11.5px; color:var(--text-muted); padding:2px 0;">${l.at ? this.esc(this.fmtStampPT(l.at)) : ''} · ${this.esc(body)}</div>`;
    };
    const stamps = [
      sla.started_at ? `Started <strong>${this.esc(this.fmtStampPT(sla.started_at))}</strong>` : '',
      sla.cleaned_at ? `Completed <strong>${this.esc(this.fmtStampPT(sla.cleaned_at))}</strong>` : '',
      t.total_minutes ? `Tracked <strong>${this.fmtDur(t.total_minutes)}</strong>` : '',
      d.est && d.est.minutes ? `Est. <strong>${this.fmtDur(d.est.minutes)}</strong>` : '',
    ].filter(Boolean).join(' · ');
    const caps = this.captureUrls(t);
    return [
      stamps ? `<div style="font-size:12.5px; color:var(--text-muted);">${stamps}</div>` : '',
      (caps.photos.length || caps.videos.length) ? sec(`Photos &amp; videos (${caps.photos.length + caps.videos.length})`, this.thumbStrip(caps.photos, caps.videos, { wrap: true })) : '',
      sec(`Checklist · ${nDone}/${listItems.length} <span style="font-weight:400; text-transform:none; letter-spacing:0;">(as reconstructed with today's list)</span>`, listItems.map(li).join('') || '<div style="font-size:12.5px; color:var(--text-faint);">—</div>'),
      extraChecked.length ? sec('Also ticked — item since edited or removed', extraChecked.map(x => `<div style="display:flex; gap:7px; font-size:12.5px; padding:2px 0; color:var(--text-muted);"><span style="color:var(--success-text);">✓</span><span>${this.esc(x)}</span></div>`).join('')) : '',
      sec('Reports', `<div style="display:flex; gap:6px; flex-wrap:wrap;">${this.repChip('damage', rep.damage)}${this.repChip('supply', rep.supply)}</div>`),
      // Review R120 #1 / #14 (round 3): "Issues reported (N)" counts LIVE
      // findings only; the dismissed ones (a person's, the office's on the
      // board, the system's duplicate of a note already listed) sit apart,
      // labelled, so a repeated note never reads twice. The board unread →
      // the records' own state, said quietly.
      liveIssues.length ? sec(`Issues reported (${liveIssues.length})`, liveIssues.map(issueLine).join('') + boardUnknownNote) : '',
      disIssues.length ? `<details class="hk-hist-sec" data-hk-hist-dismissed><summary style="font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer;">Dismissed (${disIssues.length})</summary>${disIssues.map((iss: any) => issueLine(iss) + `<div style="font-size:11.5px; color:var(--text-muted); padding:0 0 5px;">${this.esc(this.turnDismissedMeta(this.turnDisposition(iss)))}</div>`).join('')}${liveIssues.length ? '' : boardUnknownNote}</details>` : '',
      stockDone.length ? sec(`Stocking checks (${stockDone.length} ✓)`, `<div style="font-size:12.5px; color:var(--text-muted);">${stockDone.map(x => this.esc(x)).join(' · ')}</div>`) : '',
      (v.grade != null || (v.gaps || []).length) ? sec('Verification', `<div style="font-size:12.5px;">${v.grade != null ? `Grade <strong style="color:${this.gradeHex(v.grade)};">${this.esc(String(v.grade))}</strong>` : ''}${v.ready ? ' · guest-ready' : ''}${(v.gaps || []).length ? `<div style="color:var(--warning-text); margin-top:2px;">Gaps: ${(v.gaps || []).map((g: any) => this.esc(typeof g === 'string' ? g : (g && (g.note || g.label)) || '')).filter(Boolean).join(' · ')}</div>` : ''}</div>`) : '',
      infos.length ? sec('📌 Good to know — info shown on the ticket', infos.map((q: any) => `<div style="font-size:12.5px; padding:2px 0; color:var(--text-muted);"><strong>${this.esc(q.label)}</strong>${q.note ? ` — ${this.esc(q.note)}` : ''}</div>`).join('')) : '',
      log.length ? `<details class="hk-hist-sec"><summary style="font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer;">Activity log (${log.length})</summary>${log.map(logLine).join('')}</details>` : '',
    ].join('');
  }

  // Codes & creds the cleaner needs on arrival (R18 #5): address, WiFi, HK entry
  // code, supply-closet code — from PDS→Hostaway. Anything in job.creds.missing
  // renders "update property details" so the gap is visible and actionable.
  // R112 (rec E): a card per credential with a big value, tap-to-copy (a
  // "Copied" toast), and the address as a one-tap Maps link.
  private renderCreds(job: any): string {
    const c = job.creds || {};
    const missing = new Set<string>(c.missing || []);
    const wifi = c.wifi || {};
    const closet = c.supply_closet || {};
    const card = (lab: string, val: any, o: { copy?: boolean; maps?: boolean; missKey?: string; cls?: string } = {}) => {
      if (!val) {
        if (o.missKey && missing.has(o.missKey)) return `<div class="hk-code-card static ${o.cls || ''}"><div class="hk-code-lab">${lab}</div><div class="hk-update" style="margin-top:5px;">⚠ update property details</div></div>`;
        return '';
      }
      const v = String(val);
      const attr = o.maps ? `data-hk-maps="${this.esc(v)}"` : (o.copy ? `data-hk-copy="${this.esc(v)}"` : '');
      const act = o.maps ? '📍 Open in Maps' : (o.copy ? 'tap to copy' : '');
      return `<div class="hk-code-card ${attr ? '' : 'static'} ${o.cls || ''}" ${attr} ${attr ? 'role="button" tabindex="0"' : ''}>
        <div class="hk-code-lab">${lab}</div>
        <div class="hk-code-val ${o.copy ? 'hk-code' : ''}">${this.esc(v)}</div>
        ${act ? `<div class="hk-code-act">${act}</div>` : ''}
      </div>`;
    };
    const closetVal = [closet.code, closet.location].filter(Boolean).join(' · ');
    return `
      <div class="hk-job-sec hk-codes">
        <h4>🔑 Codes &amp; access <span style="font-weight:400; text-transform:none; letter-spacing:0;">— ${this.esc(job.listing_name)}</span></h4>
        <div class="hk-codes-grid">
          ${card('Address', c.address, { maps: true, missKey: 'Property address', cls: 'addr' })}
          ${card('HK entry code', c.hk_code, { copy: true, missKey: 'HK door / entry code' })}
          ${card('WiFi network', wifi.network, { copy: true, missKey: 'WiFi network' })}
          ${card('WiFi password', wifi.password, { copy: true, missKey: 'WiFi password' })}
          ${card('Supply closet', closetVal, { copy: !!closet.code, missKey: 'Supply closet code / location' })}
        </div>
      </div>`;
  }

  // R35 #9: ONE "Damages & supplies walkthrough" block (was three scattered
  // sections). Walk the home right after check-in: take before photos, log any
  // damage, request any supplies — and if there's nothing, confirm "no
  // damages" / "no supplies". Logging damage or ordering supplies flips the
  // matching confirm automatically (R31), so nobody is forced to claim "no
  // damage" after documenting some. Checkout stays gated on both answers.
  private renderWalkthrough(t: any): string {
    const r = t.reports || { damage: null, supply: null };
    const chip = (kind: 'damage' | 'supply', label: string, doneLabel: string) => {
      const st = r[kind];
      if (st === 'reported') return `<div class="hk-2r-done reported">✓ ${label} — reported</div>`;
      if (st === 'none') return `<div class="hk-2r-done">✓ ${doneLabel} <button class="hk-2r-undo" data-hk-report-undo="${this.esc(t.id)}|${kind}">change</button></div>`;
      return `<button class="hk-2r-btn" data-hk-report-none="${this.esc(t.id)}|${kind}">${doneLabel}</button>`;
    };
    const caps = this.captureUrls(t);
    const hasMedia = caps.photos.length || caps.videos.length;
    const pending = pendingCountFor(`/turns/${t.id}/photo`);
    return `
      <div class="hk-job-sec hk-2r">
        <h4>① Damages &amp; supplies walkthrough</h4>
        <div class="hk-2r-hint">Walk the home first: take your before photos, log any damage, request any supplies. Nothing wrong? Confirm below (required to finish).</div>
        ${hasMedia ? this.captureThumbs(t.capture) : `<div class="hk-cap-empty" style="margin-bottom:8px;">No before-photos yet — snap them as you walk through.</div>`}
        ${pending ? `<div class="hk-cap-empty" style="margin-bottom:8px; color:var(--info-text);">📤 ${pending} photo${pending === 1 ? '' : 's'} uploading in the background — keep working, they'll appear here.</div>` : ''}
        <label class="hk-cap-add ${hasMedia ? '' : 'primary'}" style="margin-bottom:10px; ${hasMedia ? '' : ''}">
          ${hasMedia ? '＋ Add photo / video' : '📷 Before photos / walkthrough video'}
          <!-- R82 #7 (Jesse "need a place to add photos, not sure if I'm missing it"):
               photos were only addable AFTER completing the clean (the verify
               step) — now addable any time. data-hk-JOBPHOTO rides the
               before-tagged handler (review #13: the shared data-hk-photo
               handler stamps kind:'after', which would corrupt the turn's
               only before/after record mid-clean). R38: no capture= — bulk-pick
               from the library works; forcing the camera made 'multiple' dead. -->
          <input type="file" accept="image/*,video/*" multiple data-hk-jobphoto="${this.esc(t.id)}">
        </label>
        <div class="hk-quick" style="margin:0 0 10px;">
          <button class="hk-big outline" data-hk-logissue="${this.esc(t.id)}">＋ Log damage / an issue (photo · voice)</button>
          <button class="hk-big" data-hk-suptoggle="${this.esc(t.id)}">🧴 Request supplies for this home</button>
        </div>
        <div class="hk-2r-row">${chip('damage', 'Damage', 'No damages present')}</div>
        <div class="hk-2r-row">${chip('supply', 'Supplies', 'No supplies needed')}</div>
      </div>`;
  }

  // Past issues reported on this property + a re-flag "still an issue" vote so
  // recurring unfixed issues surface instead of getting buried (R18 #8).
  private renderPastIssues(t: any): string {
    const ctx = this.dayContext[t.id];
    if (!ctx) return '';
    const issues: any[] = (ctx.open_issues || []);
    const reqs: any[] = (ctx.supply_requests || []);
    if (!issues.length && !reqs.length) return '';
    const me = this.currentCleanerName();
    const issHtml = issues.slice(0, 12).map(i => {
      const flagged = i.flags > 0;
      return `<div class="hk-past-iss">
        <div class="hk-past-iss-main">
          <span class="hk-sev ${this.esc(i.severity)}">${this.esc(i.type)}</span>
          <span class="hk-past-iss-note">${this.esc(i.note)}</span>
          <span class="hk-past-iss-date">${this.esc(String(i.check_out || i.at || '').slice(0, 10))}</span>
        </div>
        <button class="hk-flag ${flagged ? 'on' : ''}" data-hk-flag="${this.esc(t.id)}|${this.esc(i.turn_id)}|${this.esc(i.issue_id)}" title="Still an issue? Flag it — repeated flags surface it.">🚩 Still an issue${i.flags ? ` · ${i.flags}` : ''}</button>
      </div>`;
    }).join('');
    const reqHtml = reqs.slice(0, 5).map(rq => `<div class="hk-job-line" style="font-size:12px;"><span class="lab">${this.esc(String(rq.at || '').slice(0, 10))}</span> ${this.esc(rq.items)} <span style="color:var(--text-faint);">· ${this.esc(rq.status)}</span></div>`).join('');
    return `
      <div class="hk-job-sec">
        <h4>🕐 Past issues & requests — don't re-report</h4>
        ${issues.length ? issHtml : '<div class="hk-job-line" style="color:var(--text-faint);">No open past issues.</div>'}
        ${reqs.length ? `<div style="margin-top:8px; font-size:10.5px; font-weight:700; text-transform:uppercase; color:var(--text-muted);">Recent supply requests</div>${reqHtml}` : ''}
      </div>`;
  }

  // The cleaner's display name (for flag attribution).
  private currentCleanerName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return p.full_name || p.email || 'housekeeping'; }
    catch { return 'housekeeping'; }
  }

  // R52 (Mark: standard inventory "not currently showing up" on the cleaner
  // checklist): the R41 bare-minimum stocking standard, visible on every job as
  // read-only reminders. COPIED from InspectionsPage.STOCK_ITEMS (private static,
  // client/src/pages/InspectionsPage.ts) — keep the two lists in sync.
  private static readonly STOCK_ITEMS: string[] = [
    '1.5× linens', 'Dishwasher soap', 'Dish soap', 'Sponges', 'Hand soap',
    'Body wash', 'Shampoo', 'Conditioner', 'Trash bags', 'Cooking oil',
    'Salt', 'Pepper', 'Coffee', 'Creamer', 'Sugar', 'Coffee filters',
  ];
  // R70 (Mark: "standard supplies on cleaning tasks, similar to the drop-down
  // we have for inspections"): the read-only chips became the same collapsible
  // CHECK list inspections has — ticks persist on the turn (stock_check).
  private renderStocking(t: any, job?: any): string {
    // R82 (Jesse: "pull the inventory checklist (with PAR) and not this list
    // of random items"): the per-clean stocking list IS the inventory
    // template (kind:'inventory', Admin → Templates) with each item's PAR
    // expectation for this home; the hardcoded list only covers a stale
    // server. Ticks stay keyed by item TEXT (stock_check), so template edits
    // simply re-key future ticks.
    const invItems: any[] = (job && job.inventory && Array.isArray(job.inventory.items)) ? job.inventory.items : [];
    // Review #14: never silently swap identity namespaces mid-clean. The
    // hardcoded fallback is ONLY for a job payload that predates inventory
    // (stale server: the key is absent). A job WITH the key but no items = a
    // transient template read blip — say so instead of showing the old list.
    if (job && job.inventory !== undefined && !invItems.length) {
      return `
        <div class="hk-job-sec">
          <h4>📦 Stocking check</h4>
          <div style="font-size:12px; color:var(--text-muted);">The inventory list didn't load — collapse and reopen this job to retry (Admin → Templates holds the list).</div>
        </div>`;
    }
    // R87 #1.8: the stocking check follows the item's TIER — basic items every
    // clean, basic+ weekly, enhanced by their cadence; a "show all" link keeps
    // the full list one tap away.
    const dueItems = invItems.filter((x: any) => x.due_now !== false);
    const hiddenByTier = invItems.length - dueItems.length;
    const useItems = (this.stockShowAll || !invItems.length) ? invItems : dueItems;
    const items: string[] = invItems.length ? useItems.map((x: any) => String(x.item)) : HousekeepingPage.STOCK_ITEMS;
    const expectedOf = new Map<string, number>(invItems.filter((x: any) => x.expected != null).map((x: any) => [String(x.item), Number(x.expected)]));
    const minOf = new Map<string, number>(invItems.filter((x: any) => x.expected_min != null).map((x: any) => [String(x.item), Number(x.expected_min)]));
    const tierOf = new Map<string, string>(invItems.map((x: any) => [String(x.item), String(x.tier || 'basic')]));
    const cadOf = new Map<string, number | null>(invItems.map((x: any) => [String(x.item), x.cadence_days != null ? Number(x.cadence_days) : null]));   // R100 B9: configured cadence
    const flagOf = new Map<string, string | null>(invItems.map((x: any) => [String(x.item), x.flag || null]));   // R100 B10: onboarding's missing/low verdict
    const placeOf = new Map<string, string>(invItems.map((x: any) => [String(x.item), this.placementNote(x)]));
    const catOf = new Map<string, string>(invItems.map((x: any) => [String(x.item), String(x.category || '')]));
    const checks = (t.stock_check && typeof t.stock_check === 'object') ? t.stock_check : {};
    const doneN = items.filter(x => checks[x]).length;
    const open = this.stockOpenTurn === t.id;
    return `
      <div class="hk-job-sec">
        <h4 data-hk-stocktoggle="${this.esc(t.id)}" style="cursor:pointer; display:flex; justify-content:space-between; align-items:center;">📦 Stocking check <span style="font-weight:400; font-size:12px; color:var(--text-muted);">${doneN}/${items.length} ${open ? '▾' : '▸'}</span></h4>
        ${open && hiddenByTier > 0 && !this.stockShowAll ? `<div style="font-size:11px; color:var(--text-muted); margin:-4px 0 6px;">${hiddenByTier} item${hiddenByTier === 1 ? ' is' : 's are'} not due this visit (weekly/cadence tiers) · <span data-hk-stockall="${this.esc(t.id)}" style="text-decoration:underline; cursor:pointer;">show all</span></div>` : open && this.stockShowAll && invItems.length ? `<div style="font-size:11px; color:var(--text-muted); margin:-4px 0 6px;">showing every tier · <span data-hk-stockall="${this.esc(t.id)}" style="text-decoration:underline; cursor:pointer;">only what's due</span></div>` : ''}
        ${open ? `
        <div style="font-size:12px; color:var(--text-muted); margin:-4px 0 8px;">Tick each as you confirm it's stocked — running low? Use "Request supplies" above.</div>
        <div style="display:flex; flex-direction:column; gap:4px;">
          ${(() => {
            // Grouped by template category so a long list scans room by room.
            let lastCat = '\u0000';
            return items.map(x => {
              const cat = catOf.get(x) || '';
              const hdr = cat !== lastCat ? (lastCat = cat, cat ? `<div style="font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:6px 0 1px;">${this.esc(cat)}</div>` : '') : '';
              return `${hdr}<label style="display:flex; gap:8px; align-items:center; font-size:13.5px; padding:6px 8px; border:1px solid var(--border-soft,#f1f5f9); border-radius:8px; background:${checks[x] ? 'var(--success-soft,#f0fdf4)' : 'var(--bg-subtle,#f8fafc)'};">
                <input type="checkbox" data-hk-stockitem="${this.esc(t.id)}|${this.esc(x)}" ${checks[x] ? 'checked' : ''}> <span style="flex:1;">${this.esc(x)}${tierOf.get(x) === 'basic_plus' || tierOf.get(x) === 'enhanced' ? ` <span style="font-size:9.5px; color:var(--text-faint,#94a3b8);">${cadOf.get(x) === 7 ? 'weekly' : cadOf.get(x) ? `every ${cadOf.get(x)}d` : 'cadence'}</span>` : ''}${flagOf.get(x) ? ` <span style="font-size:9.5px; font-weight:700; color:${flagOf.get(x) === 'missing' ? 'var(--danger-text,#b91c1c)' : '#92400e'};" title="Flagged on the onboarding inventory checklist">${flagOf.get(x) === 'missing' ? '⚠ missing' : '↓ low'}</span>` : ''}</span>${expectedOf.has(x) || minOf.has(x) ? `<span style="font-size:11px; color:var(--text-muted); flex:none;" title="PAR is what we should have; MIN is the check-in floor — below MIN auto-requests supplies">${expectedOf.has(x) ? `need ${this.esc(expectedOf.get(x))}` : ''}${minOf.has(x) ? ` · min ${this.esc(minOf.get(x))}` : ''}${placeOf.get(x) || ''}</span>` : ''}
              </label>`;
            }).join('');
          })()}
        </div>` : ''}
      </div>`;
  }

  // R79 #3 (Jesse: PAR + counted inventory "on the HK task and the inspection
  // task; once it gets done it updates the system so each party knows"): the
  // monthly inventory count. Renders when the job payload carries the
  // inventory block; badge shows DUE vs when it was last counted. Quantities
  // save per item; "Count complete" stamps the property so the due flag
  // clears on every surface at once.
  // R83 #3: "incl. 2 in Closet · 2 in Under the sinks" — the PAR placement
  // breakdown (Admin → Templates), computed per home server-side.
  // R113 #1: + the per-size split of a per_bed rule ("incl. 6 King · 3 Queen").
  private placementNote(it: any): string {
    const pl = Array.isArray(it && it.placements) ? it.placements.filter((p: any) => p && p.label && p.count != null) : [];
    const sz = parSizesText(it);
    if (!pl.length && !sz) return '';
    const parts = pl.map((p: any) => `${this.esc(p.count)} ${this.esc(p.label)}`);
    if (sz) parts.push(this.esc(sz));
    return ` <span style="opacity:.85;">(incl. ${parts.join(' · ')})</span>`;
  }

  private stockShowAll = false;   // R87 #1.8: tier filter override on the stocking check

  private renderInventoryCount(t: any, job: any): string {
    const inv = job && job.inventory;
    if (!inv || !Array.isArray(inv.items) || !inv.items.length) return '';
    const open = this.invOpenTurn === t.id;
    const daysAgo = inv.counted_at ? Math.floor((Date.now() - Date.parse(inv.counted_at)) / 86400000) : null;
    const badge = inv.due
      ? '<span style="font-size:10.5px; font-weight:700; color:var(--warning-text,#92400e); background:var(--warning-soft,#fef3c7); border-radius:999px; padding:1px 8px;">DUE</span>'
      : `<span style="font-size:10.5px; color:var(--success-text,#166534); background:var(--success-soft,#f0fdf4); border-radius:999px; padding:1px 8px;">✓ ${daysAgo != null ? `${daysAgo}d ago` : 'done'}</span>`;
    const byCat = new Map<string, any[]>();
    for (const it of inv.items) { if (!byCat.has(it.category)) byCat.set(it.category, []); byCat.get(it.category)!.push(it); }
    return `
      <div class="hk-job-sec">
        <h4 data-hk-invtoggle="${this.esc(t.id)}" style="cursor:pointer; display:flex; justify-content:space-between; align-items:center; gap:8px;">📦 Inventory count ${badge} <span style="font-weight:400; font-size:12px; color:var(--text-muted); margin-left:auto;">monthly · ${open ? '▾' : '▸'}</span></h4>
        ${open ? `
        <div style="font-size:12px; color:var(--text-muted); margin:-4px 0 8px;">Count what's actually in the home. "Expected" comes from the property's PAR rules${inv.counted_at ? ` · last completed ${this.esc(String(inv.counted_at).slice(0, 10))}${inv.counted_by ? ` by ${this.esc(inv.counted_by)}` : ''}` : ' · never completed'}.</div>
        ${[...byCat.entries()].map(([cat, items]) => `
          <div style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:8px 0 4px;">${this.esc(cat)}</div>
          ${items.map((it: any) => {
            const short = it.expected != null && it.qty != null && it.qty < it.expected;
            return `<div style="display:flex; gap:8px; align-items:center; padding:5px 8px; border:1px solid var(--border-soft,#f1f5f9); border-radius:8px; margin-bottom:4px; background:${short ? 'var(--warning-soft,#fffbeb)' : 'var(--bg-subtle,#f8fafc)'};">
              <span style="flex:1; font-size:13px;">${this.esc(it.item)}</span>
              ${it.expected != null ? `<span style="font-size:11px; color:${short ? 'var(--warning-text,#92400e)' : 'var(--text-muted)'};" title="PAR expectation for this home">expect ${this.esc(it.expected)}${this.placementNote(it)}</span>` : ''}
              <input type="number" min="0" max="9999" inputmode="numeric" placeholder="qty" value="${it.qty == null ? '' : this.esc(it.qty)}" data-hk-invqty="${this.esc(t.id)}|${this.esc(it.item)}" style="width:64px; padding:6px 8px; border:1px solid var(--border); border-radius:8px; font-size:13px; text-align:right;">
            </div>`;
          }).join('')}`).join('')}
        <div class="hk-job-actions" style="margin-top:8px;">
          <button class="hk-big go" data-hk-invdone="${this.esc(t.id)}">✓ Count complete</button>
        </div>
        <div class="hk-vnote">Completing stamps this property's monthly count for everyone — housekeeping, inspections and the office all see it's done.</div>` : ''}
      </div>`;
  }

  // R112 B4: a photo the background uploader gave up on, on the card itself.
  private renderDroppedNote(t: any): string {
    const d = this.droppedPhotos[t.id];
    if (!d || !d.n) return '';
    return `<div class="hk-dropped"><span>⚠ ${d.n} photo${d.n === 1 ? ' was' : 's were'} rejected (${this.esc(d.note)}) — retake and add ${d.n === 1 ? 'it' : 'them'} again.</span><button type="button" data-hk-dropx="${this.esc(t.id)}" title="Dismiss">✕</button></div>`;
  }

  private renderJob(t: any): string {
    const open = this.dayJobOpen === t.id;
    const job = this.dayJobs[t.id];
    const cls = t.at_risk ? 'risk' : (t.same_day ? 'sameday' : '');
    const field = HousekeepingPage.fieldRole() === 'cleaner';
    const deadline = t.next_check_in
      ? `next check-in <strong>${this.esc(this.fmtDate(t.next_check_in))}</strong> (${this.esc(this.relDay(t.next_check_in))})`
      : 'no next booking';
    const flag = t.same_day ? `<span class="hk-badge sameday">⚡ Same-day</span>` : '';
    // R37: a card from the Recent-cleans section carries its checkout date so
    // it can't be mistaken for one of today's turns. Cleaner field view only —
    // the office "Cleaner day" date browser shows past days on purpose and
    // must not get a warning badge stamped on every card.
    const pastFlag = field && t.check_out && t.check_out < this.localToday()
      ? `<span class="hk-badge sameday">${this.esc(this.fmtDate(t.check_out))}</span>` : '';
    // R51: clock-in/out stamps on every card header (Turno-style) — when the
    // clean was actually started / completed, in PT.
    const sla = t.sla || {};
    const stamps = [
      sla.started_at ? `Started <strong>${this.esc(this.fmtStampPT(sla.started_at))}</strong>` : '',
      sla.cleaned_at ? `Completed <strong>${this.esc(this.fmtStampPT(sla.cleaned_at))}</strong>` : '',
    ].filter(Boolean).join(' · ');
    const nMedia = this.captureUrls(t).photos.length + this.captureUrls(t).videos.length;
    const head = `
      <div class="hk-job-head" data-hk-jobtoggle="${this.esc(t.id)}">
        <div class="hk-job-head-main">
          <div class="hk-job-prop">${this.esc(t.listing_name)}</div>
          <div class="hk-job-meta">${this.statusPill(t.status)}${pastFlag}${flag}<span>${deadline}</span>${nMedia ? `<span>📷 ${nMedia}</span>` : ''}</div>
          ${stamps ? `<div class="hk-job-stamp">🕐 ${stamps}</div>` : ''}
        </div>
        <div class="hk-job-caret">${open ? '▾' : '▸'}</div>
      </div>`;
    if (!open) return `<div class="hk-job ${cls}">${head}${this.companyAssignBar(t)}</div>`;

    // Body: render once the job sheet has loaded (GET /turns/:id/job).
    let body: string;
    if (!job) {
      // R112 defense in depth: an open card without a sheet fetches its own
      // (ensureJob is idempotent while a fetch is in flight) — the spinner
      // can never stick again. A failed fetch becomes a retry button.
      const failed = this.jobFailed.has(t.id);
      if (!failed) void this.ensureJob(t.id);
      body = `<div class="hk-job-body">${this.renderCompletedCard(t)}${failed
        ? `<div class="hk-job-sec" style="text-align:center;"><div class="hk-update" style="margin-bottom:10px;">⚠ Couldn't load this job sheet — check your signal.</div><div class="hk-job-actions"><button class="hk-big" data-hk-jobretry="${this.esc(t.id)}">↻ Try again</button></div></div>`
        : `<div style="padding:14px; text-align:center;">${LoadingSpinner.render()}</div>`}</div>`;
    } else {
      const beds: any[] = job.beds || [];
      const dl = job.deadline
        ? `${this.esc(this.fmtDate(job.deadline))} (${this.esc(this.relDay(job.deadline))})`
        : 'no next booking';
      const started = ['in_progress', 'cleaned', 'verified', 'ready'].includes(t.status);
      const done = ['cleaned', 'verified', 'ready'].includes(t.status);
      const busy = this.checkingIn === t.id;
      // R35 #9 / R112 rec E — ONE primary action per state, at the TOP of the
      // open card: not started → CHECK IN (busy while the POST runs) ·
      // in progress → the ① walkthrough block, then checklist / stocking /
      // timer, with COMPLETE pinned last · done → after-photos + guest-ready.
      const quick = `
          <div class="hk-quick">
            <button class="hk-big outline" data-hk-logissue="${this.esc(t.id)}">＋ Log an issue / damage (photo · voice · route it)</button>
            <button class="hk-big" data-hk-suptoggle="${this.esc(t.id)}">🧴 Request supplies for this home</button>
          </div>`;
      body = `
        <div class="hk-job-body">
          ${this.renderCompletedCard(t)}
          ${this.renderDroppedNote(t)}
          ${!started ? `<div class="hk-job-actions hk-primary-action">
            <button class="hk-big go ${busy ? 'busy' : ''}" data-hk-checkin="${this.esc(t.id)}" ${busy ? 'disabled aria-busy="true"' : ''}>${busy ? '⏳ Checking in…' : '🔑 Check in — I\'m at the door'}</button>
          </div>` : ''}
          ${done ? this.renderVerify(t) : ''}
          ${done ? `
          <div class="hk-job-actions" style="margin:0 0 12px;">
            <button class="hk-big" data-hk-reopen="${this.esc(t.id)}">↩ Reopen to edit</button>
            ${field ? `<button class="hk-big" data-hk-proof="${this.esc(String(t.listing_id))}">📄 Proof of clean</button>` : ''}
          </div>` : ''}
          ${started && !done ? this.renderWalkthrough(t) : ''}
          ${this.renderCreds(job)}
          ${this.renderJobDocs(job, t)}
          ${!started || done ? quick : ''}
          ${!done ? this.renderLoggedIssues(t) : ''}
          ${started && !done ? this.renderChecklist(t, job) : ''}
          ${started && !done ? this.renderStocking(t, job) : ''}
          ${this.renderInventoryCount(t, job)}
          ${started ? this.renderTimer(t) : ''}
          ${started && !done ? this.renderReportedIssues(t) : ''}
          ${this.renderPastIssues(t)}
          ${beds.length ? `<div class="hk-job-sec"><h4>Beds</h4>${beds.map(b => `<div class="hk-job-line"><span class="lab">${this.esc(b.label)}</span> ${this.esc(b.value)}</div>`).join('')}</div>` : ''}
          <div class="hk-job-sec"><h4>Deadline</h4><div class="hk-job-line">Next check-in: ${dl}${job.eci ? ` <span class="hk-badge sameday" title="The next guest arrives EARLY — be done sooner">⏰ Early check-in${job.eci_time ? ` — <strong>${this.esc(job.eci_time)}</strong>` : ''}</span>` : ''}${job.lco ? ` <span class="hk-badge gap" title="The departing guest leaves LATE — start later">🌙 Late check-out${job.lco_time ? ` — <strong>${this.esc(job.lco_time)}</strong>` : ''}</span>` : ''}</div></div>
          ${started && !done ? `
          <div class="hk-job-actions" style="margin-top:4px;">
            <button class="hk-big done" data-hk-checkout="${this.esc(t.id)}">✅ Complete cleaning</button>
          </div>
          <div class="hk-cap-empty" style="margin-top:6px;">Completing the clean is the last step — after it you'll add your after-photos for verification.</div>` : ''}
          ${t.photo_gate_skipped ? (() => {
            // R50: checked out past the photo gate on bad signal — reassure, uploads self-heal.
            const n = (t.photo_gate_skipped.missing || []).length;
            return `<div class="hk-cap-empty" style="margin-top:6px;">📵 Finished without ${n} photo${n === 1 ? '' : 's'} — they upload automatically when signal returns.</div>`;
          })() : ''}
        </div>`;
    }
    return `<div class="hk-job ${cls} open">${head}${this.companyAssignBar(t)}${body}</div>`;
  }

  // R51 (Marina): a cleaning COMPANY's lead reassigns today's jobs across her
  // own crew from the field view. Only when the resolved cleaner carries a
  // company and at least one teammate shares it — roster already loaded
  // (/cleaners fetched once on render), filtered client-side.
  private companyAssignBar(t: any): string {
    if (HousekeepingPage.fieldRole() !== 'cleaner') return '';
    if (!this.dayTurns.some(x => x.id === t.id)) return '';   // today's cards only
    const me = this.cleaners.find(c => c.id === this.dayCleaner);
    const company = String((me && me.company) || '').trim();
    if (!company) return '';
    const crew = this.cleaners.filter(c => String(c.company || '').trim().toLowerCase() === company.toLowerCase());
    if (crew.length < 2) return '';   // nobody else to hand it to
    const assignedIds: string[] = (t.assignees && t.assignees.length) ? t.assignees : (t.assigned_to ? [t.assigned_to] : []);
    const cur = assignedIds[0] || '';
    const opts = crew.map(c => `<option value="${this.esc(c.id)}" ${cur === c.id ? 'selected' : ''}>${this.esc(c.name)}${c.available === false ? ' (off)' : ''}</option>`);
    if (cur && !crew.some(c => c.id === cur)) opts.push(`<option value="${this.esc(cur)}" selected>${this.esc(this.cleanerName(cur))}</option>`);
    return `
      <div style="display:flex; align-items:center; gap:8px; padding:8px 17px; border-top:1px solid var(--border-soft, #f1f5f9); font-size:12.5px; color:var(--text-muted);">
        <span style="font-weight:600; white-space:nowrap;">Cleaner:</span>
        <select data-hk-crewassign="${this.esc(t.id)}" style="flex:1; max-width:240px; padding:7px 9px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; background:var(--card, #fff);">${opts.join('')}</select>
      </div>`;
  }

  // R51: the post-checkout confirmation card — the checkout response is the
  // FULL turn (sla, total_minutes, capture), so times/photo-count read from it.
  private renderCompletedCard(t: any): string {
    const c = this.completedTurn;
    if (!c || c.id !== t.id) return '';
    const sla = c.sla || {};
    const nPhotos = ((c.capture && c.capture.photos) || []).length;
    return `
      <div class="hk-confirm">
        <button class="hk-confirm-x" data-hk-confirm-x title="Dismiss">✕</button>
        <div style="font-size:17px; font-weight:700; color:var(--success-text, #166534);">🎉 Clean submitted!</div>
        <div style="font-size:13.5px; margin-top:6px;">started <strong>${this.esc(this.fmtTimePT(sla.started_at))}</strong> → finished <strong>${this.esc(this.fmtTimePT(sla.cleaned_at))}</strong></div>
        <div style="font-size:13.5px; margin-top:3px;">⏱ total <strong>${this.esc(this.fmtDur(Number(c.total_minutes || 0)))}</strong> · 📷 ${nPhotos} photo${nPhotos === 1 ? '' : 's'} so far</div>
        <div style="font-size:14.5px; font-weight:700; margin-top:10px; color:var(--warning-text, #92400e);">📸 Now add your after photos below ⬇</div>
      </div>`;
  }

  // Render captured photos + videos as a thumb strip that opens the shared
  // lightbox (R112: lazy, placeholder on error, HEIC tagged, videos as tiles).
  private captureThumbs(cap: any): string {
    const { photos, videos } = this.captureUrls({ capture: cap });
    if (!photos.length && !videos.length) return '';
    return `<div class="hk-cap-thumbs">${this.thumbStrip(photos, videos, { wrap: true, caption: true })}</div>`;
  }

  // R112: ONE thumb strip for every photo surface on the page. Each tile
  // carries the url (`attr`, default data-hk-zoom) + its index; the strip
  // carries the FULL url list (data-hk-urls) so the lightbox pages through
  // photos AND videos in order. Images are lazy + async-decoded, a load
  // failure flips the tile to a "📷 unavailable" placeholder (bind() wires
  // the error listener — no inline handlers), a .heic/.heif url wears a
  // HEIC tag (shows on iPhone/Safari only), videos are a ▶ tile (no media
  // preload), and `max` caps the tiles with a "+N" tile that either opens
  // the lightbox at that point or, with `moreAttr`, carries its own hook.
  private thumbStrip(photos: string[], videos: string[], o: { max?: number; size?: '' | 'sm' | 'lg'; wrap?: boolean; total?: number; moreAttr?: string; caption?: boolean; cls?: string; attr?: string } = {}): string {
    const ph = (photos || []).map(p => (typeof p === 'string' ? p : (p && (p as any).url) || '')).filter(Boolean);
    const vd = (videos || []).map(v => String(v || '')).filter(Boolean);
    const urls = [...ph, ...vd];
    if (!urls.length) return '';
    const attr = o.attr || 'data-hk-zoom';
    const size = o.size || '';
    const max = o.max && o.max > 0 ? o.max : urls.length;
    const shown = urls.slice(0, max);
    const total = o.total != null ? Math.max(Number(o.total) || 0, urls.length) : urls.length;
    const hidden = Math.max(0, total - shown.length);
    const isVideo = (u: string) => isVideoUrl(u);   // R116 #5: the one shared rule (shared/lib/mediaKind)
    const tile = (u: string, i: number) => {
      if (isVideo(u)) return `<figure class="hk-th vid ${size} ${o.cls || ''}" ${attr}="${this.esc(u)}" data-hk-idx="${i}" title="Video — tap to play">▶<small>video</small></figure>`;
      const heic = /\.hei[cf](\?|#|$)/i.test(u);
      return `<figure class="hk-th ${size} ${o.cls || ''} ${heic ? 'heic' : ''}" ${attr}="${this.esc(u)}" data-hk-idx="${i}" ${heic ? 'title="HEIC — shows on iPhone / Safari only"' : ''}><img src="${this.esc(u)}" alt="" loading="lazy" decoding="async"></figure>`;
    };
    const more = hidden
      ? (o.moreAttr
        ? `<figure class="hk-th more ${size}" ${o.moreAttr}>+${hidden}</figure>`
        : `<figure class="hk-th more ${size}" ${attr}="${this.esc(urls[Math.min(max, urls.length - 1)])}" data-hk-idx="${Math.min(max, urls.length - 1)}" title="Show the rest">+${hidden}</figure>`)
      : '';
    const cap = o.caption
      ? `<div class="hk-strip-cap">${ph.length ? `📷 ${ph.length} photo${ph.length === 1 ? '' : 's'}` : ''}${vd.length ? `${ph.length ? ' · ' : ''}🎥 ${vd.length} video${vd.length === 1 ? '' : 's'}` : ''} · tap one to page through</div>`
      : '';
    return `${cap}<div class="hk-strip ${o.wrap ? 'wrap' : ''}" data-hk-strip data-hk-urls="${this.esc(JSON.stringify(urls))}">${shown.map(tile).join('')}${more}</div>`;
  }

  // Minutes → "Hh Mm" (or "Mm").
  private fmtDur(mins: number): string {
    const m = Math.max(0, Math.round(mins));
    const h = Math.floor(m / 60), r = m % 60;
    return h > 0 ? `${h}h ${r}m` : `${r}m`;
  }

  // Clean timer (advisory). Auto-started on check-in / stopped on check-out;
  // this panel lets the cleaner pause/resume and log time manually.
  // R70 (Mark): the cleaning time clock — expected minutes from the effort
  // model, right beside the tracked time so the cleaner knows the budget.
  private estStrip(t: any): string {
    const est = (this.dayJobs[t.id] || {}).est;
    if (!est || !est.minutes) return '';
    const base = Number(t.total_minutes || 0);
    const running = !!t.timer_started_at;
    const elapsed = base + (running ? Math.max(0, (Date.now() - Date.parse(t.timer_started_at)) / 60000) : 0);
    const over = elapsed > est.minutes;
    return `<div data-hk-eststrip data-est="${est.minutes}" data-base="${base}" ${running ? `data-started="${this.esc(t.timer_started_at)}"` : ''} style="font-size:12.5px; margin-top:4px; color:${over ? 'var(--danger-text,#b91c1c)' : 'var(--text-muted)'};">⏱ Expected for this home: <strong>${this.fmtDur(est.minutes)}</strong> <span style="color:var(--text-faint);">(${est.beds}bd/${est.baths}ba${est.sqft ? ` · ${est.sqft}sqft${est.sqft_estimated ? ' est.' : ''}` : ''})</span><span data-hk-over style="${over ? '' : 'display:none;'}"> · over the expected time</span></div>`;
  }

  // R70 (Mark: "no cleaner or inspector 6 days in a row — the system should
  // flag it"): warning band on the office board. Weeks read Sunday-first;
  // the rule is consecutive days — a rest day resets the count.
  private streakBandHtml(): string {
    if (this.streakFlags === null) {
      this.streakFlags = [];
      this.get('/staff/streaks').then((j: any) => {
        if (j && Array.isArray(j.flags) && j.flags.length) { this.streakFlags = j.flags; this.draw(); }
      }).catch(() => { /* advisory only */ });
      return '';
    }
    if (!this.streakFlags.length) return '';
    return `<div style="margin:6px 0 10px; padding:8px 12px; background:var(--warning-soft,#fffbeb); border-left:3px solid var(--warning,#f59e0b); border-radius:6px; font-size:12.5px;">
      ⚠ <strong>Consecutive-day flags</strong> — nobody should work 6+ days in a row:
      ${this.streakFlags.map((f: any) => `<div style="margin-top:2px;">${this.esc(f.name)} <span style="color:var(--text-muted);">(${this.esc(f.kind)})</span> — <strong>${f.len} days</strong> ${this.esc(f.start)} → ${this.esc(f.end)}</div>`).join('')}
    </div>`;
  }

  private renderTimer(t: any): string {
    const done = ['cleaned', 'verified', 'ready'].includes(t.status);
    const base = Number(t.total_minutes || 0);
    // Once the clean is done the timer is closed — show the total read-only so a
    // lingering day-view card can't restart it and inflate the tracked time.
    if (done) {
      return `
        <div class="hk-job-sec">
          <h4>Time on this clean</h4>
          <div class="hk-timer"><span class="hk-timer-val">${this.fmtDur(base)}</span><span class="hk-timer-idle">total tracked</span></div>
          ${this.estStrip(t)}
        </div>`;
    }
    const running = !!t.timer_started_at;
    const elapsed = base + (running ? Math.max(0, (Date.now() - Date.parse(t.timer_started_at)) / 60000) : 0);
    return `
      <div class="hk-job-sec">
        <h4>Time on this clean</h4>
        <div class="hk-timer">
          <span class="hk-timer-val" data-hk-elapsed ${running ? `data-started="${this.esc(t.timer_started_at)}"` : ''} data-base="${base}">${this.fmtDur(elapsed)}</span>
          ${running ? '<span class="hk-timer-run">● tracking</span>' : (base > 0 ? '<span class="hk-timer-idle">paused</span>' : '')}
        </div>
        ${this.estStrip(t)}
        <div class="hk-job-actions" style="margin-top:10px;">
          ${running
            ? `<button class="hk-big" data-hk-timer="${this.esc(t.id)}|pause">⏸ Pause</button>`
            : `<button class="hk-big go" data-hk-timer="${this.esc(t.id)}|start">${base > 0 ? '▶ Resume' : '▶ Start timer'}</button>`}
          <button class="hk-big" data-hk-timermanual="${this.esc(t.id)}">✎ Log time</button>
        </div>
        <div class="hk-vnote">Tracked for records — your pay is the flat per-clean rate; this just measures how long the clean took.</div>
      </div>`;
  }

  // R35 #10: the standard list is a curated set of commonly-missed basics
  // (plus pool/hot-tub items when the PDS says the home has one). Property
  // quirks and reference areas render as their own SECTIONS in renderChecklist;
  // this returns the same flat strings (checklist_done identity) plus section
  // metadata via smartSections().
  private standardItems(job: any): string[] {
    const pc = (job && job.property_checklist) || {};
    // R51: office can disable the standard list per property (checklist
    // override store) — quirks, references and flagged items are untouched.
    if (pc.disable_standard) return [];
    const flags = pc.amenity_flags || {};
    // R78 (Jesse: "we gotta be able to edit the standard list"): the base list
    // is office-editable (hk_standard_checklist, served on the job sheet); the
    // hardcoded copy is only the fallback for a stale server. Pool / hot-tub
    // stay amenity-driven add-ons on top of whatever the office saved.
    const base: string[] = (Array.isArray(job?.standard_checklist) && job.standard_checklist.length)
      ? job.standard_checklist
      : [
        'All surfaces wiped down — counters, tables, shelves',
        'Kitchen — dishes & cutlery clean and spot-free, appliances + fridge wiped, no leftovers',
        'Couch cushions lifted — crumbs & items cleared underneath',
        'Fresh linens — no spots, stains or hair',
        'Showers & grout clear of mold / mildew',
        'Mirrors & glass streak-free',
        'Floors — vacuum & mop throughout, edges and under furniture',
        'Trash & recycling out, fresh liners',
        'Restock — coffee, paper, soap, essentials',
        'Staging — remotes, welcome setup, lights, thermostat',
        'Final walk-through — damage or left-behind items',
      ];
    return [
      ...base,
      ...(flags.pool ? ['Pool — skim surface, check water level & equipment'] : []),
      ...(flags.hot_tub ? ['Hot tub — water clear, cover on, controls off'] : []),
    ];
  }
  // R78: quirks the server classified as kind:'info' (access notes, where
  // things are) are NOT checklist tasks — they render in the "Good to know"
  // block instead. Only task quirks mint checklist items.
  private taskQuirks(pc: any): any[] { return ((pc && pc.quirks) || []).filter((q: any) => q.kind !== 'info'); }
  private infoQuirks(pc: any): any[] { return ((pc && pc.quirks) || []).filter((q: any) => q.kind === 'info'); }
  private smartChecklist(job: any): string[] {
    const beds: any[] = (job && job.beds) || [];
    const bedItems = beds.map(b => `Beds & fresh linens — ${b.label}`);
    const pc = (job && job.property_checklist) || {};
    const quirkItems = this.taskQuirks(pc).map((q: any) => `⚑ ${q.label}${q.note ? ` — ${q.note}` : ''}`.slice(0, 160));
    // R82 #4b: explicit property checks (PDS 'checks' section) — always tasks.
    const checkItems = (pc.checks || []).map((c: any) => `☑ ${c.label}${c.note ? ` — ${c.note}` : ''}`.slice(0, 160));
    const ownerReqItems = (pc.owner_requirements || []).map((o: any) => `👑 ${o.label}${o.note ? ` — ${o.note}` : ''}`.slice(0, 160));   // R87 #1.7
    const refItems = (pc.references || []).map((r: any) => `📸 Match reference: ${r.label}${r.caption ? ` (${r.caption})` : ''}`.slice(0, 160));
    return [...checkItems, ...ownerReqItems, ...quirkItems, ...refItems, ...bedItems, ...this.standardItems(job)];
  }
  // R20: logged issues routed to THIS clean — each must be photographed before
  // checkout (the server enforces it too). Verify each with a picture.
  private renderLoggedIssues(t: any): string {
    const items: any[] = Array.isArray(t.logged_issues) ? t.logged_issues : [];
    if (!items.length) return '';
    const rows = items.map(it => {
      const ok = !!it.photo;
      // R38 review: scope to THIS issue's own in-flight verify photo — a
      // turn-wide count showed '…' on every unphotographed issue whenever any
      // unrelated photo uploaded. Input stays enabled: re-picking is always
      // safe (server dedupes), and blocking the REQUIRED photo was the bug.
      const up = pendingCountWhere(`/turns/${t.id}/photo`, 'logged_issue_id', String(it.id)) > 0 && !ok;
      const key = `${this.esc(t.id)}|${this.esc(it.id)}`;
      return `<div style="display:flex; gap:10px; align-items:center; justify-content:space-between; padding:9px 11px; border:1px solid ${ok ? 'var(--success-border)' : 'var(--danger-border)'}; background:${ok ? 'var(--success-soft)' : 'var(--danger-soft)'}; border-radius:var(--radius-sm); margin-bottom:7px;">
        <div style="min-width:0;">
          <div style="font-size:14px; font-weight:600;">⚑ ${this.esc(it.title)}</div>
          ${it.note ? `<div style="font-size:12.5px; color:var(--text-muted); margin-top:2px;">${this.esc(it.note)}</div>` : ''}
          <div style="font-size:11px; color:${ok ? 'var(--success-text)' : 'var(--danger-text)'}; margin-top:3px; font-weight:600;">${ok ? '✓ photographed' : '📸 photo required before checkout'}</div>
        </div>
        ${ok
          ? this.thumbStrip([String(it.photo)], [], { size: 'sm' })
          : `<label style="flex:none; padding:9px 13px; border-radius:var(--radius-btn); background:var(--accent); color:#fff; font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap;">${up ? '📤 uploading…' : '📸 Photo'}<input type="file" accept="image/*,video/*" capture="environment" style="display:none;" data-hk-lissphoto="${key}"></label>`}
      </div>`;
    }).join('');
    return `<div class="hk-job-sec"><h4>Logged issues to verify</h4><div style="font-size:12px; color:var(--text-muted); margin:-4px 0 8px;">Flagged from a prior clean or inspection — photograph each before you check out.</div>${rows}</div>`;
  }

  // R35 #10: one row — checkbox + reference thumbs + the cleaner's own photos
  // + an add-photo button (reference/quirk/flagged items). Item STRING is the
  // checklist_done / checklist_photos identity — never alter it here.
  private checklistRow(t: any, it: string, done: Set<string>, refPhotos: Map<string, string[]>, mine: Record<string, string[]>, photoable: boolean): string {
    const on = done.has(it);
    const ref = refPhotos.get(it) || [];
    const own: string[] = Array.isArray(mine[it]) ? mine[it] : [];
    return `<div>
      <label class="hk-check ${on ? 'on' : ''}"><input type="checkbox" data-hk-check="${this.esc(t.id)}" data-hk-checkitem="${this.esc(it)}" ${on ? 'checked' : ''}><span>${this.esc(it)}</span></label>
      ${ref.length ? `<div class="hk-check-thumbs">${this.thumbStrip(ref, [], { size: 'sm' })}</div>` : ''}
      ${own.length ? `<div class="hk-check-thumbs">${this.thumbStrip(own, [], { size: 'sm', cls: 'mine' })}</div>` : ''}
      ${photoable ? `<label class="hk-item-cam">${own.length ? '＋📷 add another' : '＋📷 photo it'}<input type="file" accept="image/*" multiple data-hk-itemphoto="${this.esc(t.id)}" data-hk-item="${this.esc(it)}"></label>` : ''}
    </div>`;
  }

  private renderChecklist(t: any, job: any): string {
    // Manager-flagged items (issues board → "next clean") ride in capture.checklist
    // and go FIRST — they're the reason someone routed this here.
    const flagged: string[] = (t.capture && Array.isArray(t.capture.checklist) ? t.capture.checklist : []).filter((x: any) => typeof x === 'string');
    const pc = (job && job.property_checklist) || {};
    const done = new Set<string>(Array.isArray(t.checklist_done) ? t.checklist_done : []);
    const mine: Record<string, string[]> = (t.checklist_photos && typeof t.checklist_photos === 'object') ? t.checklist_photos : {};
    // Reference items keep their R31 photo strip AND group under their AREA.
    const refPhotos = new Map<string, string[]>();
    const refRows: Array<{ area: string; item: string }> = [];
    for (const r of (pc.references || [])) {
      const label = `📸 Match reference: ${r.label}${r.caption ? ` (${r.caption})` : ''}`.slice(0, 160);
      const ph: string[] = Array.isArray(r.photos) ? r.photos : [];
      if (ph.length) refPhotos.set(label, [...(refPhotos.get(label) || []), ...ph]);
      const ix = String(r.label).indexOf(' — ');
      refRows.push({ area: ix > 0 ? String(r.label).slice(0, ix) : 'General', item: label });
    }
    const quirkItems = this.taskQuirks(pc).map((q: any) => `⚑ ${q.label}${q.note ? ` — ${q.note}` : ''}`.slice(0, 160));
    // R83 #1: check items can carry photos now — possibly GENERIC examples
    // (how a cabinet should look), so they ride the same thumb strip as
    // references, keyed by the item's display string.
    const checkItems = (pc.checks || []).map((c: any) => {
      const label = `☑ ${c.label}${c.note ? ` — ${c.note}` : ''}`.slice(0, 160);
      const ph: string[] = Array.isArray(c.photos) ? c.photos : [];
      if (ph.length) refPhotos.set(label, [...(refPhotos.get(label) || []), ...ph]);
      return label;
    });
    // R87 #1.7: owner requirements — the owner's own rules, always tasks.
    const ownerReqItems = (pc.owner_requirements || []).map((o: any) => {
      const label = `👑 ${o.label}${o.note ? ` — ${o.note}` : ''}`.slice(0, 160);
      const ph: string[] = Array.isArray(o.photos) ? o.photos : [];
      if (ph.length) refPhotos.set(label, [...(refPhotos.get(label) || []), ...ph]);
      return label;
    });
    // R78: info quirks (access / where-things-are notes) get a no-checkbox
    // "Good to know" block — they were confusing cleaners as checklist items.
    // R83 #1: the dedicated Good to Know PDS section renders here too (first),
    // photos included.
    const infoRow = (q: any) =>
      `<div style="padding:8px 11px; background:var(--bg-subtle); border-left:3px solid var(--accent-fill); border-radius:var(--radius-sm); margin-bottom:6px; font-size:13px;">${q.about === 'owner' ? '<span title="About the owner">👤 Owner: </span>' : ''}<strong>${this.esc(q.label)}</strong>${q.note ? ` — ${this.esc(q.note)}` : ''}${(Array.isArray(q.photos) && q.photos.length) ? `<div class="hk-check-thumbs" style="margin:5px 0 0;">${this.thumbStrip(q.photos, [], { size: 'sm' })}</div>` : ''}</div>`;
    // R87 #1.7: once someone curates arrival lines, the 📌 block shows THOSE;
    // an uncurated property keeps showing everything (safe default).
    const gtkAll = (pc.good_to_know || []) as any[];
    const gtkArrival = gtkAll.filter((g: any) => g.arrival);
    const infoHtml = [...(gtkArrival.length ? gtkArrival : gtkAll), ...this.infoQuirks(pc)].map(infoRow).join('');
    const beds: any[] = (job && job.beds) || [];
    const bedItems = beds.map(b => `Beds & fresh linens — ${b.label}`);
    const standards = [...bedItems, ...this.standardItems(job)];
    const allItems = [...new Set([...flagged, ...this.smartChecklist(job)])];
    const n = allItems.filter(it => done.has(it)).length;
    const sec = (title: string, rows: string) => rows ? `<div class="hk-check-sec">${title}</div>${rows}` : '';
    const rowsOf = (items: string[], photoable: boolean) => [...new Set(items)].map(it => this.checklistRow(t, it, done, refPhotos, mine, photoable)).join('');
    // Reference areas: one sub-heading per room/area
    const areas = new Map<string, string[]>();
    for (const rr of refRows) { if (!areas.has(rr.area)) areas.set(rr.area, []); areas.get(rr.area)!.push(rr.item); }
    const refHtml = [...areas.entries()].map(([area, items]) => sec(`📍 ${this.esc(area)}`, rowsOf(items, true))).join('');
    return `
      <div class="hk-job-sec">
        <h4>Cleaning checklist · ${n}/${allItems.length}</h4>
        <div class="hk-checklist">
          ${infoHtml ? `<div class="hk-check-sec">📌 Good to know — this property <span style="font-weight:400; text-transform:none; letter-spacing:0;">(info, not tasks)</span></div>${infoHtml}` : ''}
          ${sec('⚑ Flagged for this clean', rowsOf(flagged.filter(f => !quirkItems.includes(f) && !checkItems.includes(f) && !ownerReqItems.includes(f)), true))}
          ${sec('☑ Property checklist — this property', rowsOf(checkItems, true))}
          ${sec('👑 Owner requirements — this property', rowsOf(ownerReqItems, true))}
          ${sec('Quirks & notes — this property', rowsOf(quirkItems, true))}
          ${refHtml ? `<div class="hk-check-sec" style="margin-top:2px;">Reference areas — match the photos</div>${refHtml}` : ''}
          ${sec('Standards', rowsOf(standards, false))}
        </div>
      </div>`;
  }

  // R31 #4: reference docs on the ticket — the in-app property guide (print
  // view, codes stripped), the company Cleaning & Inspection Standards, and the
  // external cleaning guidebook when the PDS has one recorded. R112: chips.
  private renderJobDocs(job: any, t: any): string {
    const link = String((job.docs && job.docs.guidebook_link) || '').trim();
    const safeLink = /^https?:\/\//i.test(link) ? link : '';
    // R52 (Mark): the Standards link is our INTERNAL SOP — cleaners get the
    // actual per-property Cleaning guide instead (openExternal: PWA-safe tab).
    const sopOrGuide = HousekeepingPage.fieldRole() === 'cleaner'
      ? `<button type="button" class="chip hk-doclink" data-hk-cleanguide="${this.esc(String(t.listing_id))}">🧽 Cleaning guide</button>`
      : `<button type="button" class="chip hk-doclink" data-hk-standards>🧭 Cleaning &amp; inspection standards</button>`;
    return `
      <div class="hk-doclinks">
        <button type="button" class="chip hk-doclink" data-hk-guide="${this.esc(String(t.listing_id))}">📘 Property guide</button>
        ${sopOrGuide}
        ${safeLink ? `<a class="chip hk-doclink" href="${this.esc(safeLink)}" target="_blank" rel="noopener">📖 Cleaning guidebook</a>` : ''}
      </div>`;
  }

  // Read-only list of what the cleaner has reported this clean (routing to a WO /
  // restock is the manager's job, on the board / issues board).
  private renderReportedIssues(t: any): string {
    const issues: any[] = (t.issues || []).filter((i: any) => i && i.source === 'cleaner');
    if (!issues.length) return '';
    const rows = issues.map(iss => {
      const media: string[] = Array.isArray(iss.photos) ? iss.photos : [];
      const thumbs = media.length ? this.captureThumbs({ photos: media.filter(u => !/\/video\//.test(u)), videos: media.filter(u => /\/video\//.test(u)) }) : '';
      return `
      <div class="hk-issue">
        <div class="hk-issue-tags">
          <span class="hk-sev ${this.esc(iss.severity || 'med')}">${this.esc(iss.severity || 'med')}</span>
          <span class="hk-itype">${this.esc(iss.type || 'cleaning')}</span>
          <span class="hk-acted" style="margin:0;">✓ Reported</span>
        </div>
        <div class="hk-issue-note">${this.esc(iss.note || '')}</div>
        ${thumbs}
      </div>`;
    }).join('');
    return `<div class="hk-job-sec"><h4>Reported this clean</h4>${rows}</div>`;
  }

  // ── Phase 3: capture after-photos + clean-verification report ────
  // Shown once the clean is done (status 'cleaned' onward). The cleaner adds
  // after-photos, runs the AI verification, then a manager flips guest-ready.
  private renderVerify(t: any): string {
    // Only relevant after the physical clean is complete.
    if (!['cleaned', 'verified', 'ready'].includes(t.status)) return '';
    const cap = t.capture || { photos: [] };
    const photos: string[] = cap.photos || [];
    const v = t.verification || { grade: null, ready: false, gaps: [], issues: [], source: null };
    const issues: any[] = t.issues || [];
    const busyA = this.analyzing === t.id;

    const thumbs = (photos.length || (cap.videos || []).length)
      ? this.captureThumbs(cap)
      : `<div class="hk-cap-empty">No after-photos yet — add a few of the finished rooms.</div>`;

    // R52 (Mark: "checklist of photos by labeling room like Bedroom 1, 2,
    // kitchen, living room"): expected after-shots built from the job sheet —
    // soft guidance only, a ✓ per shot as the photo COUNT grows (no gating,
    // photos aren't room-tagged so count is the honest proxy).
    const job = this.dayJobs[t.id];
    const nBeds = ((job && job.beds) || []).length;
    const shots = [
      ...Array.from({ length: Math.max(1, nBeds) }, (_, i) => `Bedroom ${i + 1}`),
      'Kitchen', 'Living room', 'Bathroom(s)', 'Entry',
    ];
    const shotList = `
      <div style="display:flex; flex-wrap:wrap; gap:5px; margin:6px 0 2px;">
        ${shots.map((s, i) => {
          const got = photos.length >= i + 1;
          return `<span style="font-size:12px; padding:4px 8px; border-radius:8px; border:1px solid ${got ? '#bbf7d0' : 'var(--border-soft,#f1f5f9)'}; background:${got ? '#f0fdf4' : 'var(--bg-subtle,#f8fafc)'}; color:${got ? '#15803d' : 'var(--text-muted,#64748b)'};">${got ? '✓ ' : ''}${this.esc(s)}</span>`;
        }).join('')}
      </div>
      <div class="hk-vnote" style="margin-top:0;">Aim for one shot per room — a guide, not a gate.</div>`;

    const capture = `
      <div class="hk-job-sec" data-hk-verify-sec="${this.esc(t.id)}">
        <h4>After-photos &amp; verification</h4>
        ${thumbs}
        ${shotList}
        ${(() => { const n = pendingCountFor(`/turns/${t.id}/photo`); return n ? `<div class="hk-vnote" style="color:var(--info,#1d4ed8);">📤 ${n} photo${n === 1 ? '' : 's'} uploading in the background.</div>` : ''; })()}
        ${!photos.length ? `<div class="hk-photo-prompt" data-hk-promptadd="${this.esc(t.id)}" role="button" tabindex="0" style="cursor:pointer;">📸 Add your after photos — required for proof of clean · <u>tap to add</u></div>` : ''}
        <label class="hk-cap-add ${!photos.length ? 'primary' : ''}">
          ＋ Add after photo / video
          <!-- R38: no capture= — cleaners shoot everything in the camera app
               first, then bulk-pick from the library; forcing the camera made
               'multiple' dead and uploads one-at-a-time. -->
          <input type="file" accept="image/*,video/*" multiple data-hk-photo="${this.esc(t.id)}">
        </label>
        <button class="hk-analyze" data-hk-analyze="${this.esc(t.id)}" ${busyA || !photos.length ? 'disabled' : ''}>
          ${busyA ? 'Verifying…' : (v.source ? 'Re-run verification' : 'Analyze / verify clean')}
        </button>
        ${!photos.length ? (pendingCountFor(`/turns/${t.id}/photo`) > 0
          ? '<div class="hk-vnote">Verification unlocks when your photos finish uploading.</div>'
          : '<div class="hk-vnote">Add at least one after-photo to verify the clean.</div>') : ''}
      </div>`;

    // Guest-ready flip: enabled at 'verified' (or manager override with confirm
    // when only 'cleaned'). Disabled once already 'ready'. AI-unblock: rendered
    // whether or not a verification exists — the AI check is an optional extra,
    // and an out-of-credits / down Anthropic must never strand a finished clean.
    // data-hk-noai picks the override copy (no AI check vs. AI ran and didn't pass).
    const isReady = t.status === 'ready';
    // data-hk-noai: '1' the AI check was tried and didn't run · '2' nobody ran it
    const noAi = v.source ? '0' : (this.aiCheckFailed.has(t.id) ? '1' : '2');
    const flip = isReady
      ? `<button class="hk-big done" disabled>✓ Guest-ready — released</button>`
      : `<button class="hk-big ${t.status === 'verified' ? 'go' : ''}" data-hk-makeready="${this.esc(t.id)}" data-hk-verified="${t.status === 'verified' ? '1' : '0'}" data-hk-noai="${noAi}">Mark guest-ready</button>`;

    // No verification yet → the capture step + the flip (no report card). The
    // after-photos stay the proof-of-clean gate without the AI: the field
    // view needs at least one before it offers the release (the office can
    // always release — it owns the override).
    if (!v.source) {
      const role = HousekeepingPage.fieldRole();
      const office = !role || ['admin', 'director', 'manager', 'remote_ops'].includes(role);
      const canRelease = isReady || office || photos.length > 0;
      return `
      ${capture}
      <div class="hk-job-sec">
        ${canRelease ? flip : '<div class="hk-vnote">Add your after-photos — then you can mark this clean guest-ready (the AI check is optional).</div>'}
        ${!isReady && canRelease ? `<div class="hk-vnote">AI verification is optional${this.aiCheckFailed.has(t.id) ? ' — it didn\'t run this time (out of credits / unavailable)' : ''}. You can still mark this clean guest-ready.</div>` : ''}
      </div>`;
    }

    // Report card: grade ring + ready badge + gaps + issues + guest-ready flip.
    const g = v.grade;
    const ringPct = g == null ? 0 : Math.max(0, Math.min(100, Number(g)));
    const ringColor = this.gradeHex(g);
    const C = 2 * Math.PI * 26;
    const ring = `
      <svg class="hk-ring" width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r="26" fill="none" stroke="var(--border-soft, #f1f5f9)" stroke-width="8"></circle>
        <circle cx="36" cy="36" r="26" fill="none" stroke="${ringColor}" stroke-width="8" stroke-linecap="round"
          stroke-dasharray="${C}" stroke-dashoffset="${C * (1 - ringPct / 100)}" transform="rotate(-90 36 36)"></circle>
        <text x="36" y="41" text-anchor="middle" font-size="20" font-weight="600" fill="${ringColor}">${g == null ? '—' : g}</text>
      </svg>`;
    const readyBadge = v.ready
      ? `<span class="hk-rb ready">✓ Guest-ready</span>`
      : `<span class="hk-rb notready">⚠ Not ready</span>`;

    const gaps: string[] = v.gaps || [];
    const gapsHtml = gaps.length ? `
      <div class="hk-callout">
        <div class="h">Coverage gaps — not shown in a photo or incomplete</div>
        <ul>${gaps.map(x => `<li>${this.esc(x)}</li>`).join('')}</ul>
      </div>` : '';

    const issuesHtml = issues.length
      ? issues.map(iss => this.renderTurnIssue(t, iss)).join('')
      : '<div class="hk-vnote">No issues flagged by the AI.</div>';

    return `
      ${capture}
      <div class="hk-job-sec">
        <h4>Verification${v.source === 'ai' ? ' (AI)' : ''}</h4>
        <div class="hk-grade-row">
          ${ring}
          <div>
            <div class="hk-grade-num" style="color:${ringColor};">${g == null ? '—' : g}<span style="font-size:13px; color:var(--text-muted); font-weight:600;"> / 100</span></div>
            ${readyBadge}
          </div>
        </div>
        ${gapsHtml}
      </div>
      <div class="hk-job-sec">
        <h4>Issues found</h4>
        ${issuesHtml}
      </div>
      <div class="hk-job-sec">
        ${flip}
        ${t.status === 'cleaned' && v.source ? '<div class="hk-vnote warn">AI did not pass this clean as guest-ready. Marking ready is a manager override.</div>' : ''}
      </div>`;
  }

  // One verification-found issue: severity + type tags, note, and either the
  // action buttons or the actioned result (mirrors Inspections renderIssue).
  private renderTurnIssue(t: any, iss: any): string {
    const act = iss.action;
    let actedHtml: string;
    const dis = this.turnDisposition(iss);
    if (dis.state === 'dismissed') {
      actedHtml = this.renderTurnDismissed(t, iss, dis);
    } else if (!act && dis.state === 'actioned' && dis.via === 'board') {
      // review R120 #14 (round 3): the office resolved it (or made the work
      // order) on the Issues board, so routing it again here would double it
      actedHtml = '<div class="hk-acted">✓ Handled by the office on the Issues board</div>';
    } else if (!act && dis.board_unknown) {
      // the board could not be read: it may hold a dismissal or a resolve, so
      // nothing is offered that the board might contradict (fail closed)
      actedHtml = `<div class="hk-vnote" data-hk-board-unknown>${HousekeepingPage.BOARD_UNKNOWN_NOTE}</div>`;
    } else if (act) {
      const map: Record<string, string> = {
        wo: '✓ Work order created', restock: '✓ Restock requested',
        owner_note: '✓ Owner note saved', dismissed: '✓ Dismissed',
        cleaner: `✓ Sent to the cleaner — next clean${act.strike ? ' · ⚠ strike candidate' : ''}`,   // R104 #6
      };
      actedHtml = `<div class="hk-acted ${act.kind === 'dismissed' ? 'dismissed' : ''}">${map[act.kind] || '✓ Done'}</div>`;
    } else {
      const sa = iss.suggested_action || 'none';
      const id = this.esc(t.id), iid = this.esc(iss.id);
      const btn = (action: string, label: string, primary: boolean) =>
        `<button class="hk-act-btn ${primary ? 'primary' : ''}" data-hk-issueact="${id}|${iid}|${action}">${label}</button>`;
      // R104 #6: a staged shortfall on an after-clean check is a cleaning miss — the cleaner corrects it
      const miss = iss.cause === 'cleaning_miss';
      actedHtml = `
        ${miss ? '<div style="font-size:11px; font-weight:700; color:#92400e; margin-bottom:4px;">🧹 cleaning miss — staged item short after the clean</div>' : ''}
        <div class="hk-act-row">
          ${(miss || String(iss.type || '') === 'supply') ? btn('cleaner', '→ Cleaner (next clean)', sa === 'cleaner' || miss) : ''}
          ${btn('wo', 'Create WO', sa === 'wo')}
          ${btn('restock', 'Restock', sa === 'restock' && !miss)}
          ${btn('owner_note', 'Owner note', sa === 'owner_note')}
          ${btn('dismiss', 'Dismiss', false)}
        </div>`;
    }
    return `
      <div class="hk-issue">
        <div class="hk-issue-tags">
          <span class="hk-sev ${this.esc(iss.severity || 'low')}">${this.esc(iss.severity || 'low')}</span>
          <span class="hk-itype">${this.esc(iss.type || 'cleaning')}</span>
        </div>
        <div class="hk-issue-note">${this.esc(iss.note || '')}</div>
        ${actedHtml}
      </div>`;
  }

  // R120 fix round 2 (#14 client, D11 "office any; the person their own"):
  // a dismissed turn finding names who / why / when, and its Undo and reason
  // chips show ONLY when the server would allow them (POST /turns/:id/
  // issue-action restore / dismiss+reason_code): the office on any native
  // dismissal, anyone else on their own (disposition.by_id = their session
  // id). A pre-R120 stamp (no by_id) is the office's; a board dismissal is
  // restored on the Issues board, never here. Unknown role or id → the
  // controls stay hidden (fail closed), so a field login never gets a
  // control that only answers with a refusal.
  // The disposition: the server's iss.disposition when an issue-action answer
  // attached it (it merges the board's dismissals), else from the action.
  // Review R120 #14 (round 3): every turn answer Housekeeping reads now
  // carries it (GET /turns/:id/job, /cleaners/:id, the issue-action answers);
  // board_unknown = the server could not read the board, so the board's side
  // of this finding is unknown and the card offers no Undo / routing on it.
  private turnDisposition(iss: any): TurnDisp {
    const d = iss && iss.disposition;
    if (d && d.state) return { state: String(d.state), by: d.by || null, by_id: d.by_id ? String(d.by_id) : null, at: d.at || null, reason: d.reason || null, reason_code: d.reason_code || null, via: String(d.via || 'legacy'), board_unknown: !!d.board_unknown };
    const a = iss && iss.action;
    if (a && a.kind === 'dismissed') return { state: 'dismissed', by: a.by || null, by_id: a.by_id ? String(a.by_id) : null, at: a.at || null, reason: a.reason || null, reason_code: a.reason_code || null, via: String(a.via || 'legacy'), board_unknown: false };
    return { state: a && a.kind ? 'actioned' : 'open', by: null, by_id: null, at: null, reason: null, reason_code: null, via: 'housekeeping', board_unknown: false };
  }
  // Review R120 #14 (round 4): the NEWEST answer always wins (it replaces
  // round 3's carry, which fell back to the cached job sheet whenever a row's
  // finding had no disposition: after one raw answer, a board dismissal the
  // office had since restored came back on the second).
  //   noteBoardState  what an answer said, per finding: the newest wins
  //                   (`at` = the answer's receipt order)
  //   withBoardState  each finding as the card shows it: the answer's own
  //                   disposition when it is the newest word on it; else the
  //                   newest word (the whole of it when that is newer than this
  //                   answer; else its board part, a dismissal / resolve or
  //                   board_unknown, over this answer's own record); a finding
  //                   no answer has described yet → board_unknown (no Undo, no
  //                   routing) until one does
  // A disposition composed here is never noted back as if an answer said it.
  private static readonly CARRIED = new WeakSet<object>();
  private static carried(d: any): any { HousekeepingPage.CARRIED.add(d); return d; }
  // the record's own part (lib/findingState's disposition without the board)
  static nativeDisposition(a: any): any {
    if (a && a.kind === 'dismissed') {
      return { state: 'dismissed', by: a.by || null, by_id: a.by_id ? String(a.by_id) : null, at: a.at || null, reason: a.reason || null, reason_code: a.reason_code || null,
        via: ['inspection', 'housekeeping', 'auto'].includes(a.via) ? a.via : 'legacy' };
    }
    if (a && a.kind) return { state: 'actioned', by: a.by || null, by_id: a.by_id ? String(a.by_id) : null, at: a.at || null, reason: a.note || null, reason_code: null, via: ['inspection', 'housekeeping', 'auto'].includes(a.via) ? a.via : 'legacy' };
    return { state: 'open', by: null, by_id: null, at: null, reason: null, reason_code: null, via: null };
  }
  private static answered(i: any): any {
    const d = i && i.disposition;
    return d && typeof d === 'object' && d.state && !HousekeepingPage.CARRIED.has(d) ? d : null;
  }
  static noteBoardState(seen: Record<string, { at: number; d: any }>, issues: any, at: number): void {
    if (!Array.isArray(issues)) return;
    for (const i of issues) {
      if (!i || typeof i !== 'object' || i.id == null) continue;
      const d = HousekeepingPage.answered(i);
      if (!d) continue;
      const k = String(i.id);
      if (!seen[k] || seen[k].at <= at) seen[k] = { at, d };
    }
  }
  static withBoardState(seen: Record<string, { at: number; d: any }>, issues: any, at: number): any {
    if (!Array.isArray(issues)) return issues;
    const C = HousekeepingPage.carried, native = HousekeepingPage.nativeDisposition;
    return issues.map((i: any) => {
      if (!i || typeof i !== 'object' || i.id == null) return i;
      const own = HousekeepingPage.answered(i);
      const e = seen[String(i.id)];
      if (!e) return own ? i : { ...i, disposition: C({ ...native(i.action), board_unknown: true }) };
      if (own && e.d === own) return i;
      if (e.at > at || e.d.via === 'board') return { ...i, disposition: C({ ...e.d }) };
      return { ...i, disposition: C(e.d.board_unknown ? { ...native(i.action), board_unknown: true } : native(i.action)) };
    });
  }
  private seenFor(id: string): Record<string, { at: number; d: any }> {
    return this.boardSeen[id] || (this.boardSeen[id] = {});
  }
  // the cached job sheet shows the newest word too (a day reload heals the
  // row from it), its own stamp kept
  private refreshSheet(id: string): void {
    const job = this.dayJobs[id];
    if (!job || !job.turn || !Array.isArray(job.turn.issues)) return;
    job.turn.issues = HousekeepingPage.withBoardState(this.seenFor(id), job.turn.issues, this.stampOf(job.turn));
  }
  // a job sheet stored without merging its turn (the checkout handler): its
  // dispositions count unless its turn is older than the row's
  private noteJobSheet(id: string, j: any): void {
    const t = j && j.turn;
    if (t && Array.isArray(t.issues) && !this.isStaleTurn(t)) HousekeepingPage.noteBoardState(this.seenFor(id), t.issues, this.stampOf(t));
    this.refreshSheet(id);
  }
  private static readonly BOARD_UNKNOWN_NOTE = "Couldn't check the Issues board just now, so actions on this finding are paused. Reopen the clean to try again.";
  private static readonly OFFICE_ROLES = ['admin', 'director', 'manager', 'remote_ops'];
  private static readonly DISMISS_REASONS: Array<[string, string]> = [
    ['present', 'Present'], ['not_issue', 'Not an issue'], ['duplicate', 'Duplicate'], ['other', 'Other'],
  ];
  private static sessionUserId(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.id || '').trim(); } catch { return ''; } }
  private static mayUndoTurnDismissal(d: { state: string; by_id: string | null; via: string; board_unknown?: boolean }): boolean {
    if (d.state !== 'dismissed' || d.via === 'board' || d.board_unknown) return false;   // the board unread: it may hold the office's dismissal (the server answers 503)
    if (HousekeepingPage.OFFICE_ROLES.includes(HousekeepingPage.fieldRole())) return true;
    if (d.via === 'auto') return false;   // the system's (a duplicate) on a clean: the office's call (D11; the server answers 403)
    const me = HousekeepingPage.sessionUserId();
    return !!(me && d.by_id && d.by_id === me);
  }
  // The dismissed line's words (plain text): "Dismissed · <who> · <why> ·
  // <when>"; the system's duplicate reads "Dismissed automatically · Duplicate
  // of …" (review R120 #1: it is not the stocking check's, and it said a
  // plain "✓ Dismissed").
  private turnDismissedMeta(d: TurnDisp): string {
    const byCode: Record<string, string> = Object.fromEntries(HousekeepingPage.DISMISS_REASONS);
    const reason = String(d.reason || (d.reason_code ? byCode[d.reason_code] || '' : '')).trim();
    let when = '';
    if (d.at) {
      const dt = new Date(d.at);
      if (!isNaN(dt.getTime())) when = this.fmtDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
    }
    if (d.via === 'auto' && d.reason_code === 'duplicate') return ['Dismissed automatically', reason || 'Duplicate', when].filter(Boolean).join(' · ');
    // who: the first name (a shared login reads "Company — Person"), never an email
    const who = d.via === 'auto' ? 'the stocking check'
      : d.via === 'board' ? 'the office'
        : (d.via === 'legacy' || !d.by || d.by === 'system') ? 'not recorded'
          : ((String(d.by).split(' — ').pop() || String(d.by)).trim().split(/\s+/)[0] || 'not recorded');
    return ['Dismissed', who, reason, when].filter(Boolean).join(' · ');
  }
  private renderTurnDismissed(t: any, iss: any, d: TurnDisp): string {
    const id = this.esc(t.id), iid = this.esc(iss.id);
    const meta = this.esc(this.turnDismissedMeta(d));
    const allowed = HousekeepingPage.mayUndoTurnDismissal(d);
    // reason chips are a person's to change on a person's dismissal; the
    // system's (auto) carries its own reason (as on the inspection card)
    const chips = allowed && d.via !== 'auto' ? `
          <span class="hk-dis-reasons" role="group" aria-label="Why was it dismissed?">${HousekeepingPage.DISMISS_REASONS.map(([code, label]) =>
            `<button type="button" class="hk-act-btn hk-dis-reason ${d.reason_code === code ? 'on' : ''}" aria-pressed="${d.reason_code === code ? 'true' : 'false'}" data-hk-dis-reason="${id}|${iid}|${code}">${this.esc(label)}</button>`).join('')}</span>` : '';
    const acts = allowed ? `
        <div class="hk-act-row hk-dis-acts">${chips}
          <button type="button" class="hk-act-btn hk-dis-undo" data-hk-dis-undo="${id}|${iid}" title="Put this finding back on the open list">Undo</button>
        </div>` : '';
    const office = HousekeepingPage.OFFICE_ROLES.includes(HousekeepingPage.fieldRole());
    const boardNote = d.via === 'board'
      ? `<div class="hk-vnote">Dismissed on the Issues board. ${office ? 'Restore it there.' : 'Ask the office to restore it.'}</div>`
      : (d.board_unknown ? `<div class="hk-vnote" data-hk-board-unknown>${HousekeepingPage.BOARD_UNKNOWN_NOTE}</div>` : '');
    return `<div class="hk-dis" data-hk-dis-row="${iid}"><div class="hk-acted dismissed">✓ ${meta}</div>${acts}${boardNote}</div>`;
  }
  // A reason chip or Undo on a dismissed turn finding → the turn route; a
  // refusal is post()'s alert and nothing changes (the row re-enables).
  private async postTurnFindingAction(id: string, issueId: string, body: any, btn: HTMLButtonElement): Promise<void> {
    const row = btn.closest('.hk-dis-acts');
    row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = true));
    const landed = (j: any) => { if (j && j.turn) this.replaceDayTurn(j.turn); this.draw(); };
    const j = await this.post(`/turns/${id}/issue-action`, { issue_id: issueId, ...body }, landed);   // board unreadable: Try again (round 6)
    if (!j || !j.ok) { row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = false)); return; }
    landed(j);
  }

  // ── events ───────────────────────────────────────────────────
  private bind(): void {
    bindFieldViewSwitcher(this.container);   // R64: field role self-switch chips
    const all = (sel: string) => this.container.querySelectorAll<HTMLElement>(sel);

    // tab switch — refresh whatever the new tab needs before drawing.
    all('[data-hk-tab]').forEach(el => el.addEventListener('click', async () => {
      this.tab = el.dataset.hkTab as any;
      if (this.tab === 'cleaners') { await this.reloadCleaners(); }
      else if (this.tab === 'scheduler') {
        this.schedLoading = true; this.draw();
        await Promise.all([this.reloadCleaners(), this.reloadScheduler()]);
        this.draw();
        return;
      }
      else if (this.tab === 'day') { await this.reloadCleaners(); await this.reloadDay(); }
      else if (this.tab === 'history') {
        this.draw();
        await this.reloadHistory();
        this.draw();
        return;
      }
      else if (this.tab === 'performance') {
        this.perfLoading = true; this.draw();   // show spinner immediately
        await this.reloadPerformance();
        this.draw();
        return;
      }
      this.draw();
    }));

    // horizon toggle → regenerate + reload for the new window
    // R45: Today = a client-side filter mode, so switching into it never
    // refetches or regenerates — the horizon fetch already covers today.
    all('[data-hk-today]').forEach(el => el.addEventListener('click', () => {
      if (this.todayMode && !this.selectedDate) return;
      this.todayMode = true;
      this.selectedDate = null;
      this.boardView = 'list';   // Today is the audit LIST; the grid is a horizon view
      this.calAssignTurn = null;   // review fix: a leftover popup id must not reopen on the next calendar visit
      this.expandedTurn = null;
      this.draw();
    }));
    all('[data-hk-days]').forEach(el => el.addEventListener('click', async () => {
      const d = Number(el.dataset.hkDays);
      if (!d) return;
      const wasFocused = this.todayMode || !!this.selectedDate;
      this.selectedDate = null;
      if (d === this.days) {
        // Same horizon already loaded — leaving a day focus is draw-only.
        if (wasFocused) { this.todayMode = false; this.expandedTurn = null; this.draw(); }
        return;
      }
      this.todayMode = false;
      this.days = d;
      this.expandedTurn = null;
      this.generating = true; this.draw();
      await this.generate();
      await this.reloadBoard();
      this.generating = false;
      this.draw();
    }));
    // R46: jump to a specific day — same audit list as Today, keyed to the
    // picked date. A date past the fetched horizon widens days (≤60, the
    // server ceiling) and regenerates turns so the day is actually populated.
    // Chrome fires 'change' per SEGMENT edit (see data-hk-movedate), so:
    // checkValidity rejects out-of-range commits (year 0002 mid-typing —
    // min/max never BLOCK a value), and a debounce lets rapid segment edits
    // settle before the redraw destroys the input's focus.
    all('[data-hk-date]').forEach(el => el.addEventListener('change', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      if (this.hkDateTimer) window.clearTimeout(this.hkDateTimer);
      this.hkDateTimer = window.setTimeout(async () => {
        this.hkDateTimer = null;
        if (input.value && !input.checkValidity()) return;
        const v = input.value || null;
        if (v === this.selectedDate) return;
        this.selectedDate = v;
        this.expandedTurn = null;
        this.calAssignTurn = null;   // review fix: leaving the calendar closes its popup for good
        if (!v) { this.todayMode = true; this.boardView = 'list'; this.draw(); return; }
        this.todayMode = false;
        this.boardView = 'list';
        const asOf = (this.board && this.board.as_of) || this.localToday();
        const off = Math.round((Date.parse(v + 'T00:00:00Z') - Date.parse(asOf + 'T00:00:00Z')) / 86400000);
        if (off > this.days && off <= 60) {
          this.days = off;
          this.generating = true; this.draw();
          await this.generate();
          await this.reloadBoard();
          this.generating = false;
        }
        this.draw();
      }, 600);
    }));

    // refresh turns (re-generate from the calendar, idempotent)
    all('[data-hk-refresh]').forEach(el => el.addEventListener('click', async () => {
      if (this.generating) return;
      this.generating = true; this.draw();
      await this.generate();
      await this.reloadBoard();
      this.generating = false;
      this.draw();
    }));

    // board view toggle + filters (R6 #4)
    all('[data-hk-view]').forEach(el => el.addEventListener('click', () => {
      const v = (el as HTMLElement).dataset.hkView as 'list' | 'calendar';
      if (!v || v === this.boardView) return;
      // R45/R46: the calendar is a horizon view — leaving any day focus keeps
      // the grid honest (a 1-day-filtered calendar reads as "no cleans all week").
      if (v === 'calendar') { this.todayMode = false; this.selectedDate = null; }
      if (v === 'list') this.calAssignTurn = null;   // review fix: no stale popup on the next calendar visit
      this.boardView = v;
      this.draw();
    }));
    all('[data-hk-cov]').forEach(el => el.addEventListener('click', () => {
      const v = (el as HTMLElement).dataset.hkCov as 'in_house' | 'vendor' | 'all';
      if (v && v !== this.boardFilters.coverage) { this.boardFilters.coverage = v; this.draw(); }
    }));
    all('[data-hk-fstatus]').forEach(el => el.addEventListener('change', (e) => {
      this.boardFilters.status = (e.currentTarget as HTMLSelectElement).value; this.draw();
    }));
    all('[data-hk-fcleaner]').forEach(el => el.addEventListener('change', (e) => {
      this.boardFilters.cleaner = (e.currentTarget as HTMLSelectElement).value; this.draw();
    }));
    all('[data-hk-fprop]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      this.boardFilters.prop = input.value;
      // Redraw without stealing focus mid-typing: debounce via rAF and restore.
      requestAnimationFrame(() => {
        const pos = input.selectionStart || input.value.length;
        this.draw();
        const again = this.container.querySelector<HTMLInputElement>('[data-hk-fprop]');
        if (again) { again.focus(); again.setSelectionRange(pos, pos); }
      });
    }));
    // calendar chip → quick-assign popup (R48); "Open in list ↗" keeps the old
    // jump-to-card path.
    all('[data-hk-calchip]').forEach(el => el.addEventListener('click', () => {
      this.calAssignTurn = (el as HTMLElement).dataset.hkCalchip!;
      this.draw();
    }));
    // Own dismiss attrs — data-hk-closemodal/-modalbg are hard-bound to resetStore.
    const closeCalAssign = () => { this.calAssignTurn = null; this.draw(); };
    all('[data-hk-calassign-x]').forEach(el => el.addEventListener('click', closeCalAssign));
    all('[data-hk-calassign-bg]').forEach(el => el.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) closeCalAssign();
    }));
    all('[data-hk-calassign-open]').forEach(el => el.addEventListener('click', () => {
      this.expandedTurn = (el as HTMLElement).dataset.hkCalassignOpen!;
      this.boardView = 'list';
      this.calAssignTurn = null;
      this.draw();
      this.container.querySelector(`[data-hk-toggle="${this.expandedTurn}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }));
    // popup assign — mirrors [data-hk-assign] below; popup stays open so the
    // chips recolor behind it (it self-closes only if the turn disappears).
    all('[data-hk-calassign-sel]').forEach(el => el.addEventListener('change', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.hkCalassignSel!;
      const slots = Array.from(this.container.querySelectorAll<HTMLSelectElement>(`[data-hk-calassign-sel="${id}"]`))
        .sort((a, b) => Number(a.dataset.hkCalassignSlot) - Number(b.dataset.hkCalassignSlot))
        .map(s => s.value).filter(Boolean);
      const ids = [...new Set(slots)];
      const j = await this.postConfirm(`/turns/${id}/assign`, { cleaner_ids: ids });
      if (j) await Promise.all([this.reloadBoard(), this.reloadCleaners()]);
      this.draw();   // on cancel/failure the redraw snaps the selects back
    }));

    // ── scheduler tab (R6 #6) ──
    // Every row input syncs into schedRows STATE immediately, so any redraw
    // (weekday toggle, facts toggle, horizon change) re-renders the user's
    // unsaved edits instead of silently reverting to last-saved values.
    all('[data-sc-shift]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const row = this.schedRows.find(s => String(s.cleaner_id) === input.dataset.scShift);
      const h = Number(input.value);
      if (row && h > 0) row.shift_minutes = Math.round(h * 60);
    }));
    all('[data-sc-max]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const row = this.schedRows.find(s => String(s.cleaner_id) === input.dataset.scMax);
      const n = Number(input.value);
      if (row && n > 0) row.max_cleans = Math.round(n);
    }));
    // R107: same-day cap; blank = null (use the team default), 0 = none at all.
    all('[data-sc-maxsd]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const row = this.schedRows.find(s => String(s.cleaner_id) === input.dataset.scMaxsd);
      if (!row) return;
      if (input.validity.badInput) return;   // a half-typed "1e" reads as '' — never let a typo clear the limit
      const v = input.value.trim();
      row.max_same_day = v === '' ? null : (Number(v) >= 0 && Number.isFinite(Number(v)) ? Math.round(Number(v)) : row.max_same_day);
    }));
    all('[data-sc-sddefault]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      if (input.validity.badInput) return;   // keep the last valid draft — a typo must never redraw as "no limit"
      this.sdDefaultDraft = input.value;
    }));
    all('[data-sc-sdsave]').forEach(el => el.addEventListener('click', async () => {
      const input = this.container.querySelector('[data-sc-sddefault]') as HTMLInputElement | null;
      const v = input ? input.value.trim() : '';
      if ((input && input.validity.badInput) || (v !== '' && !(Number(v) >= 0))) { alert('Enter a number (0 or more), or leave it blank for no limit.'); return; }
      const j = await this.post('/housekeeping/rules', { max_same_day_default: v === '' ? null : Math.round(Number(v)) });
      if (j && j.rules) {
        this.schedRules = j.rules;
        this.sdDefaultDraft = null;
        this.schedNote = `✓ Same-day turn limit ${j.rules.max_same_day_default == null ? 'removed' : `set to ${j.rules.max_same_day_default} per cleaner per day`}.`;
        this.schedProposal = null;   // no reload: the rows' own (possibly unsaved) values don't change with the default
      }
      this.draw();
    }));
    // R81: weekly cap syncs like the others; blank = null (no cap).
    all('[data-sc-maxwk]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const row = this.schedRows.find(s => String(s.cleaner_id) === input.dataset.scMaxwk);
      if (!row) return;
      const v = input.value.trim();
      row.max_cleans_week = v === '' ? null : (Number(v) > 0 ? Math.round(Number(v)) : row.max_cleans_week);
    }));
    // R81 #1.1: coverage controls. Search redraws with caret preserved.
    all('[data-sc-covsearch]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      this.covSearch = input.value;
      const pos = input.selectionStart ?? input.value.length;
      requestAnimationFrame(() => {
        this.draw();
        const el2 = this.container.querySelector('[data-sc-covsearch]') as HTMLInputElement | null;
        if (el2) { el2.focus(); el2.setSelectionRange(pos, pos); }
      });
    }));
    all('[data-sc-covsort]').forEach(el => el.addEventListener('change', (e) => {
      this.covUnassignedFirst = (e.currentTarget as HTMLInputElement).checked;
      this.draw();
    }));
    // R81 (Jesse: "search for a property when I go to assign it"): live filter
    // over the checkbox list — DOM-only show/hide, no redraw mid-typing.
    all('[data-sc-propfilter]').forEach(el => el.addEventListener('input', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const q2 = input.value.trim().toLowerCase();
      const list = input.parentElement?.querySelector('.hk-prop-list');
      list?.querySelectorAll<HTMLElement>('.hk-prop-opt').forEach(opt => {
        opt.style.display = !q2 || (opt.textContent || '').toLowerCase().includes(q2) ? '' : 'none';
      });
    }));
    all('[data-sc-prop]').forEach(el => el.addEventListener('change', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const row = this.schedRows.find(s => String(s.cleaner_id) === input.dataset.scProp);
      if (!row) return;
      const set = new Set((row.listing_ids || []).map(String));
      if (input.checked) set.add(input.value); else set.delete(input.value);
      row.listing_ids = [...set];
    }));
    // weekday toggle — flip state, redraw (safe now that inputs are state-backed).
    all('[data-sc-day]').forEach(el => el.addEventListener('click', () => {
      const [cid, d] = (el as HTMLElement).dataset.scDay!.split('|');
      const row = this.schedRows.find(s => String(s.cleaner_id) === cid);
      if (row) { row.days[d] = !row.days[d]; this.draw(); }
    }));
    all('[data-sc-save]').forEach(el => el.addEventListener('click', async () => {
      const cid = (el as HTMLElement).dataset.scSave!;
      const row = this.schedRows.find(s => String(s.cleaner_id) === cid);
      if (!row) return;
      const j = await this.post('/housekeeping/schedules', {
        cleaner_id: cid, days: row.days,
        shift_minutes: Number(row.shift_minutes) > 0 ? row.shift_minutes : undefined,
        max_cleans: Number(row.max_cleans) > 0 ? row.max_cleans : undefined,
        // R81: blank weekly cap = explicit null (no cap); a number sets it.
        max_cleans_week: Number(row.max_cleans_week) > 0 ? row.max_cleans_week : null,
        // R107: blank = null → the team same-day default applies.
        max_same_day: row.max_same_day == null || row.max_same_day === '' ? null : row.max_same_day,
        listing_ids: (row.listing_ids || []).map(String),
      });
      if (j) { this.schedNote = `✓ Saved ${row.name}'s schedule.`; this.schedProposal = null; await this.reloadScheduler(); this.draw(); }
    }));
    all('[data-sc-factstoggle]').forEach(el => el.addEventListener('click', () => { this.factsOpen = !this.factsOpen; this.draw(); }));
    all('[data-sc-syncfacts]').forEach(el => el.addEventListener('click', async () => {
      this.syncingFacts = true; this.draw();
      const j = await this.post('/housekeeping/facts/sync', {});
      this.syncingFacts = false;
      if (j) { this.schedNote = `✓ Synced sqft from Hostaway — ${j.synced} updated, ${j.missing_sqft} listings have no sqft in Hostaway.`; this.schedProposal = null; }
      await this.reloadScheduler(); this.draw();
    }));
    all('[data-sc-sqft]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const j = await this.post('/housekeeping/facts', { listing_id: input.dataset.scSqft, sqft: input.value ? Number(input.value) : null });
      if (j) { this.schedProposal = null; await this.reloadScheduler(); this.draw(); }
    }));
    all('[data-sc-horizon]').forEach(el => el.addEventListener('click', () => {
      const d = Number((el as HTMLElement).dataset.scHorizon);
      if (d && d !== this.schedHorizon) { this.schedHorizon = d; this.schedProposal = null; this.draw(); }
    }));
    all('[data-sc-propose]').forEach(el => el.addEventListener('click', async () => {
      this.proposing = true; this.schedNote = ''; this.draw();
      const j = await this.get(`/housekeeping/schedule/propose?days=${this.schedHorizon}`);
      this.proposing = false;
      this.schedProposal = j || null;
      this.draw();
    }));
    all('[data-sc-apply]').forEach(el => el.addEventListener('click', async () => {
      if (!this.schedProposal) return;
      const assignments = (this.schedProposal.days || []).flatMap((d: any) =>
        (d.turns || []).filter((t: any) => !t.locked && t.proposed_cleaner_id)
          .map((t: any) => ({ turn_id: t.turn_id, cleaner_id: t.proposed_cleaner_id })));
      if (!assignments.length) { this.schedNote = 'Nothing to apply — every clean is already assigned or has no eligible cleaner.'; this.draw(); return; }
      this.applying = true; this.draw();
      const j = await this.post('/housekeeping/schedule/apply', { assignments });
      // R107: apply re-checks every cap at write time (the proposal can be
      // stale); anything that would now go over is held back and asked about.
      let forced = 0, raced = 0;
      const over: any[] = (j && Array.isArray(j.over_cap)) ? j.over_cap : [];
      if (over.length) {
        const lines = over.slice(0, 8).map((o: any) => `• ${o.listing_name || 'Clean'} (${o.date}): ${(o.warnings || []).map((w: any) => w.message).join('; ')}`).join('\n');
        if (confirm(`${over.length} assignment${over.length === 1 ? '' : 's'} would go over a cleaning limit (the schedule changed since this proposal):\n${lines}${over.length > 8 ? `\n…and ${over.length - 8} more` : ''}\n\nAssign ${over.length === 1 ? 'it' : 'them'} anyway?`)) {
          const j2 = await this.post('/housekeeping/schedule/apply', { assignments: over.map((o: any) => ({ turn_id: o.turn_id, cleaner_id: o.cleaner_id })), override: true });
          if (j2) { forced = Number(j2.applied) || 0; raced = Number(j2.skipped) || 0; }   // raced = assigned by someone else meanwhile
        }
      }
      this.applying = false;
      if (j) {
        const applied = (Number(j.applied) || 0) + forced;
        const held = over.length - forced - raced;
        const skipped = (Number(j.skipped) || 0) + raced;
        this.schedNote = `✓ Applied ${applied} assignment${applied === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped (assigned in the meantime)` : ''}${held > 0 ? ` · ${held} held back (over a cleaning limit, still unassigned)` : ''}. See them on the Board, or open Cleaner day and pick the date.`;
        this.schedProposal = null;
        await Promise.all([this.reloadBoard(), this.reloadCleaners()]);
      }
      this.draw();
    }));

    // expand / collapse a turn card (expanding lazily fetches that property's
    // checklist override for the R51 "skip standard checklist" checkbox)
    all('[data-hk-toggle]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.hkToggle!;
      this.expandedTurn = this.expandedTurn === id ? null : id;
      this.draw();
      if (this.expandedTurn === id) void this.loadChecklistOverride(id);
    }));

    // R51: per-property standard-checklist override checkbox (office card).
    all('[data-hk-skipstd]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const lid = input.dataset.hkSkipstd!;
      const want = input.checked;
      const j = await this.post('/property-checklist-override', { listing_id: lid, disable_standard: want, by: HousekeepingPage.actorName() || 'office' });
      if (j) this.checklistOverrides.set(String(lid), (j && j.override) || { disable_standard: want });
      else input.checked = !want;   // failed save — snap the box back
      this.draw();
    }));

    // status change → POST /turns/:id
    all('[data-hk-status]').forEach(el => el.addEventListener('change', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.hkStatus!;
      const status = (e.currentTarget as HTMLSelectElement).value;
      const j = await this.post(`/turns/${id}`, { status });
      if (j) { await this.reloadBoard(); this.draw(); }
    }));

    // type change → POST /turns/:id
    all('[data-hk-type]').forEach(el => el.addEventListener('change', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.hkType!;
      const type = (e.currentTarget as HTMLSelectElement).value;
      const j = await this.post(`/turns/${id}`, { type });
      if (j) { await this.reloadBoard(); this.draw(); }
    }));

    // board: assign cleaner → POST /turns/:id/assign ('' = unassign → null).
    all('[data-hk-assign]').forEach(el => el.addEventListener('change', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.hkAssign!;
      // Collect BOTH cleaner slots for this turn (R18 #6: two cleaners on a big
      // property). De-dupe; slot order gives the primary.
      const slots = Array.from(this.container.querySelectorAll<HTMLSelectElement>(`[data-hk-assign="${id}"]`))
        .sort((a, b) => Number(a.dataset.hkAssignSlot) - Number(b.dataset.hkAssignSlot))
        .map(s => s.value).filter(Boolean);
      const ids = [...new Set(slots)];
      const j = await this.postConfirm(`/turns/${id}/assign`, { cleaner_ids: ids });
      if (j) await Promise.all([this.reloadBoard(), this.reloadCleaners()]);
      this.draw();   // on cancel/failure the redraw snaps the selects back
    }));

    // owner proof-of-clean — AUTHED /api HTML → must go through openApiDoc
    // (token-carrying fetch); a plain navigation is a 401 page in production.
    // stopPropagation keeps the turn row from collapsing when the button is clicked.
    all('[data-hk-clean-report]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = el.dataset.hkCleanReport!;
      void openApiDoc(`${API}/housekeeping/clean-report/${encodeURIComponent(id)}?days=90`, 'Proof of clean');
    }));
    // R63 (CEO): one-tap PDF — same report, auto-opens the print dialog so
    // "Save as PDF" is the only click left.
    all('[data-hk-clean-pdf]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = el.dataset.hkCleanPdf!;
      void openApiDoc(`${API}/housekeeping/clean-report/${encodeURIComponent(id)}?days=90&print=1`, 'Proof of clean PDF');
    }));
    // R63 / R112: card photo strip → the shared lightbox (pages through the
    // whole strip). stopPropagation keeps the turn row from toggling.
    all('[data-hk-photfull]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openStrip(el as HTMLElement, 'data-hk-photfull');
    }));

    // ── cleaners tab ──
    all('[data-cf-new]').forEach(el => el.addEventListener('click', () => { this.editCleaner = ''; this.cfDup = null; this.draw(); }));
    all('[data-cf-edit]').forEach(el => el.addEventListener('click', () => { this.editCleaner = el.dataset.cfEdit!; this.cfDup = null; this.draw(); }));
    all('[data-cf-cancel]').forEach(el => el.addEventListener('click', () => { this.editCleaner = null; this.cfDup = null; this.draw(); }));
    // R120: "<name> already has this email on the roster" → edit THAT row instead of making a twin.
    all('[data-cf-dupedit]').forEach(el => el.addEventListener('click', () => { this.editCleaner = el.dataset.cfDupedit!; this.cfDup = null; this.draw(); }));
    all('[data-cf-save]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.cfSave || '';
      const field = (k: string) => (this.container.querySelector(`[data-cf="${k}"]`) as HTMLInputElement | null);
      const name = (field('name')?.value || '').trim();
      if (!name) { alert('Name is required'); return; }
      const body: any = {
        name,
        kind: field('kind')?.value || 'w2',
        company: field('company')?.value || '',
        phone: field('phone')?.value || '',
        email: field('email')?.value || '',
        zones: field('zones')?.value || '',
        skills: field('skills')?.value || '',
        available: !!field('available')?.checked,
        rate_per_turn: field('rate_per_turn')?.value || null,
        hourly_rate: field('hourly_rate')?.value || null,
        rating: field('rating')?.value || null,
      };
      if (id) body.id = id;
      // R120: a 409 DUP_EMAIL is not an error alert — it names the existing
      // cleaner and offers to edit that row (the form keeps what was typed).
      const r = await this.send('POST', '/cleaners', body);
      if (r.status === 409 && r.j && r.j.code === 'DUP_EMAIL') {
        const nm = String(r.j.existing_name || 'Another cleaner');
        this.cfDup = { msg: `${nm} already has this email on the roster.`, id: String(r.j.existing_id || ''), name: nm };
        this.draw();
        return;
      }
      // R120 review fix (#9): the login with this email already works as another
      // row (its link) → offer that row, never a twin it would not point at.
      if (r.status === 409 && r.j && r.j.code === 'DUP_LOGIN') {
        const nm = String(r.j.existing_name || 'another cleaner');
        this.cfDup = { msg: `${r.j.login_first ? `${r.j.login_first}'s` : 'This email’s'} login already works as ${nm}. Add the email to that cleaner instead.`, id: String(r.j.existing_id || ''), name: nm };
        this.draw();
        return;
      }
      if (!r.ok) { alert((r.j && (r.j.message || r.j.error)) || `request failed (${r.status})`); return; }
      this.editCleaner = null; this.cfDup = null; await this.reloadCleaners(); this.draw();
    }));
    all('[data-cf-del]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.cfDel!;
      const c = this.cleaners.find(x => x.id === id);
      if (!confirm(`Remove ${c ? c.name : 'this cleaner'} from the roster?`)) return;
      // R120: removing a row a login is connected to (or one with upcoming
      // cleans) answers 409 with the reason — confirm "Remove anyway" → ?force=1.
      let r = await this.send('DELETE', `/cleaners/${encodeURIComponent(id)}`);
      if (r.status === 409 && r.j && (r.j.code === 'LINKED_LOGIN' || r.j.code === 'UPCOMING_TURNS')) {
        if (!confirm(`${r.j.error || 'This cleaner is still in use.'}\n\nOK = Remove anyway · Cancel = keep them`)) return;
        r = await this.send('DELETE', `/cleaners/${encodeURIComponent(id)}?force=1`);
      }
      if (!r.ok) { alert((r.j && (r.j.message || r.j.error)) || `request failed (${r.status})`); return; }
      if (this.editCleaner === id) this.editCleaner = null;
      await this.reloadCleaners(); this.draw();
    }));

    // ── cleaner-day view ──
    all('[data-hk-daypick]').forEach(el => el.addEventListener('change', async (e) => {
      this.dayCleaner = (e.currentTarget as HTMLSelectElement).value;
      this.dayJobOpen = null; this.dayJobs = {};
      this.draw();              // show the spinner immediately
      await this.reloadDay();
      this.draw();
    }));
    // R39: past-cleans date range. Typed dates can bypass the input's max=
    // attr — clamp to TODAY (R63: today's completed cleans belong here now;
    // the server keeps today's unstarted turns out on its own).
    const histClamp = (v: string) => { const maxD = this.localToday(); return v && v > maxD ? maxD : v; };
    const histReload = async () => { this.draw(); await this.reloadHistory(); this.draw(); };
    all('[data-hk-histfrom]').forEach(el => el.addEventListener('change', (e) => {
      this.histFrom = histClamp((e.currentTarget as HTMLInputElement).value); void histReload();
    }));
    all('[data-hk-histto]').forEach(el => el.addEventListener('change', (e) => {
      this.histTo = histClamp((e.currentTarget as HTMLInputElement).value); void histReload();
    }));
    all('[data-hk-histclear]').forEach(el => el.addEventListener('click', () => {
      this.histFrom = ''; this.histTo = ''; void histReload();
    }));
    // R112: "Dates mean" is a chip pair (the hook name is unchanged; a
    // legacy <select> with the same hook still works through 'change').
    all('[data-hk-histmode]').forEach(el => {
      const pick = (v: string) => {
        const mode = v === 'completed' ? 'completed' : 'assigned';
        if (mode === this.histMode) return;
        this.histMode = mode;
        void histReload();
      };
      if (el.tagName === 'SELECT') el.addEventListener('change', (e) => pick((e.currentTarget as HTMLSelectElement).value));
      else el.addEventListener('click', () => pick((el as HTMLElement).dataset.hkHistmode || ''));
    });
    // R78: expand a past clean → lazy-load its full job payload once (read-only).
    const histLoad = async (id: string) => {
      this.histDetail.set(id, 'loading'); this.draw();
      try {
        const r = await fetch(`${API}/turns/${encodeURIComponent(id)}/job`);
        this.histDetail.set(id, r.ok ? await r.json() : 'error');
      } catch { this.histDetail.set(id, 'error'); }
      // Draw only if the row is still open and the user isn't mid-edit in the
      // date filters (review: the resolve-time draw wiped a half-typed date).
      const ae = document.activeElement as HTMLElement | null;
      const dateFocus = !!(ae && ae.dataset && ('hkHistfrom' in ae.dataset || 'hkHistto' in ae.dataset));
      if (this.histOpen.has(id) && !dateFocus) this.draw();
    };
    all('[data-hk-histopen]').forEach(el => el.addEventListener('click', () => {
      const id = String((el as HTMLElement).dataset.hkHistopen || '');
      if (!id) return;
      if (this.histOpen.has(id)) { this.histOpen.delete(id); this.draw(); return; }
      this.histOpen.add(id);
      const cached = this.histDetail.get(id);
      if (!cached || cached === 'error') { void histLoad(id); return; }
      this.draw();
    }));
    all('[data-hk-histretry]').forEach(el => el.addEventListener('click', () => {
      const id = String((el as HTMLElement).dataset.hkHistretry || '');
      if (id) void histLoad(id);
    }));

    // office date browse (R8 #5) — see any day's route, e.g. tomorrow after applying a schedule
    all('[data-hk-daydate]').forEach(el => el.addEventListener('change', async (e) => {
      this.dayDate = (e.currentTarget as HTMLInputElement).value || this.localToday();
      this.dayJobOpen = null; this.dayJobs = {};
      this.draw();
      await this.reloadDay();
      this.draw();
    }));

    // expand a job → its job sheet (GET /turns/:id/job + /context) through
    // ensureJob: a fresh fetch when there is no cached sheet (renderJob's
    // !job branch kicks it, so the card self-heals from ANY path), a quiet
    // revalidate when there is one (R70 Mark: "when you update the PDS it's
    // not updating the info that goes to cleaners" — codes, instructions and
    // checklist quirks change mid-day; the cached sheet paints instantly and
    // the refetch merges when it lands, never over a mid-interaction DOM).
    all('[data-hk-jobtoggle]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.hkJobtoggle!;
      if (this.dayJobOpen === id) { this.dayJobOpen = null; this.draw(); return; }
      this.dayJobOpen = id;
      this.jobFailed.delete(id);
      this.draw();              // open with the cached sheet (or a spinner that fetches its own)
      if (this.dayJobs[id]) void this.ensureJob(id, { revalidate: true });
    }));

    // R70: stocking checklist — collapsible + per-item ticks saved on the turn.
    all('[data-hk-stocktoggle]').forEach(el => el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.hkStocktoggle!;
      this.stockOpenTurn = this.stockOpenTurn === id ? null : id;
      this.draw();
    }));
    all('[data-hk-stockall]').forEach(el => el.addEventListener('click', () => {
      this.stockShowAll = !this.stockShowAll;
      this.draw();
    }));
    all('[data-hk-stockitem]').forEach(el => el.addEventListener('change', async (e) => {
      const [id, item] = (el as HTMLInputElement).dataset.hkStockitem!.split('|');
      const checked = (e.currentTarget as HTMLInputElement).checked;
      const t = this.findDayTurn(id);
      if (t) { if (!t.stock_check || typeof t.stock_check !== 'object') t.stock_check = {}; t.stock_check[item] = checked; this.draw(); }
      const j = await this.post(`/turns/${id}`, { stock_item: item, stock_checked: checked });
      if (!j) {
        // Save failed — revert the optimistic tick so the list never lies.
        const t2 = this.findDayTurn(id);
        if (t2 && t2.stock_check && typeof t2.stock_check === 'object') { t2.stock_check[item] = !checked; this.draw(); }
      } else if (j.turn) { this.replaceDayTurn(j.turn); }
    }));

    // R79 #3: inventory count — qty saves per item (serialized; the server
    // rewrites the whole pds blob), completion stamps the property.
    all('[data-hk-invtoggle]').forEach(el => el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.hkInvtoggle!;
      this.invOpenTurn = this.invOpenTurn === id ? null : id;
      this.draw();
    }));
    all('[data-hk-invqty]').forEach(el => el.addEventListener('change', (e) => {
      // First-pipe split — item names are pipe-free by server rule now, but a
      // legacy piped name must not silently save under a truncated identity.
      const rawKey = String((el as HTMLElement).dataset.hkInvqty || '');
      const sep = rawKey.indexOf('|');
      const id = sep < 0 ? '' : rawKey.slice(0, sep);
      const item = sep < 0 ? '' : rawKey.slice(sep + 1);
      if (!id || !item) return;
      const raw = (e.currentTarget as HTMLInputElement).value.trim();
      const qty = raw === '' ? null : Number(raw);
      if (qty != null && !(qty >= 0 && qty <= 9999)) { alert('Quantity must be 0–9999.'); return; }
      const job = this.dayJobs[id];
      const row = job?.inventory?.items?.find((x: any) => x.item === item);
      const prev = row ? row.qty : null;
      if (row) row.qty = qty;   // optimistic — inputs keep their value across draws
      this.dayMutSeq++;   // review: the cached-job revalidate must discard snapshots issued before this write
      this.invSaveChain = this.invSaveChain.then(async () => {
        try {
          const r = await fetch('/api/ob/inventory-count', {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ listing_id: this.findDayTurn(id)?.listing_id, item, qty, by: HousekeepingPage.actorName() || 'cleaner' }),
          });
          if (!r.ok) { const j = await r.json().catch(() => ({} as any)); throw new Error(j.error || `HTTP ${r.status}`); }
          const jOk = await r.json().catch(() => ({} as any));
          if (jOk.auto_request) alert(`📦 Below the MIN — a supply request was created automatically:\n${jOk.auto_request.note || ''}`);   // R87 #1.8
        } catch (err: any) {
          alert(`Count didn't save — ${err.message}`);
          if (row) row.qty = prev;
          const inp = this.container.querySelector(`[data-hk-invqty="${CSS.escape(`${id}|${item}`)}"]`) as HTMLInputElement | null;
          if (inp) inp.value = prev == null ? '' : String(prev);
        }
      });
    }));
    all('[data-hk-invdone]').forEach(el => el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.hkInvdone!;
      const t = this.findDayTurn(id);
      if (!t) return;
      this.dayMutSeq++;   // review: same revalidate guard as every other mutation
      await this.invSaveChain;   // queued qty saves land first
      try {
        const r = await fetch('/api/ob/inventory-count-complete', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ listing_id: t.listing_id, by: HousekeepingPage.actorName() || 'cleaner' }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        const job = this.dayJobs[id];
        if (job && job.inventory) { job.inventory.counted_at = j.counted_at; job.inventory.counted_by = j.by; job.inventory.due = false; }
        this.draw();
      } catch (err: any) { alert(`Couldn't complete the count — ${err.message}`); }
    }));

    // check in → advance status, then refresh the day route.
    // R112 (Jesse #7 "this just keeps loading … click it again and it opens"):
    // the old handler threw the cached job sheet away, reloaded the day with
    // SLIM rows and redrew — the still-open card had no sheet and nothing
    // refetched it, so it sat on a spinner until the header was tapped twice.
    // Now: a busy, disabled button while the POST runs (it is a whole-blob
    // write — seconds on production — so cleaners double-tapped), the FULL
    // turn from the response merged and drawn immediately (the walkthrough
    // appears on this very tap), then the sheet revalidated + the day
    // reloaded (which re-merges the full fields itself) in the background.
    all('[data-hk-checkin]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.hkCheckin!;
      if (this.checkingIn) return;                           // double-tap guard
      this.checkingIn = id; this.draw();                     // button → disabled "Checking in…"
      const j = await this.post(`/turns/${id}/checkin`, {});
      this.checkingIn = null;
      if (!j) { this.draw(); return; }                       // post() already alerted
      const full = (j && j.turn && j.turn.id) ? j.turn : null;
      if (full) this.mergeTurnIfFresh(full);
      this.dayJobOpen = id;                                  // stay on this card whatever else moved
      this.draw();                                           // ONE tap → the walkthrough is on screen
      this.container.querySelector(`[data-hk-jobtoggle="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      await Promise.all([
        this.ensureJob(id, { revalidate: true, draw: false }),
        this.reloadDay(),
        HousekeepingPage.fieldRole() === 'cleaner' ? Promise.resolve() : this.reloadBoard(),
        this.reloadCleaners(),
      ]);
      if (full) this.mergeTurnIfFresh(full);                 // after reloadDay — slim rows never win over the full turn
      this.drawIfIdle();
    }));
    all('[data-hk-checkout]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.hkCheckout!;
      // R50: R38's alert-and-wait dead-ended on dead/thin wifi (740 SLR — the
      // upload never finishes, checkout blocked forever). The bypass exists
      // ONLY for photos that were TAKEN and are stuck in the upload queue —
      // those self-heal when signal returns. A photo that was never taken
      // keeps the R20 hard block (server 400): skipping must never become the
      // easy path around must-photograph accountability.
      const t = this.findDayTurn(id);
      const missingPhoto = (t?.logged_issues || []).some((x: any) => x && x.photo_required && !x.photo);
      const queued = pendingCountFor(`/turns/${id}/photo`);
      let body: any = {};
      if (missingPhoto && queued > 0) {
        if (!confirm("Photos haven't finished uploading (bad signal?). Finish the clean anyway — your queued photos keep uploading in the background and the office will see which are still missing.")) return;
        body = { skip_photo_gate: true, reason: 'connectivity' };
      }
      const j = await this.post(`/turns/${id}/checkout`, body);
      if (j) {
        // R51: the checkout response is the FULL turn — it powers the
        // "🎉 Clean submitted!" confirmation card (start/finish/total/photos).
        const full = (j && j.turn) ? j.turn : j;
        this.completedTurn = full && full.id ? full : null;
        delete this.dayJobs[id];
        // Refetch the job sheet too — the card stays open on the verify step,
        // and without a sheet the body would sit on a spinner.
        const [jj] = await Promise.all([this.get(`/turns/${id}/job`), this.reloadDay(), this.reloadBoard(), this.reloadCleaners()]);
        if (jj) { this.dayJobs[id] = jj; this.noteJobSheet(id, jj); }   // review R120 #14 (round 4): newer than the checkout's own answer
        // Slim day rows drop capture — merge the full checkout turn back so
        // the thumbs/verify state on this card stay populated (its findings
        // show the newest word: the sheet's, when that is newer).
        if (full && full.id) this.replaceDayTurn(full);
        this.draw();
        // Walk the cleaner straight to the after-photo step.
        this.container.querySelector(`[data-hk-verify-sec="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }));

    // R51: dismiss the checkout confirmation card.
    all('[data-hk-confirm-x]').forEach(el => el.addEventListener('click', () => {
      this.completedTurn = null;
      this.draw();
    }));

    // R51: reopen a submitted clean to edit (server already allows the
    // backward status move) — check out again when done.
    all('[data-hk-reopen]').forEach(el => el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.hkReopen!;
      if (!confirm('Reopen this clean to edit? It goes back to "In progress" — complete it again when you finish.')) return;
      const j = await this.post(`/turns/${id}`, { status: 'in_progress' });
      if (j) {
        if (this.completedTurn && this.completedTurn.id === id) this.completedTurn = null;
        await this.refreshDayAndBoard();
      }
    }));

    // R51: cleaner-facing proof-of-clean (the office board button stays).
    // Authed /api HTML — openApiDoc, never a plain navigation (401 in prod).
    all('[data-hk-proof]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const lid = (el as HTMLElement).dataset.hkProof!;
      void openApiDoc(`${API}/housekeeping/clean-report/${encodeURIComponent(lid)}?days=7`, 'Proof of clean');
    }));

    // R51 (Marina): company lead hands a job to a teammate — same-company
    // roster select on today's cards in the field view.
    all('[data-hk-crewassign]').forEach(el => {
      el.addEventListener('click', (e) => e.stopPropagation());
      el.addEventListener('change', async (e) => {
        const sel = e.currentTarget as HTMLSelectElement;
        const id = sel.dataset.hkCrewassign!;
        if (!sel.value) return;
        const j = await this.postConfirm(`/turns/${id}/assign`, { cleaner_ids: [sel.value] });
        if (j) await this.refreshDayAndBoard();
        else this.draw();   // Cancel / failure: back to the cleaner the server still holds
      });
    });

    // GLOBAL cleaning mode per property (Scheduler → Properties list) → POST /cleaning-modes.
    all('[data-hk-mode]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [lid, mode] = (e.currentTarget as HTMLElement).dataset.hkMode!.split('|');
      if (this.modeOf(lid) === mode) return;
      const j = await this.post('/cleaning-modes', { listing_id: lid, mode });
      if (j) { this.cleaningModes[String(lid)] = mode; this.schedProposal = null; this.draw(); }
    }));
    // Per-TURN override (board detail) → POST /turns/:id { mode_override } ('' = back to default).
    all('[data-hk-modeover]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [tid, mode] = (e.currentTarget as HTMLElement).dataset.hkModeover!.split('|');
      const j = await this.post(`/turns/${tid}`, { mode_override: mode || null });
      if (j && j.turn) {
        const i = (this.board?.turns || []).findIndex((x: any) => x.id === tid);
        if (i >= 0) this.board.turns[i] = { ...this.board.turns[i], mode_override: j.turn.mode_override };
        this.draw();
      }
    }));

    // clean timer: start / pause → POST /turns/:id/timer.
    all('[data-hk-timer]').forEach(el => el.addEventListener('click', async (e) => {
      const [id, action] = (e.currentTarget as HTMLElement).dataset.hkTimer!.split('|');
      const j = await this.post(`/turns/${id}/timer`, { action });
      if (j && j.turn) { this.replaceDayTurn(j.turn); this.draw(); }
    }));
    // clean timer: log minutes manually.
    all('[data-hk-timermanual]').forEach(el => el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.hkTimermanual!;
      const val = window.prompt('Minutes to add for this clean:', '');
      if (val === null) return;
      const minutes = Math.round(Number(val));
      if (!Number.isFinite(minutes) || minutes <= 0) { alert('Enter a positive number of minutes.'); return; }
      const j = await this.post(`/turns/${id}/timer`, { action: 'manual', minutes });
      if (j && j.turn) { this.replaceDayTurn(j.turn); this.draw(); }
    }));

    // smart checklist: toggle an item → persist the full checked set.
    all('[data-hk-check]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkCheck!;
      const turn = this.findDayTurn(id);
      if (!turn) return;
      const done = new Set<string>(Array.isArray(turn.checklist_done) ? turn.checklist_done : []);
      const item = input.dataset.hkCheckitem!;
      if (input.checked) done.add(item); else done.delete(item);
      turn.checklist_done = [...done];   // optimistic
      this.draw();
      // R70 (Mark: "cleaners click submit but forget to hit complete"): when
      // the LAST checklist box gets ticked, offer the finish right here. The
      // two-step stays (Complete stamps the pay/SLA record and opens the
      // photo-verification stage) — this just removes the forgetting.
      if (input.checked) {
        const boxes = [...this.container.querySelectorAll<HTMLInputElement>(`input[data-hk-check="${CSS.escape(id)}"]`)];
        // Only invite a checkout the server will ACCEPT — both walk-in reports
        // answered and no photo-required issue still missing its photo.
        const reportsDone = !!(turn.reports && turn.reports.damage != null && turn.reports.supply != null);
        const photoBlocked = (turn.logged_issues || []).some((x: any) => x && x.photo_required && !x.photo)
          && pendingCountFor(`/turns/${id}/photo`) === 0;
        if (boxes.length > 1 && boxes.every(bx => bx.checked) && reportsDone && !photoBlocked) {
          setTimeout(() => {
            if (this.dayJobOpen === id && confirm('That was the last checklist item 🎉 — Complete this cleaning now? (You\'ll add after-photos right after.)')) {
              (this.container.querySelector(`[data-hk-checkout="${CSS.escape(id)}"]`) as HTMLButtonElement | null)?.click();
            }
          }, 150);
        }
      }
      const j = await this.post(`/turns/${id}/checklist`, { checked: turn.checklist_done });
      if (j && j.turn) this.replaceDayTurn(j.turn);
    }));

    // request supplies: open the SHOPPING storefront preset to this turn's
    // property (R13 #2 — one ordering experience everywhere; the free-text
    // path lives on as the "Something else?" row inside the storefront).
    all('[data-hk-suptoggle]').forEach(el => el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.hkSuptoggle!;
      const t = this.findDayTurn(id) || ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
      this.storeListing = t ? String(t.listing_id) : '';
      this.storeLock = !!t;
      this.storeTurnId = t ? String(t.id) : '';
      this.storeNeededBy = t ? this.nextCleanFor(String(t.listing_id)) : '';
      this.storeNeededByEdited = false;
      this.storeOpen = true;
      this.draw();
      await this.loadStorefront(); this.draw();
    }));

    // R14: my-performance / my-requests section toggles.
    all('[data-hk-mysec]').forEach(el => el.addEventListener('click', () => {
      const k = (el as HTMLElement).dataset.hkMysec!;
      if (k === 'perf') this.myPerfOpen = !this.myPerfOpen;
      else this.myOrdersOpen = !this.myOrdersOpen;
      this.draw();
    }));
    // R37: recent-cleans section toggle.
    all('[data-hk-recent]').forEach(el => el.addEventListener('click', () => {
      this.recentOpen = !this.recentOpen;
      this.draw();
    }));

    // R31 #4: reference docs from the ticket. These are AUTHED /api routes —
    // R63 moved the shell-then-write mechanics into the shared openApiDoc
    // (nav.ts) so every page opens API documents the same PWA-safe way.
    all('[data-hk-guide]').forEach(el => el.addEventListener('click', () => {
      const lid = (el as HTMLElement).dataset.hkGuide!;
      void openApiDoc(`/api/ob/cleaner-guide/${encodeURIComponent(lid)}`, 'Property guide');
    }));
    all('[data-hk-standards]').forEach(el => el.addEventListener('click', () => {
      void openApiDoc('/api/ob/standards', 'Standards');
    }));
    // R52: cleaner's actual Cleaning guide (per-property, print view).
    all('[data-hk-cleanguide]').forEach(el => el.addEventListener('click', () => {
      const lid = (el as HTMLElement).dataset.hkCleanguide!;
      void openApiDoc(`/api/ob/cleaner-guide/${encodeURIComponent(lid)}`, 'Cleaning guide');
    }));

    // R20 — Quick Log Issue from a cleaning task (photo/video + voice + route).
    all('[data-hk-logissue]').forEach(el => el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.hkLogissue!;
      const t = this.findDayTurn(id);
      if (!t) return;
      QuickLogIssue.open({
        sourceContext: 'cleaning_task', listingId: String(t.listing_id), listingName: t.listing_name,
        turnId: id, createdBy: this.currentCleanerName(),
        onLogged: async () => { await this.refreshDayAndBoard(); },
      });
    }));
    // R35 #10: attach photo(s) to a specific checklist item — the inspector
    // sees them on their task (clean-context panel) with a verify toggle.
    // R38: checklist-item photos ride the same background uploader — the old
    // inline loop silently dropped photos on a non-ok response and abandoned
    // remaining chunks on the first thrown fetch.
    all('[data-hk-itemphoto]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const turnId = input.dataset.hkItemphoto!;
      const item = input.dataset.hkItem!;
      const files = Array.from(input.files || []);
      if (!turnId || !item || !files.length) return;
      input.value = ''; input.disabled = true;   // R38: double-pick guard
      await enqueuePhotos(`${API}/turns/${turnId}/photo`, files, { kind: 'before', checklist_item: item }, 'checklist photo');
      this.draw();
    }));

    // R20 — verify a logged issue with a photo (satisfies the must-photograph
    // gate). R38: queued — the old path dropped the photo TOTALLY silently on
    // any failure, which for a must-photograph gate meant a blocked checkout.
    all('[data-hk-lissphoto]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const [turnId, lissId] = String(input.dataset.hkLissphoto || '').split('|');
      const file = input.files && input.files[0];
      if (!turnId || !lissId || !file) return;
      input.value = ''; input.disabled = true;   // R38: double-pick guard
      await enqueuePhotos(`${API}/turns/${turnId}/photo`, [file], { logged_issue_id: lissId }, 'issue verify photo');
      this.draw();
    }));

    // R33 #3: '+ Add clean' — open/submit/cancel the manual-clean form.
    all('[data-hk-addclean]').forEach(el => el.addEventListener('click', async () => {
      this.addCleanOpen = !this.addCleanOpen;
      this.draw();
      if (this.addCleanOpen && !this.pickerListings.length) { await this.loadStorefront(); this.draw(); }
    }));
    // R78: global standard-checklist editor.
    const loadStdList = async () => {
      this.stdEditData = null; this.draw();
      try {
        const r = await fetch(`${API}/standard-checklist`);
        this.stdEditData = r.ok ? await r.json() : { error: true };
      } catch { this.stdEditData = { error: true }; }
      this.draw();
    };
    all('[data-hk-stdedit]').forEach(el => el.addEventListener('click', () => {
      this.stdEditOpen = !this.stdEditOpen;
      // Refetch on EVERY open (review: a snapshot cached at 9am let a later
      // save silently delete another editor's additions) — an unsaved draft
      // still wins the textarea, but the caption/baseline stay current.
      if (this.stdEditOpen) { void loadStdList(); return; }
      this.draw();
    }));
    all('[data-hk-stdretry]').forEach(el => el.addEventListener('click', () => void loadStdList()));
    all('[data-hk-stdclose]').forEach(el => el.addEventListener('click', () => { this.stdEditOpen = false; this.stdDraft = null; this.draw(); }));
    all('[data-hk-stdtext]').forEach(el => el.addEventListener('input', (e) => {
      this.stdDraft = (e.currentTarget as HTMLTextAreaElement).value;
    }));
    all('[data-hk-stdreset]').forEach(el => el.addEventListener('click', () => {
      const ta = this.container.querySelector('[data-hk-stdtext]') as HTMLTextAreaElement | null;
      const defs: string[] = (this.stdEditData && this.stdEditData.default_items) || [];
      if (ta && defs.length) { ta.value = defs.join('\n'); this.stdDraft = ta.value; }   // fills the box only — nothing saves until Save
    }));
    all('[data-hk-stdsave]').forEach(el => el.addEventListener('click', async () => {
      if (this.stdEditSaving || !this.stdEditData || this.stdEditData.error) return;
      const ta = this.container.querySelector('[data-hk-stdtext]') as HTMLTextAreaElement | null;
      const items = (ta?.value || '').split('\n').map(s => s.trim()).filter(Boolean);
      if (!items.length) { alert('The standard list can\'t be empty — use "Skip standard checklist" on a property card to hide it for one home.'); return; }
      this.stdEditSaving = true; this.draw();
      const j = await this.put('/standard-checklist', { items, by: HousekeepingPage.actorName() || 'office' });
      this.stdEditSaving = false;
      if (j) {
        this.stdEditData = { ...this.stdEditData, items: j.items, customized: true, by: j.by, updated_at: j.updated_at };
        // Cached job sheets carry the old list — refetch lazily. Clearing the
        // open-sheet id too, or an expanded Day-tab card spins forever (only
        // the toggle handler refetches). Past-cleans details cache the same
        // payload — clear so "reconstructed with today's list" stays true.
        this.dayJobs = {}; this.dayJobOpen = null;
        this.histDetail.clear(); this.histOpen.clear();
        // Show the SAVED list, not the typed draft — the server may have
        // sanitized (40-item / 200-char caps, dedupe) — and drop the draft.
        this.stdDraft = null;
        if (ta) ta.value = (j.items || []).join('\n');
      }
      this.draw();
    }));
    all('[data-hk-ac-cancel]').forEach(el => el.addEventListener('click', () => { this.addCleanOpen = false; this.acForm = null; this.draw(); }));
    all('[data-hk-ac-submit]').forEach(el => el.addEventListener('click', async () => {
      if (this.addingClean) return;
      const dateEl = this.container.querySelector('[data-hk-ac-date]') as HTMLInputElement;
      // R46: datalist picker resolves the typed LABEL back to the listing id;
      // a non-matching label blocks submit rather than guessing.
      const typed = ((this.container.querySelector('[data-hk-ac-listing]') as HTMLInputElement)?.value || '').trim().toLowerCase();
      const match = typed ? this.pickerListings.find(l => this.pickerLabelFor(l).toLowerCase() === typed) : null;
      const listing_id = match ? String(match.id) : '';
      const date = dateEl?.value || '';
      const type = (this.container.querySelector('[data-hk-ac-type]') as HTMLSelectElement)?.value || 'turnover';
      const note = (this.container.querySelector('[data-hk-ac-note]') as HTMLInputElement)?.value.trim() || '';
      const cleaner_id = (this.container.querySelector('[data-hk-ac-cleaner]') as HTMLSelectElement)?.value || '';
      if (!listing_id) { alert('Pick a property from the list — type to search, then choose a suggestion.'); return; }
      if (!date || !dateEl.checkValidity() || date < this.localToday()) { alert('Pick a date (today or later).'); return; }
      // the busy redraw rebuilds the form — keep what was typed
      this.acForm = { listing_id, date, type, note, cleaner_id };
      this.addingClean = true; this.draw();
      // R82: a home cleaned within the prior 2 days 409s with needs_confirm —
      // surface the server's exact message as the confirm, then repost.
      const addBody: any = { listing_id, date, type, note, by: HousekeepingPage.actorName() || 'office' };
      let j: any = null;
      {
        const r = await fetch(`${API}/turns`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addBody) }).catch(() => null);
        const jr = r ? await r.json().catch(() => ({} as any)) : {};
        if (r && r.ok) j = jr;
        else if (r && r.status === 409 && jr.needs_confirm) {
          if (window.confirm(jr.error)) j = await this.post('/turns', { ...addBody, confirm_recent: true });
        } else {
          alert((jr && (jr.message || jr.error)) || 'Couldn\'t add the clean — try again.');
        }
      }
      this.addingClean = false;
      if (j && j.turn) {
        // Assign-at-creation (R46: "add all cleaners as well") — a failed
        // assign leaves the clean unassigned rather than blocking creation.
        // R107: an over-limit pick asks first; Cancel keeps the clean, unassigned.
        if (cleaner_id) await this.postConfirm(`/turns/${j.turn.id}/assign`, { cleaner_ids: [cleaner_id] });
        this.addCleanOpen = false;
        this.acForm = null;
        this.expandedTurn = j.turn.id;
        this.schedProposal = null;
        this.noteIfBeyondHorizon(date);
        await this.reloadBoard();
      }
      this.draw();
    }));
    // R35 #3: manual ECI/LCO toggle on the turn card — ECI tags the ARRIVAL
    // date (next check-in), LCO tags the CHECKOUT date; the same tags feed the
    // cleaner ticket, inspection queue, and arrivals board.
    all('[data-hk-ecilco]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, kind] = String((e.currentTarget as HTMLElement).dataset.hkEcilco || '').split('|');
      const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
      if (!t) return;
      const date = kind === 'eci' ? t.next_check_in : t.check_out;
      if (!date) return;
      const cur = kind === 'eci' ? !!t.eci : !!t.lco;
      const j = await this.post('/eci-lco', { listing_id: String(t.listing_id), date, kind, value: !cur, by: HousekeepingPage.actorName() || 'office' });
      if (j) { await this.reloadBoard(); this.draw(); }
    }));
    // ECI/LCO time — "put the time of the LCO or ECI so cleaners/inspectors
    // can see it". Free text ('1 PM', '13:00'), stored as given; empty clears.
    // Debounced 600ms so typing settles before the POST; the redraw (which
    // refreshes the row badges) waits for blur so it never steals focus.
    all('[data-hk-ecitime]').forEach(el => {
      el.addEventListener('click', (e) => e.stopPropagation());
      let tmr: number | null = null;
      const save = async (input: HTMLInputElement) => {
        const [id, kind] = String(input.dataset.hkEcitime || '').split('|');
        const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
        if (!t) return;
        const date = kind === 'eci' ? t.next_check_in : t.check_out;
        if (!date) return;
        const timeField = kind === 'eci' ? 'eci_time' : 'lco_time';
        const val = input.value.trim();
        if (val === (t[timeField] || '')) return;
        const j = await this.post('/eci-lco', { listing_id: String(t.listing_id), date, kind, value: true, [timeField]: val, by: HousekeepingPage.actorName() || 'office' });
        if (j) {
          const i = this.board.turns.findIndex((x: any) => x.id === id);
          if (i >= 0) this.board.turns[i] = { ...this.board.turns[i], [timeField]: (j.entry && j.entry[timeField] != null) ? j.entry[timeField] : (val || null) };
          // Redraw only when the cursor has left the input — otherwise the
          // rebuilt DOM would eat the keystrokes mid-typing.
          if (document.activeElement !== input) this.draw();
        }
      };
      el.addEventListener('input', (e) => {
        const input = e.currentTarget as HTMLInputElement;
        if (tmr) window.clearTimeout(tmr);
        tmr = window.setTimeout(() => { tmr = null; void save(input); }, 600);
      });
      el.addEventListener('change', async (e) => {
        const input = e.currentTarget as HTMLInputElement;
        if (tmr) { window.clearTimeout(tmr); tmr = null; }
        await save(input);
        this.draw();
      });
    });

    // R35 #1: admin note on a clean ("waiting on cleaner", …) — saved on blur,
    // shown on the collapsed row so the office sees status without expanding.
    all('[data-hk-note]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkNote!;
      const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
      if (!t || input.value === (t.note || '')) return;
      const j = await this.post(`/turns/${id}`, { note: input.value.trim() });
      if (j && j.turn) {
        const i = this.board.turns.findIndex((x: any) => x.id === id);
        if (i >= 0) this.board.turns[i] = { ...this.board.turns[i], note: j.turn.note };
        this.draw();
      }
    }));

    // R52: office edits the door code from the turn card. Writes ONE PDS field
    // via POST /api/ob/pds {listing_id, section, field, value} (server/onboarding.js
    // ~L762). Field = 'Smart lock code location' — the FIRST field in
    // resolvePropertyCreds' hk_code pick chain (server/assetops.js), so the new
    // value is what cleaners see on the job sheet.
    all('[data-hk-editcodes]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const lid = (el as HTMLElement).dataset.hkEditcodes!;
      const val = prompt('Door / entry code for cleaners (saves to Property Details → Access):');
      if (val == null || !val.trim()) return;
      try {
        const r = await fetch('/api/ob/pds', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listing_id: lid, section: 'access', field: 'Smart lock code location', value: val.trim() }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) { alert(j.error || `Couldn't save the code (${r.status}).`); return; }
        alert('Code saved — job sheets pick it up (Hostaway-cached creds refresh within ~30 min).');
      } catch { alert('No connection — the code didn\'t save.'); }
    }));

    // R33 #3: reschedule — move a clean to a different date. Date inputs fire
    // 'change' per segment edit in Chrome, so guard hard (validity + not-past)
    // and confirm before posting — a mistyped year must never strand a clean.
    // R61 (Madel): cancel a clean whose reservation moved/cancelled — the
    // extension case left an orphaned turn with no way to remove it.
    all('[data-hk-cancelturn]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.hkCancelturn!;
      if (!confirm('Cancel this clean? The turn is removed from the board and the cleaner\'s day. This cannot be undone (a new clean can always be added).')) return;
      const ok = await this.del(`/turns/${encodeURIComponent(id)}`);   // del() alerts the server's reason on failure
      if (!ok) return;
      await this.refreshDayAndBoard();
    }));

    all('[data-hk-movedate]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkMovedate!;
      const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === id);
      const date = input.value;
      if (!t || !date || date === t.check_out) return;
      if (!input.checkValidity() || date < this.localToday()) { input.value = t.check_out; return; }
      // R114 (Madel): the clean's inspection moves with it (with_inspection —
      // best-effort server-side; it never fails or undoes the clean move).
      if (!confirm(`Move the ${t.listing_name} clean from ${t.check_out} to ${date}? (An inspection scheduled that day moves with it.)`)) { input.value = t.check_out; return; }
      const j = await this.postConfirm(`/turns/${id}/move`, { date, by: HousekeepingPage.actorName() || 'office', with_inspection: true });
      if (j) {
        this.schedProposal = null;
        if (j.inspection && j.inspection.moved) this.toast(`Its inspection moved too${j.inspection.inspector ? ` (${j.inspection.inspector})` : ' (unassigned)'}.`, 3600);
        else {
          // the confirm promised it would move — a split the office must act on
          // (an alert survives the redraw below; a toast would not)
          const warn = followWarning(j.inspection, { name: t.listing_name, to: date, from: t.check_out });
          if (warn) alert(warn);
        }
        this.noteIfBeyondHorizon(date);
        await this.reloadBoard();
      }
      this.draw();   // on failure the redraw snaps the input back to the stored date
    }));

    // ── ad-hoc field storefront + damage report ──
    all('[data-hk-openstore]').forEach(el => el.addEventListener('click', async () => {
      // Ad-hoc open always starts fresh from the picker — a lock left behind by
      // a turn-open would silently file the order against the wrong property.
      this.storeLock = false; this.storeListing = ''; this.storeTurnId = '';
      this.storeCustomName = ''; this.storeCustomQty = 1;
      this.storeNeededBy = ''; this.storeNeededByEdited = false;
      this.storeOpen = true; this.draw();
      await this.loadStorefront(); this.draw();
    }));
    // R30: Report damage IS the quick issue logger (Jesse: "should basically
    // be like the issue reporting you already built") — damage preset,
    // searchable picker, assigned-today homes first, photos-first flow.
    all('[data-hk-opendamage]').forEach(el => el.addEventListener('click', () => {
      QuickLogIssue.open({
        sourceContext: 'cleaning_task',
        defaultCategory: 'damage',
        createdBy: this.currentCleanerName(),
        priorityListings: this.dayTurns.map((t: any) => ({ id: String(t.listing_id), name: String(t.listing_name || t.listing_id) })),
        onLogged: async () => { await this.refreshDayAndBoard(); },
      });
    }));
    // ONE reset path for every way out of the store modal — a stale lock or
    // custom item surviving a backdrop-tap files orders against the wrong home.
    const resetStore = () => {
      this.storeOpen = false;
      this.storeLock = false; this.storeListing = ''; this.storeTurnId = '';
      this.storeCustomName = ''; this.storeCustomQty = 1;
      this.storeNeededBy = ''; this.storeNeededByEdited = false;
    };
    all('[data-hk-closemodal]').forEach(el => el.addEventListener('click', () => { resetStore(); this.draw(); }));
    all('[data-hk-modalbg]').forEach(el => el.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) { resetStore(); this.draw(); }
    }));
    // R30: searchable picker — resolve the typed label back to a listing id.
    // NON-matching text CLEARS the selection (submit stays blocked) — showing
    // one property while a different one is silently selected is worse.
    // R66: soonest-date-needed — store only, no redraw (Chrome fires change
    // per date segment; a redraw mid-edit would eat the cleaner's typing).
    all('[data-hk-neededby]').forEach(el => el.addEventListener('change', (e) => {
      this.storeNeededBy = (e.currentTarget as HTMLInputElement).value || '';
      this.storeNeededByEdited = true;
    }));
    all('[data-hk-storelisting-in]').forEach(el => el.addEventListener('change', (e) => {
      const typed = (e.currentTarget as HTMLInputElement).value.trim().toLowerCase();
      const hit = this.sortedPickerListings().find(l => this.pickerLabelFor(l).toLowerCase() === typed);
      this.storeListing = hit ? String(hit.id) : '';
      this.storeNeededBy = hit ? this.nextCleanFor(String(hit.id)) : '';
      this.storeNeededByEdited = false;
      this.draw();
    }));
    // R64 (Alan: "+ scrolls to the top every tap, 40 taps for pods") — patch
    // the modal IN PLACE instead of draw(): the full rebuild reset the order
    // sheet's scroll position on every quantity change. Quantities are also
    // TYPE-ABLE now, so a big count never needs 40 taps regardless of
    // pack settings.
    const patchStoreTotals = () => {
      const cartCount = Object.values(this.cart).reduce((a, b) => a + b, 0);
      const cartTotal = this.catalog.reduce((sum, s) => sum + (this.cart[s.id] || 0) * (Number(s.target_price) || 0), 0);
      const hasCustom = this.storeCustomName.trim().length > 0;
      const sum = this.container.querySelector('.hk-cart-sum');
      if (sum) sum.textContent = `${cartCount} item${cartCount === 1 ? '' : 's'} in cart${hasCustom ? ' + 1 custom' : ''}${cartTotal ? ` · ~${this.fmtMoney(cartTotal)}` : ''}`;
      const submit = this.container.querySelector('[data-hk-submitstore]') as HTMLButtonElement | null;
      if (submit) submit.disabled = (!cartCount && !hasCustom) || !this.storeListing || this.fieldBusy;
    };
    const setCartQty = (id: string, next: number) => {
      if (next <= 0) delete this.cart[id]; else this.cart[id] = next;
      const input = this.container.querySelector(`[data-hk-qtyin="${CSS.escape(id)}"]`) as HTMLInputElement | null;
      if (input && Number(input.value) !== next) input.value = String(Math.max(0, next));
      const dec = this.container.querySelector(`[data-hk-cart="${CSS.escape(id)}|dec"]`) as HTMLButtonElement | null;
      if (dec) dec.disabled = next <= 0;
      patchStoreTotals();
    };
    all('[data-hk-cart]').forEach(el => el.addEventListener('click', (e) => {
      const [id, op] = (e.currentTarget as HTMLElement).dataset.hkCart!.split('|');
      const cur = this.cart[id] || 0;
      setCartQty(id, op === 'inc' ? cur + 1 : Math.max(0, cur - 1));
    }));
    all('[data-hk-qtyin]').forEach(el => el.addEventListener('change', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkQtyin!;
      const n = Math.max(0, Math.min(999, Math.round(Number(input.value) || 0)));
      setCartQty(id, n);
    }));
    // custom "something else?" row mirrors to STATE so stepper redraws can't
    // eat the typed text (same state-backed rule as everywhere else).
    all('[data-hk-customname]').forEach(el => el.addEventListener('input', (e) => {
      this.storeCustomName = (e.currentTarget as HTMLInputElement).value;
      // enable/disable submit in place — a full draw would steal focus mid-typing
      const btn = this.container.querySelector('[data-hk-submitstore]') as HTMLButtonElement | null;
      if (btn) btn.disabled = (!Object.values(this.cart).some(q => q > 0) && !this.storeCustomName.trim()) || !this.storeListing || this.fieldBusy;
    }));
    all('[data-hk-customqty]').forEach(el => el.addEventListener('input', (e) => {
      this.storeCustomQty = Math.max(1, Math.round(Number((e.currentTarget as HTMLInputElement).value) || 1));
    }));
    all('[data-hk-submitstore]').forEach(el => el.addEventListener('click', async () => {
      const items = Object.entries(this.cart).filter(([, q]) => q > 0).map(([id, qty]) => {
        const s = this.catalog.find(x => String(x.id) === id);
        return { name: s ? s.name : id, qty, unit_price: s && s.target_price != null ? Number(s.target_price) : 0 };
      });
      if (this.storeCustomName.trim()) {
        items.push({ name: this.storeCustomName.trim(), qty: this.storeCustomQty, unit_price: 0 });
      }
      if (!items.length || !this.storeListing) return;
      const l = this.pickerListings.find(x => String(x.id) === this.storeListing);
      // R37: resolve by the EXACT turn the store was opened from — re-deriving
      // by listing would attach a recent turn's supply report to today's turn
      // at the same home (leaving the recent turn's checkout gate unsatisfied).
      const lockedTurn = this.storeLock
        ? (this.findDayTurn(this.storeTurnId)
          || [...this.dayTurns, ...this.recentTurns].find(t => String(t.listing_id) === this.storeListing))
        : null;
      this.fieldBusy = true; this.draw();
      const j = await this.post('/supplies/orders', {
        listing_id: this.storeListing,
        listing_name: l ? (l.short_name || l.name) : (lockedTurn ? lockedTurn.listing_name : this.storeListing),
        items,
        requested_by: HousekeepingPage.actorName() || 'cleaner',
        // R31: turn_id lets the server flip that turn's "supplies needed?"
        // walk-in flag to 'reported' — this order IS the supply report.
        turn_id: lockedTurn ? lockedTurn.id : undefined,
        // Only an ACTUAL edit ships — an untouched prefill lets the server
        // auto-match (source 'auto'), so the office chip stays honest.
        needed_by: this.storeNeededByEdited ? (this.storeNeededBy || undefined) : undefined,
        note: lockedTurn
          ? `[housekeeping turn ${lockedTurn.id}] storefront request during the clean`
          : 'Field storefront order — requested by cleaner',
      });
      this.fieldBusy = false;
      if (j) {
        this.cart = {}; this.storeOpen = false; this.storeListing = ''; this.storeLock = false; this.storeTurnId = '';
        this.storeCustomName = ''; this.storeCustomQty = 1;
        this.storeNeededBy = ''; this.storeNeededByEdited = false;
        if (lockedTurn) await this.reloadDay();   // pick up the flipped walk-in flag
        await this.loadMine();   // the new request shows up in "My supply requests"
        this.draw(); alert('Order request sent to your admin team.');
      } else this.draw();
    }));
    // ── Phase 3: capture + verify ──
    // add after-photo(s) — R38: compress on-device + enqueue to the background
    // uploader, then hand the phone back immediately. Thumbnails appear as each
    // upload lands (the 'surge-photo-uploaded' listener merges the turn).
    // R66 (Marina): the orange "add your after photos" banner LOOKS tappable —
    // make it actually open the picker (proxy-click the real file input).
    all('[data-hk-promptadd]').forEach(el => {
      const open = () => {
        const id = (el as HTMLElement).dataset.hkPromptadd!;
        (this.container.querySelector(`input[data-hk-photo="${CSS.escape(id)}"]`) as HTMLInputElement | null)?.click();
      };
      el.addEventListener('click', open);
      // role=button promises keyboard activation — deliver it (Space would
      // otherwise scroll the page).
      el.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === 'Enter' || k === ' ') { e.preventDefault(); open(); }
      });
    });
    all('[data-hk-photo]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkPhoto!;
      const files = Array.from(input.files || []);
      if (!files.length) return;
      // Guard the compression window synchronously — a re-pick of the same
      // photos would mint fresh client_keys the server can't dedupe.
      input.value = ''; input.disabled = true;
      await enqueuePhotos(`${API}/turns/${id}/photo`, files, { kind: 'after' }, 'after photo');
      this.draw();
    }));

    // #12: cleaner can submit photos DURING the clean (before-photos + evidence),
    // not just after checkout. Same /photo path, tagged kind='before'.
    all('[data-hk-jobphoto]').forEach(el => el.addEventListener('change', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const id = input.dataset.hkJobphoto!;
      const files = Array.from(input.files || []);
      if (!files.length) return;
      input.value = ''; input.disabled = true;   // R38: double-pick guard (see after-photo handler)
      await enqueuePhotos(`${API}/turns/${id}/photo`, files, { kind: 'before' }, 'before photo');
      this.draw();
    }));

    // R18 #2 — two-reports confirm buttons (walk-in habit).
    all('[data-hk-report-none]').forEach(el => el.addEventListener('click', async () => {
      const [id, kind] = el.dataset.hkReportNone!.split('|');
      const j = await this.post(`/turns/${id}/report-confirm`, { kind, none: true });
      if (j && j.turn) { this.replaceDayTurn(j.turn); this.draw(); }
    }));
    all('[data-hk-report-undo]').forEach(el => el.addEventListener('click', async () => {
      const [id, kind] = el.dataset.hkReportUndo!.split('|');
      const j = await this.post(`/turns/${id}/report-confirm`, { kind, none: false });
      if (j && j.turn) { this.replaceDayTurn(j.turn); this.draw(); }
    }));
    // R18 #8 — re-flag a past issue as "still an issue".
    all('[data-hk-flag]').forEach(el => el.addEventListener('click', async () => {
      const [turnId, issTurn, issId] = el.dataset.hkFlag!.split('|');
      const j = await this.post('/turns/flag-issue', { turn_id: issTurn, issue_id: issId, by: this.currentCleanerName() });
      if (j && j.ok) {
        const ctx = this.dayContext[turnId];
        if (ctx) for (const arr of [ctx.open_issues || [], ctx.all_issues || []]) {
          const it = arr.find((x: any) => x.issue_id === issId); if (it) it.flags = j.flags;
        }
        this.draw();
      }
    }));

    // run the clean-verification → POST /turns/:id/analyze.
    all('[data-hk-analyze]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.hkAnalyze!;
      if (this.analyzing) return;
      this.analyzing = id; this.draw();
      const j = await this.post(`/turns/${id}/analyze`, {});
      this.analyzing = null;
      if (j && j.turn) this.replaceDayTurn(j.turn);
      // AI-unblock: a failed check (out of credits, API down, bad parse, or a
      // 500/no-signal that post() already alerted) must never read as a dead
      // end — the card keeps a note and the alert says release still works.
      if (!j || j.ok === false) this.aiCheckFailed.add(id); else this.aiCheckFailed.delete(id);
      if (j && j.ok === false && j.message) alert(`${j.message}\n\nThe AI check is optional — you can still mark this clean guest-ready.`);
      // analyze may have advanced the turn to 'verified' → keep the board honest.
      await this.reloadBoard();
      this.draw();
    }));

    // route a verification-found issue → POST /turns/:id/issue-action.
    all('[data-hk-issueact]').forEach(el => el.addEventListener('click', async (e) => {
      const [id, issueId, action] = (e.currentTarget as HTMLElement).dataset.hkIssueact!.split('|');
      const btn = e.currentTarget as HTMLButtonElement;
      const body: any = { issue_id: issueId, action };
      if (action === 'owner_note') {
        const turn = this.findDayTurn(id);
        const iss = (turn?.issues || []).find((x: any) => x.id === issueId);
        const note = window.prompt('Owner note (leave as-is to use the issue text):', iss?.note || '');
        if (note === null) return;
        if (note.trim()) body.note = note.trim();
      }
      // R104 #6: cleaner path may file a strike candidate; ordering a staging miss asks first
      if (action === 'cleaner') body.strike = confirm('Send this to the cleaner as a must-photograph item on the next clean.\n\nAlso flag a strike candidate? (an admin reviews it before anything sticks)\n\nOK = yes, also flag · Cancel = just send it');
      if (action === 'restock') {
        const turn0 = this.findDayTurn(id);
        const iss0 = (turn0?.issues || []).find((x: any) => x.id === issueId);
        if (iss0?.cause === 'cleaning_miss') { if (!confirm('This looks like a staging miss — the cleaner sets this item out every turn. Order anyway (closet confirmed empty)?')) return; body.force_order = true; }
      }
      const row = btn.closest('.hk-act-row');
      row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = true));
      const landed = (j: any) => { if (j && j.turn) this.replaceDayTurn(j.turn); this.draw(); };
      // a 409 cleaning-miss refusal surfaces as post()'s alert; the board unreadable offers Try again (round 6)
      const j = await this.post(`/turns/${id}/issue-action`, body, landed);
      if (!j) { row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = false)); return; }
      landed(j);
    }));
    // R120 fix round 2 (#14 client): a dismissed finding's reason chips re-post
    // the dismiss with that reason; Undo restores it (both rendered only when
    // the server would allow them: mayUndoTurnDismissal).
    all('[data-hk-dis-reason]').forEach(el => el.addEventListener('click', () => {
      const [id, issueId, code] = el.dataset.hkDisReason!.split('|');
      if (el.classList.contains('on')) return;   // the same reason again is a no-op
      void this.postTurnFindingAction(id, issueId, { action: 'dismiss', reason_code: code }, el as HTMLButtonElement);
    }));
    all('[data-hk-dis-undo]').forEach(el => el.addEventListener('click', () => {
      const [id, issueId] = el.dataset.hkDisUndo!.split('|');
      void this.postTurnFindingAction(id, issueId, { action: 'restore' }, el as HTMLButtonElement);
    }));

    // guest-ready flip → POST /turns/:id {status:'ready'}. Enabled at 'verified';
    // a manager override from 'cleaned' confirms first. AI-unblock: when no AI
    // check exists at all (out of credits / unavailable), the confirm says so
    // rather than implying the AI judged the clean.
    all('[data-hk-makeready]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.hkMakeready!;
      const verified = el.dataset.hkVerified === '1';
      const noAi = el.dataset.hkNoai || '0';
      if (!verified && !confirm(noAi === '1'
        ? 'The AI check didn\'t run (out of credits / unavailable). Release it to the guest anyway?'
        : noAi === '2'
          ? 'No AI check has been run on this clean. Release it to the guest anyway?'
          : 'AI has not verified this clean as guest-ready. Release it to the guest anyway?')) return;
      const j = await this.post(`/turns/${id}`, { status: 'ready' });
      if (j && j.turn) this.replaceDayTurn(j.turn);
      await this.reloadBoard();
      this.draw();
    }));

    // evidence / after-photo lightbox (R112: the shared viewer, whole strip)
    all('[data-hk-zoom]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.openStrip(el as HTMLElement, 'data-hk-zoom'); }));
    // R112: a thumb that fails to load (deleted object, HEIC on a non-Apple
    // browser, timed-out cellular fetch) becomes a labelled placeholder
    // instead of a blank box — bound here, never an inline handler.
    all('.hk-th img').forEach(el => {
      const img = el as HTMLImageElement;
      const mark = () => img.parentElement && img.parentElement.classList.add('broken');
      if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) mark();
      else img.addEventListener('error', mark, { once: true });
    });
    // R112 board KPI cards are lenses: tap to filter, tap again to clear.
    all('[data-hk-kpi]').forEach(el => {
      const go = () => {
        const k = (el as HTMLElement).dataset.hkKpi || '';
        const f = this.boardFilters;
        if (k === 'all') { f.status = 'all'; f.cleaner = 'all'; f.prop = ''; f.only = ''; }
        else if (k === 'unassigned') f.cleaner = f.cleaner === 'none' ? 'all' : 'none';
        else if (k === 'in_progress') f.status = f.status === 'in_progress' ? 'all' : 'in_progress';
        else if (k === 'done') f.status = f.status === 'done' ? 'all' : 'done';
        else if (k === 'at_risk') f.only = f.only === 'at_risk' ? '' : 'at_risk';
        else if (k === 'same_day') f.only = f.only === 'same_day' ? '' : 'same_day';
        this.draw();
      };
      el.addEventListener('click', go);
      el.addEventListener('keydown', (e) => { const key = (e as KeyboardEvent).key; if (key === 'Enter' || key === ' ') { e.preventDefault(); go(); } });
    });
    // R112 codes card: tap-to-copy (+ toast) and the address → Maps.
    all('[data-hk-copy]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const v = (el as HTMLElement).dataset.hkCopy || '';
      let ok = false;
      try { await navigator.clipboard.writeText(v); ok = true; } catch { ok = false; }
      if (!ok) {
        // Fallback for an http://localhost or an old WebView: a hidden textarea + execCommand.
        try { const ta = document.createElement('textarea'); ta.value = v; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed; opacity:0; left:-9999px;'; document.body.appendChild(ta); ta.select(); ok = document.execCommand('copy'); ta.remove(); } catch { ok = false; }
      }
      el.classList.add('copied');
      const act = el.querySelector('.hk-code-act');
      if (act) act.textContent = ok ? '✓ copied' : 'couldn\'t copy — long-press the code to select it';
      this.toast(ok ? 'Copied' : 'Couldn\'t copy');
      setTimeout(() => { el.classList.remove('copied'); if (act) act.textContent = 'tap to copy'; }, 1800);
    }));
    all('[data-hk-maps]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const addr = (el as HTMLElement).dataset.hkMaps || '';
      if (!addr) return;
      // iPhone/iPad → Apple Maps (opens the app); everything else → Google Maps search. PWA-safe via openExternal.
      const apple = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
      openExternal(apple ? `https://maps.apple.com/?q=${encodeURIComponent(addr)}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`);
    }));
    // R112 B4: dismiss the "photo rejected" note.
    all('[data-hk-dropx]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      delete this.droppedPhotos[(el as HTMLElement).dataset.hkDropx || ''];
      this.draw();
    }));
    // R112: retry a job sheet that failed to load.
    all('[data-hk-jobretry]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.hkJobretry || '';
      this.jobFailed.delete(id);
      this.draw();   // renderJob's !job branch kicks ensureJob
    }));

    // ── performance tab (Phase 4) ──
    // window toggle → reload all three reports for the new lookback.
    all('[data-hk-perfdays]').forEach(el => el.addEventListener('click', async () => {
      const d = Number(el.dataset.hkPerfdays);
      if (!d || d === this.perfDays) return;
      this.perfDays = d;
      this.payoutTurnsOpen = null; this.payoutTurns = {};
      this.perfLoading = true; this.draw();
      await this.reloadPerformance();
      this.draw();
    }));
    // coverage toggle (R10 #4) → refetch ONLY the cost report (scorecards and
    // payouts are coverage-independent).
    all('[data-hk-perfcovval]').forEach(el => el.addEventListener('click', async () => {
      const v = el.dataset.hkPerfcovval as 'in_house' | 'vendor' | 'all';
      if (!v || v === this.perfCoverage || this.perfLoading) return;
      this.perfCoverage = v;
      this.perfLoading = true; this.draw();
      await this.reloadCostsOnly();
      this.draw();
    }));

    // ── R78 payroll audit binds ─────────────────────────────────────────
    // Daily-hours grid cell → serialized save; local state updated so the next
    // draw doesn't blank the cell.
    all('[data-hk-dh]').forEach(el => el.addEventListener('change', (e) => {
      const [date, cid] = String((el as HTMLElement).dataset.hkDh || '').split('|');
      if (!date || !cid) return;
      const raw = (e.currentTarget as HTMLInputElement).value.trim();
      const hours = raw === '' ? null : Number(raw);
      if (hours != null && (!Number.isFinite(hours) || hours < 0 || hours > 24)) { alert('Hours must be 0–24.'); return; }
      const setCell = (v: number | null) => {
        if (!this.perfDaily) return;
        const map = this.perfDaily.hours || (this.perfDaily.hours = {});
        if (v == null) { if (map[date]) delete map[date][cid]; }
        else { if (!map[date]) map[date] = {}; map[date][cid] = v; }
        this.perfDaily.over12 = (this.perfDaily.over12 || []).filter((o: any) => !(o.date === date && String(o.cleaner_id) === cid));
        if (v != null && v > 12) this.perfDaily.over12.push({ date, cleaner_id: cid, hours: v });
      };
      const prev = this.perfDaily ? (((this.perfDaily.hours || {})[date] || {})[cid] ?? null) : null;
      setCell(hours);   // optimistic — the input keeps its value across draws
      this.payrollChain = this.payrollChain.then(async () => {
        const j = await this.post('/housekeeping/daily-hours', { date, cleaner_id: cid, hours, by: HousekeepingPage.actorName() || 'office' });
        if (!j) {
          // Save failed (alert shown) — revert so totals and >12h flags don't
          // keep asserting a value that was never persisted.
          setCell(prev == null ? null : Number(prev));
          const ae = document.activeElement as HTMLElement | null;
          if (!(ae && ae.dataset && (ae.dataset.hkPpcell || ae.dataset.hkDh))) this.draw();
        }
      });
    }));
    // Pay periods: create (suggested / custom), expand, per-row register save,
    // close/reopen, delete.
    // Create/close/delete ride payrollChain too (review: unchained, they raced
    // a queued register-cell save on the same blob and could drop it — and a
    // reloadPerformance issued before queued daily-hours POSTs landed showed
    // pre-write data).
    const ppCreate = (start: string, end: string) => {
      this.payrollChain = this.payrollChain.then(async () => {
        const j = await this.post('/housekeeping/pay-periods', { start, end, by: HousekeepingPage.actorName() || 'office' });
        if (j && j.period) { this.ppOpen = j.period.id; await this.reloadPerformance(); this.draw(); }
      });
    };
    all('[data-hk-ppnew]').forEach(el => el.addEventListener('click', () => {
      const [start, end] = String((el as HTMLElement).dataset.hkPpnew || '').split('|');
      ppCreate(start, end);
    }));
    all('[data-hk-ppnewcustom]').forEach(el => el.addEventListener('click', () => {
      const start = (this.container.querySelector('[data-hk-ppstart]') as HTMLInputElement)?.value || '';
      const end = (this.container.querySelector('[data-hk-ppend]') as HTMLInputElement)?.value || '';
      if (!start || !end) { alert('Pick both start and end dates.'); return; }
      ppCreate(start, end);
    }));
    all('[data-hk-pptoggle]').forEach(el => el.addEventListener('click', () => {
      const id = String((el as HTMLElement).dataset.hkPptoggle || '');
      this.ppOpen = this.ppOpen === id ? null : id;
      this.draw();
    }));
    all('[data-hk-ppcell]').forEach(el => el.addEventListener('change', () => {
      const [pid, cid] = String((el as HTMLElement).dataset.hkPpcell || '').split('|');
      if (!pid || !cid) return;
      // Send the row's THREE current values together (server row-replaces).
      const read = (f: string) => {
        const inp = this.container.querySelector(`[data-hk-ppcell="${pid}|${cid}|${f}"]`) as HTMLInputElement | null;
        const v = (inp?.value || '').trim();
        return v === '' ? null : Number(v);
      };
      const body = { cleaner_id: cid, hours: read('hours'), gross: read('gross'), taxes: read('taxes'), by: HousekeepingPage.actorName() || 'office' };
      this.payrollChain = this.payrollChain.then(async () => {
        const j = await this.post(`/housekeeping/pay-periods/${encodeURIComponent(pid)}`, body);
        if (j && j.period && this.perfPeriods) {
          this.perfPeriods.periods = (this.perfPeriods.periods || []).map((p: any) => p.id === pid ? j.period : p);
          // Draw-race guard: a redraw while the user is typing the NEXT cell
          // would wipe it — hold the redraw when a payroll input has focus
          // (state is updated; the next natural draw shows the computed cells).
          const ae = document.activeElement as HTMLElement | null;
          if (!(ae && ae.dataset && (ae.dataset.hkPpcell || ae.dataset.hkDh))) this.draw();
        }
      });
    }));
    all('[data-hk-ppclose]').forEach(el => el.addEventListener('click', () => {
      const [pid, want] = String((el as HTMLElement).dataset.hkPpclose || '').split('|');
      // Chained behind queued cell saves so the close response reflects them.
      this.payrollChain = this.payrollChain.then(async () => {
        const j = await this.post(`/housekeeping/pay-periods/${encodeURIComponent(pid)}`, { closed: want === '1', by: HousekeepingPage.actorName() || 'office' });
        if (j && j.period && this.perfPeriods) {
          this.perfPeriods.periods = (this.perfPeriods.periods || []).map((p: any) => p.id === pid ? j.period : p);
          // Closing a period can change which register feeds the loaded rates.
          void this.reloadUnitCostOnly();
          this.draw();
        }
      });
    }));
    all('[data-hk-ppdel]').forEach(el => el.addEventListener('click', () => {
      const pid = String((el as HTMLElement).dataset.hkPpdel || '');
      if (!confirm('Delete this pay period and its register entries? Daily hours are untouched.')) return;
      this.payrollChain = this.payrollChain.then(async () => {
        const ok = await this.del(`/housekeeping/pay-periods/${encodeURIComponent(pid)}`);
        if (ok && this.perfPeriods) {
          this.perfPeriods.periods = (this.perfPeriods.periods || []).filter((p: any) => p.id !== pid);
          if (this.ppOpen === pid) this.ppOpen = null;
          void this.reloadUnitCostOnly();
          this.draw();
        }
      });
    }));
    all('[data-hk-chanfee]').forEach(el => el.addEventListener('change', async (e) => {
      const pct = Number((e.currentTarget as HTMLInputElement).value);
      if (!Number.isFinite(pct) || pct < 0 || pct > 50) { alert('Channel fee must be 0–50%.'); return; }
      const j = await this.post('/housekeeping/unit-cost-config', { channel_fee_pct: pct });
      if (j) await this.reloadUnitCostOnly();
    }));

    // expand a payout row → lazy-load that cleaner's completed turns (GET
    // /cleaners/:id) once so each turn can be marked paid/unpaid.
    all('[data-hk-payouttoggle]').forEach(el => el.addEventListener('click', async () => {
      const cid = el.dataset.hkPayouttoggle!;
      if (this.payoutTurnsOpen === cid) { this.payoutTurnsOpen = null; this.draw(); return; }
      this.payoutTurnsOpen = cid;
      this.draw();              // open with a spinner
      if (!this.payoutTurns[cid]) {
        const j = await this.get(`/cleaners/${cid}`);
        const done = ['cleaned', 'verified', 'ready'];
        this.payoutTurns[cid] = ((j && j.turns) || []).filter((t: any) => done.includes(t.status));
      }
      this.draw();
    }));

    // mark a turn paid/unpaid → POST /turns/:id {payout_status}. Refresh the
    // payout totals and the expanded turn list so both stay honest.
    all('[data-hk-payoutset]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();      // don't collapse the row we're inside
      const [id, status] = (e.currentTarget as HTMLElement).dataset.hkPayoutset!.split('|');
      const j = await this.post(`/turns/${id}`, { payout_status: status });
      if (!j) return;
      // Patch the turn in the expanded list, then refetch the payout summary.
      for (const cid of Object.keys(this.payoutTurns)) {
        const i = this.payoutTurns[cid].findIndex(t => t.id === id);
        if (i >= 0 && j.turn) this.payoutTurns[cid][i] = { ...this.payoutTurns[cid][i], ...j.turn };
      }
      const pj = await fetch(`${API}/housekeeping/payouts`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (pj) this.perfPayouts = pj;
      this.draw();
    }));
  }

  // Replace a turn in the cleaner-day list in place (preserves enrich flags the
  // board adds; the /turns endpoints return the bare stored turn, so we merge
  // onto the existing day row to keep same_day/at_risk/etc. for the card class).
  // Review R120 #14 (round 4): the NEWEST answer always wins — what this
  // answer says about each finding is noted (newest per finding, by receipt
  // order), the row shows the newest word (withBoardState), and the cached
  // job sheet follows (never the other way round).
  private replaceDayTurn(turn: any): void {
    const i = this.dayTurns.findIndex(t => t.id === turn.id);
    const r = this.recentTurns.findIndex(t => t.id === turn.id);   // R37
    if (turn && turn.id != null && Array.isArray(turn.issues)) {
      const at = this.stampOf(turn);
      const seen = this.seenFor(turn.id);
      // a disposition the row holds that no noted answer gave (it came in some
      // other way) counts as the OLDEST word: it fills a gap, never overrides
      const cur = i >= 0 ? this.dayTurns[i] : r >= 0 ? this.recentTurns[r] : null;
      if (cur && Array.isArray(cur.issues)) HousekeepingPage.noteBoardState(seen, cur.issues, this.answerAt.get(cur) ?? 0);
      HousekeepingPage.noteBoardState(seen, turn.issues, at);
      turn = { ...turn, issues: HousekeepingPage.withBoardState(seen, turn.issues, at) };
      this.refreshSheet(turn.id);
    }
    if (i >= 0) this.dayTurns[i] = { ...this.dayTurns[i], ...turn };
    if (r >= 0) this.recentTurns[r] = { ...this.recentTurns[r], ...turn };
  }

  // R37: job-card handlers must resolve turns from the Recent-cleans section
  // too — its cards reuse the exact same renderJob markup and data- hooks.
  private findDayTurn(id: string): any {
    return this.dayTurns.find(t => t.id === id) || this.recentTurns.find(t => t.id === id);
  }

  // R112: open the shared lightbox on a tapped thumb — the strip's full url
  // list (data-hk-urls, photos + videos in order) and the tile's index; a
  // strip without the list falls back to collecting its sibling hooks.
  private openStrip(el: HTMLElement, attr: string): void {
    const strip = el.closest('[data-hk-strip]') as HTMLElement | null;
    let urls: string[] = [];
    try { urls = strip && strip.dataset.hkUrls ? JSON.parse(strip.dataset.hkUrls) : []; } catch { urls = []; }
    let idx = Number(el.dataset.hkIdx);
    if (!Array.isArray(urls) || !urls.length) { const r = stripUrlsFor(el, attr); urls = r.urls; idx = r.index; }
    if (!Number.isFinite(idx) || idx < 0 || idx >= urls.length) idx = Math.max(0, urls.indexOf(el.getAttribute(attr) || ''));
    if (!urls.length) return;
    openLightbox(urls, idx);
  }

  // R37: called by App's 15-min auto-refresh — a full render() while a field
  // cleaner has a job card open destroys picked-but-unsent photo inputs.
  suspendAutoRefresh(): boolean {
    return HousekeepingPage.fieldRole() === 'cleaner' && this.dayJobOpen != null;
  }

  // (R38: the foreground chunked uploader + its offline fallback were replaced
  // by lib/photoUploader — compress on-device, persist to IndexedDB, upload in
  // the background with retry. See enqueuePhotos call sites.)

  // (R112: the page-local single-image lightbox was replaced by the shared
  // shared/components/Lightbox — prev/next, counter, keys, swipe, video.)

  // R112: the ONE way a job sheet (GET /turns/:id/job + /context) reaches the
  // card. Idempotent while a fetch is in flight; a missing sheet on an open
  // card self-heals from renderJob; `revalidate` refreshes a cached sheet
  // quietly (codes, instructions and checklist quirks change mid-day). The
  // fetch is QUIET (no alert) — a failure parks the card on a retry button,
  // never an alert loop. A write that lands mid-flight (dayMutSeq moved)
  // makes the snapshot stale — one refetch — so a stale turn can never
  // overwrite a fresher mutation response.
  private async ensureJob(id: string, o: { revalidate?: boolean; draw?: boolean } = {}): Promise<void> {
    const revalidate = !!o.revalidate;
    if (this.jobLoading.has(id)) return;
    if (this.dayJobs[id] && !revalidate) return;
    this.jobLoading.add(id);
    const quiet = (p: string) => fetch(`${API}${p}`).then(r => r.ok ? r.json() : null).catch(() => null);
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        const seq = this.dayMutSeq;
        const [j, ctx] = await Promise.all([quiet(`/turns/${encodeURIComponent(id)}/job`).then(x => this.stampAnswer(x)), quiet(`/turns/${encodeURIComponent(id)}/context`)]);
        if (seq !== this.dayMutSeq && attempt === 0) continue;
        if (j) {
          // Review #14: a revalidate whose inventory read blipped must not
          // flip the stocking list's identity namespace mid-clean — keep
          // the last-known-good inventory block.
          if (j.inventory !== undefined && (!j.inventory || !(j.inventory.items || []).length)) {
            const prevJob = this.dayJobs[id];
            if (prevJob && prevJob.inventory && (prevJob.inventory.items || []).length) j.inventory = prevJob.inventory;
          }
          this.dayJobs[id] = j;
          this.jobFailed.delete(id);
          // R59 slim seam: the job body renders from the day ROW — merge the
          // sheet's full turn over it (checklist state, photos, gates).
          if (j.turn) this.mergeTurnIfFresh(j.turn);
          this.refreshSheet(id);   // review R120 #14 (round 4): a stale sheet shows the newest word too
        } else if (!this.dayJobs[id]) this.jobFailed.add(id);
        if (ctx) this.dayContext[id] = ctx;
        break;
      }
    } finally { this.jobLoading.delete(id); }
    if (o.draw === false) return;
    if (this.dayJobOpen === id || this.histOpen.size) this.drawIfIdle();
  }

  // R112: a short confirmation on the cleaner's phone ("Copied").
  private toast(msg: string, ms = 1600): void {
    document.querySelectorAll('.hk-toast').forEach(el => el.remove());
    const el = document.createElement('div');
    el.className = 'hk-toast';
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), ms);
  }

  // R51: fetch (once per listing) the property's checklist override so the
  // expanded board card's "skip standard checklist" box reflects reality.
  private async loadChecklistOverride(turnId: string): Promise<void> {
    const t = ((this.board && this.board.turns) || []).find((x: any) => x.id === turnId);
    const lid = t ? String(t.listing_id) : '';
    if (!lid || this.checklistOverrides.has(lid)) return;
    const j = await fetch(`${API}/property-checklist-override/${encodeURIComponent(lid)}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (!j) return;   // leave the box disabled — an unknown state must not be writable
    this.checklistOverrides.set(lid, j.override || null);
    if (this.expandedTurn === turnId) this.draw();
  }

  // After a check-in/out: refresh the cleaner's day list (status moved) and
  // keep the board summary honest, then redraw. The field view has no board
  // (R112: it never loads one — nextCleanFor uses the day/upcoming lists).
  private async refreshDayAndBoard(): Promise<void> {
    const field = HousekeepingPage.fieldRole() === 'cleaner';
    await Promise.all([this.reloadDay(), field ? Promise.resolve() : this.reloadBoard(), this.reloadCleaners()]);
    if (this.dayJobOpen) await this.ensureJob(this.dayJobOpen, { revalidate: true, draw: false });
    this.draw();
  }
}
